// ============================================================================
// CadernoCockpit.jsx — NÍVEL 1 da arquitetura Gênero/Espécie
// ----------------------------------------------------------------------------
// Gênero = Processo (este componente). Espécie = Peça (FileHubArea).
// Base normativa: Pesquisas 01 + 02 (Cockpit Dinâmico + Tela Cirúrgica).
// Artigo 131 da Constituição RDA (Slow is smooth, smooth is fast).
// ============================================================================

import React, {
  useState,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import { db, auth } from "../../shared/api/firebase/config";
import { doc, onSnapshot } from "firebase/firestore";
import {
  persistPieces as repoPersistPieces,
  updateWorkspaceAlias,
  fetchPublishedDocs,
  saveInfoGerais,
  savePortalStatus,
  savePartes,
  saveCoPartes,
  saveCaseTeam,
  saveSigilo,
  migrateDriveToMatterFolder,
  savePrazosAudiencias,
  saveProcessRelations,
  linkProcessRelation,
  unlinkProcessRelation,
  subscribeMovements,
} from "./CadernoCockpitRepository";
import { MovementCaptureForm } from "./MovementCaptureForm";
import { MovementResumoPanel } from "./MovementResumoPanel";
import { MovementTimelineItem } from "./MovementTimelineItem";
import { CadernoMarkdownViewer } from "./CadernoMarkdownViewer";
import {
  FIREBASE_CONFIG,
  CASE_TEAM_ROLES,
  TITULAR_SEED,
} from "../../config/Config";
import FileHubArea from "./FileHubArea";
import { formatNPU, resolveProcessTitle, normalizeSearchText, getStatusStyle, toTitleCasePt } from "../../utils/formatters";
import { STATUS_COLUMNS } from "./ProcessosCadernoListView";
import { ProcessPicker } from "../../components/common/ProcessPicker";
import { FlyoutPanel } from "../../components/common/FlyoutPanel";
import {
  POSICOES_POR_RAMO,
  RAMO_TAG_MAP,
  POSICOES_AUXILIARES_JUSTICA,
  POLO_PROCESSUAL,
  POLO_NAO_PROCESSUAL,
} from "../../config/PosicoesProcessuais";
import { CrmClientPickerModal } from "../../components/CrmClientPickerModal";
import CadernoTarefas from "../../components/files/CadernoTarefas";
import ConsentBuilder from "../../features/petitions/ConsentBuilder";
import { useCadernoApproval } from "../../hooks/useCadernoApproval";
import { useCadernoNavigation } from "../../hooks/useCadernoNavigation";
import { useCadernoTree } from "../../hooks/useCadernoTree";
import { useCadernoPieces } from "../../hooks/useCadernoPieces";
import { useCadernoInfoGerais } from "../../hooks/useCadernoInfoGerais";
import { useCadernoPartes } from "../../hooks/useCadernoPartes";
import { useCadernoCaseTeam } from "../../hooks/useCadernoCaseTeam";
import { useCadernoSigilo } from "../../hooks/useCadernoSigilo";
import { fetchClientDoc } from "../../services/FileHubCacheRepository";
import { REGISTER_PROVIDERS } from "../../config/ClientConstants";
import { generateQualificationText } from "../../config/ClientSchema";

const appId = FIREBASE_CONFIG.projectId;

// `dataMovimento` (aba Histórico) vem gravado com sufixo "Z" mas o valor já
// É a hora local de Brasília (confirmado campo a campo contra a aba "Eventos
// do Processo" do Projudi — ex.: 21/08/2026 03:05:54 bate exatamente, sem
// deslocamento). Usar getUTC*() em vez de getHours()/toLocaleString() — senão
// o navegador aplica fuso horário uma segunda vez. Sem vírgula entre data e
// hora (formato PT-BR de tribunal, não o formato do Intl.DateTimeFormat).
function formatDataHoraMovimento(iso) {
  if (!iso) return { data: null, hora: null };
  const dt = new Date(iso);
  const pad2 = (n) => String(n).padStart(2, "0");
  return {
    data: `${pad2(dt.getUTCDate())}/${pad2(dt.getUTCMonth() + 1)}/${dt.getUTCFullYear()}`,
    hora: `${pad2(dt.getUTCHours())}:${pad2(dt.getUTCMinutes())}:${pad2(dt.getUTCSeconds())}`,
  };
}

const getBackendBaseUrl = () => {
  const envUrl = String(import.meta?.env?.VITE_BACKEND_URL || "").trim();
  if (envUrl) return envUrl.replace(/\/+$/, "");
  return "https://rda-backend-thuq.onrender.com";
};

// FIX DATA-PREFIXO EM PEÇA (22/09/2026): formata "AAAA-MM-DD" (como vem de
// pieceFolderDatePrefix) para "DD/MM/AAAA" só por manipulação de string —
// nunca via `new Date(str)`, que em UTC-3 pode voltar um dia (regra de
// timezone do CLAUDE.md). "—" quando a peça ainda não tem data (legada ou
// criada antes deste fix).
function formatPieceDateLabel(isoDate) {
  const m = String(isoDate || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "—";
  const [, y, mo, d] = m;
  return `${d}/${mo}/${y}`;
}

// ─── Seção Partes (24/08/2026) — seleção manual "nosso cliente" ────────────
// O titular pediu um jeito de marcar, dentre as partes REAIS de um processo
// (partes.adversas, vindas do Projudi), qual é nosso cliente/representado —
// sem inventar campo novo, reaproveitando partiesRefs (mesmo mecanismo já
// usado por "+ Co-parte (CRM)"/"+ Manual"). Entes oficiais (MP, Estado,
// União, Município, Fazenda Pública, Delegacia/Polícia, autarquias) nunca
// são "nosso cliente" — excluídos do seletor por tipo=ORGAO ou por nome.
const ENTE_PUBLICO_REGEX =
  /minist[ée]rio\s+p[uú]blico|defensoria\s+p[uú]blica|procuradoria|fazenda\s+p[uú]blica|^estado\s+d[eo]|\buni[aã]o\b|munic[ií]pio|prefeitura|\binss\b|pol[ií]cia|delegacia|autarquia|receita\s+federal/i;
function isAdversaEntePublico(row) {
  const tipo = String(row?.tipo || "").toUpperCase();
  if (tipo === "ORGAO") return true;
  return ENTE_PUBLICO_REGEX.test(String(row?.nome || ""));
}

// ─── Fallback de polo (24/08/2026, correção da 4ª revisão) ─────────────────
// ANTES: todo ponto que lia/gravava `polo` sem valor explícito assumia
// cegamente "Terceiro Interessado" (~7 ocorrências de `|| "Terceiro
// Interessado"`). Isso classificava errado qualquer parte real sem `polo`
// gravado, mesmo quando o `role`/papel dela já indicava outra coisa — ex.:
// a Dra. Vanessa ("Fiel Depositário") caía em Terceiro Interessado quando
// juridicamente é Auxiliar da Justiça (Art. 149 CPC).
// AGORA: se o role/papel bate com algo em POSICOES_AUXILIARES_JUSTICA
// (comparação por substring, sem acento/caixa — cobre "Fiel Depositário"
// vs. "Depositário / Fiel Depositário"), o fallback correto é "Auxiliar da
// Justiça". Só quando NÃO bate, mantém "Terceiro Interessado" como último
// recurso — decisão CONSCIENTE, não esquecimento: continua sendo uma
// suposição (dado ausente), não confirmação de que a parte É terceira
// interessada (Constituição de Processos Art. 5 — nunca gravar suposição
// como se fosse dado confirmado sem sinalizar isso).
function inferPoloFallback(role) {
  const roleNorm = normalizeSearchText(String(role || ""));
  if (roleNorm) {
    const bateAuxiliar = POSICOES_AUXILIARES_JUSTICA.some((posicao) => {
      const posicaoNorm = normalizeSearchText(posicao);
      return (
        roleNorm.includes(posicaoNorm) ||
        posicaoNorm
          .split("/")
          .map((p) => p.trim())
          .filter(Boolean)
          .some((parte) => roleNorm.includes(parte))
      );
    });
    if (bateAuxiliar) return "Auxiliar da Justiça";
  }
  return "Terceiro Interessado";
}

// ─── DocViewPanel — Visualizador somente-leitura (Contratos e Procurações) ───
function DocViewPanel({ doc, onClose }) {
  if (!doc) return null;
  const isCT = String(
    doc?.type || doc?.category || doc?.classification?.category || "",
  )
    .toUpperCase()
    .includes("CONTRATO");
  const tipo = isCT ? "Contrato" : "Procuração";
  const title = String(doc?.title || doc?.name || tipo);
  const viewUrl = String(doc?.fileUrl || doc?.downloadUrl || "");
  const htmlContent = String(doc?.htmlContent || "");
  const publishedAt = doc?.createdAt?.toDate
    ? doc.createdAt.toDate().toLocaleDateString("pt-BR")
    : "—";
  const publishedBy = String(
    doc?.createdBy || doc?.publication?.publishedBy || "—",
  );
  const version = doc?.publicationVersion || doc?.publication?.version || "—";
  const isPdf = String(doc?.extension || doc?.mimeType || "")
    .toLowerCase()
    .includes("pdf");
  return (
    <div className="flex-1 flex flex-col h-full bg-white min-w-0">
      <div className="shrink-0 px-4 py-2.5 border-b border-gray-200 bg-[#20243B] flex items-center gap-2.5">
        <i
          className={`fas ${isCT ? "fa-file-contract" : "fa-file-signature"} text-[#C5A059] text-sm`}
        />
        <div className="flex-1 min-w-0">
          <div className="text-white font-bold text-[11px] truncate">
            {title}
          </div>
          <div className="text-gray-300 text-[9px] uppercase tracking-widest">
            {tipo} · Somente Leitura
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-6 h-6 rounded-full flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/20 transition-colors shrink-0"
          title="Fechar visualização"
        >
          <i className="fas fa-times text-xs" />
        </button>
      </div>
      <div className="shrink-0 px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="text-[10px]">
          <span className="font-bold text-gray-500 uppercase tracking-wider mr-1">
            Versão
          </span>
          <span className="text-[#20243B] font-semibold">v{version}</span>
        </div>
        <div className="text-[10px]">
          <span className="font-bold text-gray-500 uppercase tracking-wider mr-1">
            Formato
          </span>
          <span className="text-[#20243B] font-semibold">
            {isPdf ? "PDF" : "HTML"}
          </span>
        </div>
        <div className="text-[10px]">
          <span className="font-bold text-gray-500 uppercase tracking-wider mr-1">
            Publicado
          </span>
          <span className="text-[#20243B] font-semibold">{publishedAt}</span>
        </div>
        <div className="text-[10px] flex-1 min-w-0 truncate">
          <span className="font-bold text-gray-500 uppercase tracking-wider mr-1">
            Por
          </span>
          <span className="text-[#20243B] font-semibold truncate">
            {publishedBy}
          </span>
        </div>
        {viewUrl ? (
          <a
            href={viewUrl}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold rounded-lg transition-colors shrink-0"
          >
            <i className="fas fa-external-link-alt text-[9px]" /> Abrir{" "}
            {isPdf ? "PDF" : "Doc"}
          </a>
        ) : null}
      </div>
      <div className="flex-1 overflow-hidden min-h-0">
        {htmlContent ? (
          <iframe
            srcDoc={htmlContent}
            title={title}
            className="w-full h-full border-0"
            sandbox="allow-same-origin"
          />
        ) : viewUrl ? (
          <div className="flex items-center justify-center h-full p-8 text-center">
            <div>
              <i className="fas fa-file-pdf text-5xl text-red-400 mb-4 opacity-70" />
              <div className="text-sm font-bold text-[#20243B] mb-1">
                {title}
              </div>
              <div className="text-xs text-gray-500 mb-4">Documento PDF.</div>
              <a
                href={viewUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold rounded-lg transition-colors"
              >
                <i className="fas fa-external-link-alt" /> Abrir documento
              </a>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full p-8 text-center">
            <div>
              <i className="fas fa-unlink text-5xl text-gray-200 mb-4" />
              <div className="text-sm font-bold text-gray-500 mb-1">
                Visualização indisponível
              </div>
              <div className="text-xs text-gray-400">
                Este documento não possui conteúdo para visualização.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Hook de redimensionamento do painel esquerdo ──────────────────────────
const LS_SIDEBAR_WIDTH = "rda_cadernoCockpitWidth";
const SIDEBAR_MIN = 240;
const SIDEBAR_MAX = 600;
const SIDEBAR_DEFAULT = 360;

function useSidebarResize() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    try {
      const saved = window.localStorage.getItem(LS_SIDEBAR_WIDTH);
      if (saved) {
        const num = parseInt(saved, 10);
        if (!isNaN(num) && num >= SIDEBAR_MIN && num <= SIDEBAR_MAX) return num;
      }
    } catch {}
    return SIDEBAR_DEFAULT;
  });
  const persistWidth = useCallback((w) => {
    try {
      window.localStorage.setItem(LS_SIDEBAR_WIDTH, String(w));
    } catch {}
  }, []);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const latestWidthRef = useRef(sidebarWidth);
  useEffect(() => {
    latestWidthRef.current = sidebarWidth;
  }, [sidebarWidth]);
  const handleMouseDown = useCallback(
    (e) => {
      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = sidebarWidth;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
    },
    [sidebarWidth],
  );
  // P25-fix: listeners de mousemove/mouseup precisam ficar registrados desde o
  // mount (não só quando isDraggingRef já é true) — senão o resize nunca
  // "liga" sozinho na primeira tentativa, pois handleMouseDown só muta um ref
  // (não dispara re-render), então este efeito nunca reexecutaria para
  // registrar os listeners. Cada handler já se protege internamente com
  // `if (!isDraggingRef.current) return`, então mantê-los sempre montados é
  // seguro e não tem custo fora do drag.
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const delta = e.clientX - startXRef.current;
      const next = Math.min(
        SIDEBAR_MAX,
        Math.max(SIDEBAR_MIN, startWidthRef.current + delta),
      );
      latestWidthRef.current = next;
      setSidebarWidth(next);
    };
    const handleMouseUp = () => {
      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      persistWidth(latestWidthRef.current);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [persistWidth]);
  useEffect(() => {
    return () => {
      persistWidth(sidebarWidth);
    };
  }, [sidebarWidth, persistWidth]);
  return {
    sidebarWidth,
    setSidebarWidth,
    handleResizeMouseDown: handleMouseDown,
  };
}

// ─── DragHandle — Divisor redimensionável ──────────────────────────────────
function DragHandle({ onMouseDown }) {
  return (
    <div
      className="relative z-30 shrink-0 w-[5px] cursor-col-resize bg-transparent hover:bg-[#C5A059]/30 active:bg-[#C5A059]/50 transition-colors group"
      onMouseDown={onMouseDown}
    >
      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] rounded-full bg-gray-200 group-hover:bg-[#C5A059]/50 group-active:bg-[#C5A059] transition-colors" />
    </div>
  );
}

// MC103 (12/09/2026): pedido do titular (pendência da lista de
// reformulação) — novo modo de ordenação "Por status" na árvore de
// Processos, com divisor visível entre grupos. Ordem reaproveita
// `STATUS_COLUMNS` (mesma ordem já usada no Kanban de Processos, em
// `ProcessosCadernoListView.jsx`), só inserindo "Sem Status" antes de
// "Arquivado" — status vazio/não reconhecido cai nesse grupo (mesmo
// critério de fallback do `getStatusStyle`).
const STATUS_GROUP_ORDER = [
  ...STATUS_COLUMNS.map((s) => s.toLowerCase()),
].flatMap((s) => (s === "arquivado" ? ["sem status", "arquivado"] : [s]));
const STATUS_GROUP_LABELS = STATUS_COLUMNS.reduce(
  (acc, s) => ({ ...acc, [s.toLowerCase()]: s }),
  { "sem status": "Sem Status" },
);
const getStatusGroupKey = (w) => {
  const s = String(w?.status || "").trim().toLowerCase();
  return s && STATUS_GROUP_ORDER.includes(s) ? s : "sem status";
};

export default function CadernoCockpit({
  activeWorkspace, // pode ser null — IDE nasce em modo "Selecione um processo"
  onBack, // legado — mantido por compat; não é mais usado no UI
  initialFolderId,
  autoConnect = false,
  // MC_B/MC_C (IDE V9) — navegação autônoma:
  clientWorkspaces = null, // (opcional) se AutosDashboard já filtrou; senão derivado de allWorkspaces
  allWorkspaces = [], // todos os workspaces (para o seletor filtrar por cliente)
  onSwitchWorkspace, // (w) => void — troca de caderno sem desmontar o IDE
  onSwitchClient, // legado — não usado no novo fluxo (picker é interno)
  // MC_X (Extermínio da Tela Zero) — handlers delegados ao pai:
  onRequestNewCaderno, // (clientHint?) => void — abre modal "Novo Caderno" no pai
  onRequestLinkProcess, // (workspaceId) => void — abre modal "Vincular Processo" no pai
  onRequestDeleteWorkspace, // (workspaceId) => void — dispara confirmação/delete no pai
  onRequestLegacyCleanup, // () => void — abre modal "Limpar Drive Legado" no pai
} = {}) {
  const {
    activePecaDoc,
    setActivePecaDoc,
    activeViewDoc,
    setActiveViewDoc,
    selectedClientId,
    setSelectedClientId,
    selectedClientName,
    setSelectedClientName,
    selectedClientMeta,
    setSelectedClientMeta,
    isClientPickerOpen,
    setIsClientPickerOpen,
  } = useCadernoNavigation(activeWorkspace);

  const extractClientMeta = (src) => {
    if (!src || typeof src !== "object")
      return { cpf: "", cnpj: "", email: "" };
    const cpf = String(
      src?.qualification?.physical?.cpf ||
        src?.cpf ||
        src?.clientCpf ||
        src?.client?.cpf ||
        src?.documento ||
        "",
    ).trim();
    const cnpj = String(
      src?.qualification?.legal?.cnpj ||
        src?.cnpj ||
        src?.clientCnpj ||
        src?.client?.cnpj ||
        "",
    ).trim();
    const email = String(
      src?.contact?.email ||
        src?.email ||
        src?.clientEmail ||
        src?.client?.email ||
        src?.mail ||
        "",
    ).trim();
    return { cpf, cnpj, email };
  };
  const selectedClientIdRef = useRef(selectedClientId);
  useEffect(() => {
    selectedClientIdRef.current = selectedClientId;
  }, [selectedClientId]);

  const {
    editingNodeId,
    setEditingNodeId,
    editingNodeValue,
    setEditingNodeValue,
    workspaceSortMode,
    setWorkspaceSortMode,
  } = useCadernoTree();
  const cycleSortMode = () => {
    setWorkspaceSortMode((prev) => {
      // "modified" removido do ciclo (22/08/2026, pedido do titular): ordenar
      // por updatedAt fazia o caderno pular de posição sempre que qualquer
      // coisa tocava nesse campo (inclusive só abrir o caderno, antes do fix
      // do autosave) — "ordem fixa, não auto-arrumar" é mais previsível.
      // "numero" adicionado em 24/08/2026: o toggle nunca ofereceu ordenação
      // NUMÉRICA por nº de processo (CNJ) — só "criação" e "A→Z" — e um
      // advogado com lista de processos numerados quer esse critério. Reusa
      // a MESMA sortKey já usada como ordem-base em AutosDashboard.jsx
      // (2ª correção, rodada [PET_A]) pra não inventar uma 2ª regra de
      // comparação de CNJ. Nota: como useCadernoTree.js não foi tocado
      // (fora do escopo desta tarefa), o modo "numero" não sobrevive a um
      // reload de página — sessionStorage cai de volta pra "created" nesse
      // caso (ver comentário no useState de useCadernoTree.js).
      // "status" e "vinculos" adicionados em 12/09/2026 (MC103/MC104,
      // pedidos da lista de reformulação): agrupar por status (com
      // divisor) e agrupar por família de vínculos (árvore estilo
      // Explorer) — ver STATUS_GROUP_ORDER/vinculosFlat.
      const CYCLE = ["created", "alpha", "numero", "status", "vinculos"];
      const idx = CYCLE.indexOf(prev);
      const next = CYCLE[(idx + 1) % CYCLE.length];
      try {
        window.sessionStorage.setItem("rda_cadernoSortMode", next);
      } catch {}
      return next;
    });
  };

  // ─── Sidebar Resize Hook ────────────────────────────────────────────
  const { sidebarWidth, handleResizeMouseDown } = useSidebarResize();

  const [hubBridge, setHubBridge] = useState(null);

  const approval = useCadernoApproval({
    activeWorkspace,
    selectedClientId,
    hubBridge,
  });

  const { savedPieces, setSavedPieces, activePieceId, setActivePieceId } =
    useCadernoPieces();

  // ─── useRef para evitar stale closure em handleChangePieceType ──────────────
  const savedPiecesRef = useRef(savedPieces);
  useEffect(() => {
    savedPiecesRef.current = savedPieces;
  }, [savedPieces]);

  // Ajuste de estado DURANTE a renderização (padrão React oficial para
  // "resetar estado quando uma prop muda"), em vez de useEffect. Um useEffect
  // só roda DEPOIS do commit — nesse meio-tempo o restante da árvore (incl.
  // useFileHub) renderizava com activeWorkspace já trocado mas activePieceId
  // ainda apontando pro caderno anterior, causando estado inconsistente
  // (stateKey vazio → reset do organizer store) e a "piscada" visível ao
  // trocar de caderno. Ajustando aqui, a combinação inconsistente nunca chega
  // a ser pintada na tela.
  const pieceSyncWorkspaceIdRef = useRef(undefined);
  if (pieceSyncWorkspaceIdRef.current !== activeWorkspace?.id) {
    pieceSyncWorkspaceIdRef.current = activeWorkspace?.id;
    const raw = activeWorkspace?.pieces;
    const arr = Array.isArray(raw) ? raw : [];
    setSavedPieces(arr);
    // SEMPRE atualizar — evita stale pieceId de caderno anterior (regressão Aline)
    setActivePieceId(arr.length > 0 ? arr[0]?.id || null : null);
  }

  const persistPieces = async (nextPieces) => {
    try {
      await repoPersistPieces(workspaceKey, nextPieces);
    } catch {
      /* silently */
    }
  };

  // FIX DUPLO-CLIQUE (20/08/2026): trava simples para impedir que um clique
  // duplo em "Nova Peça" crie duas peças em branco em paralelo.
  const addingPieceRef = useRef(false);
  const [isAddingPiece, setIsAddingPiece] = useState(false);
  const handleAddPiece = async () => {
    if (addingPieceRef.current) return;
    addingPieceRef.current = true;
    setIsAddingPiece(true);
    try {
      const newId = `piece-${Date.now()}`;
      let newPiece = { id: newId, pieceType: "" };

      // FIX ESTRUTURA CADERNO > PEÇAS (21/08/2026): cada peça agora ganha a
      // sua PRÓPRIA pasta + Anexos no Drive, dentro da pasta do caderno —
      // antes, todas as peças de um caderno compartilhavam (e renomeavam) a
      // mesma pasta única, o que parecia "sobrescrever" a peça anterior.
      const parentFolderId = String(
        activeWorkspace?.cadernoFolderId ||
          activeWorkspace?.processRootFolderId ||
          "",
      ).trim();
      const baseUrl = getBackendBaseUrl();
      if (parentFolderId && baseUrl) {
        try {
          const resp = await fetch(`${baseUrl}/drive/ensure-workspace-folders`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              processRootFolderId: parentFolderId,
              workspaceId: `${workspaceKey}-${newId}`,
              pieceType: "Nova Peça",
              clientId: activeWorkspace?.clientId || "",
              clientName: activeWorkspace?.clientName || "",
              // FIX DATA-PREFIXO EM PEÇA (22/09/2026): estava `false` aqui, então
              // a peça nascia sem data ("Nova Peça" puro) e o rename posterior
              // pro tipo real (handleChangePieceType) também nunca aplicava
              // data — resultado: nenhuma peça criada por este fluxo ganhava
              // data, em nenhum cliente. `true` (== default do backend) faz a
              // pasta nascer "AAAA-MM-DD - Nova Peça"; o prefixo devolvido é
              // extraído abaixo e preservado no rename pro tipo real.
              includeDatePrefix: true,
            }),
          });
          const payload = await resp.json().catch(() => ({}));
          if (resp.ok) {
            const createdFolderName = String(payload?.pieceFolder?.name || "").trim();
            const datePrefixMatch = createdFolderName.match(/^(\d{4}-\d{2}-\d{2})\s*-\s*/);
            newPiece = {
              ...newPiece,
              processPieceFolderId: String(payload?.pieceFolder?.id || "").trim() || null,
              processPieceFolderName: createdFolderName || null,
              // Data de criação real da pasta (vinda do backend, não calculada
              // no cliente — evita o bug de timezone UTC-3 do CLAUDE.md).
              // Preservada em todo rename subsequente do tipo da peça.
              pieceFolderDatePrefix: datePrefixMatch ? datePrefixMatch[1] : null,
              attachmentsFolderId: String(payload?.attachmentsFolder?.id || "").trim() || null,
              attachmentsFolderName: String(payload?.attachmentsFolder?.name || "").trim() || "Anexos",
            };
          }
        } catch {
          /* segue sem pasta própria — cai no fallback do caderno (legado) */
        }
      }

      const next = [...savedPieces, newPiece];
      setSavedPieces(next);
      setActivePieceId(newId);
      setActiveViewDoc(null);
      await persistPieces(next);
    } finally {
      addingPieceRef.current = false;
      setIsAddingPiece(false);
    }
  };

  const handleRemovePiece = async (pieceId) => {
    // MC60 (03/09/2026, incidente real: caderno inteiro apagado por
    // engano ao tentar apagar só uma peça) — esta função não tinha
    // NENHUMA confirmação. O comentário original dizia "já apagar os
    // anexos da peça no Drive antes de remover o registro" — **falso**:
    // `hubBridge.handleRemovePieceById` nunca existiu em lugar nenhum do
    // código (`grep` no repo inteiro não acha definição, só esta chamada).
    // `if (hubBridge?.handleRemovePieceById)` era sempre falsy e o corpo
    // do if nunca rodava — achado real 03/09/2026 (titular reportou:
    // excluir peça some do Portal mas a pasta continua no Drive).
    // Corrigido: chama /drive/delete-folder de verdade, mesmo padrão já
    // usado (e funcionando) em handleNukeClientWorkspaces
    // (AutosDashboard.jsx) — abortar a remoção no Firestore se o Drive
    // falhar, pra nunca ORFANAR a pasta (apagar a única referência que
    // levava até ela sem ter certeza que ela já foi removida).
    const targetPiece = savedPieces.find((p) => p.id === pieceId);
    const targetLabel =
      String(targetPiece?.pieceType || "").trim() || "esta peça (sem tipo definido)";
    const confirmado = window.confirm(
      `Apagar "${targetLabel}"?\n\n` +
        `Isso remove a peça e os anexos vinculados a ela — NÃO apaga o resto do caderno, ` +
        `só esta peça específica.\n\nEsta ação não tem desfazer. Clique Cancelar se não tiver certeza.`,
    );
    if (!confirmado) return;
    // Limpar a pasta da peça no Drive ANTES de remover o registro.
    const baseUrl = getBackendBaseUrl();
    const pieceDriveFolderIds = [
      ...new Set(
        [
          targetPiece?.processPieceFolderId,
          targetPiece?.attachmentsFolderId,
        ]
          .map((id) => String(id || "").trim())
          .filter((id) => id && id !== "undefined" && id !== "null"),
      ),
    ];
    if (pieceDriveFolderIds.length > 0) {
      if (!baseUrl) {
        window.alert(
          "Backend indisponível — não foi possível apagar a pasta da peça no Google Drive. " +
            "A peça NÃO foi removida do Portal (pra não deixar a pasta órfã sem referência nenhuma). " +
            "Tente novamente quando o backend estiver acessível.",
        );
        return;
      }
      const driveResults = await Promise.allSettled(
        pieceDriveFolderIds.map((folderId) =>
          fetch(`${baseUrl}/drive/delete-folder`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ folderId }),
          }).then(async (res) => {
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok)
              throw new Error(String(data?.detail || data?.error || "Drive API delete failed"));
            return data;
          }),
        ),
      );
      const driveFailCount = driveResults.filter((r) => r.status === "rejected").length;
      if (driveFailCount > 0) {
        window.alert(
          `Falha ao apagar ${driveFailCount} pasta(s) da peça no Google Drive. ` +
            `A peça NÃO foi removida do Portal (pra não deixar a pasta órfã). ` +
            `Verifique as permissões do Drive e tente novamente.`,
        );
        return;
      }
    }
    const next = savedPieces.filter((p) => p.id !== pieceId);
    setSavedPieces(next);
    if (activePieceId === pieceId) {
      if (next.length > 0) {
        setActivePieceId(next[0]?.id || null);
      } else {
        setActivePieceId(null);
        // Limpar nome no motor de anexos quando não há mais peças
        if (hubBridge?.commitPieceType) hubBridge.commitPieceType("");
      }
    }
    persistPieces(next);
  };

  // Estado diferido para evitar "Cannot update a component while rendering"
  const [pendingPieceType, setPendingPieceType] = useState(null);

  useEffect(() => {
    if (pendingPieceType !== null && hubBridge?.commitPieceType) {
      hubBridge.commitPieceType(pendingPieceType);
      setPendingPieceType(null);
    }
  }, [pendingPieceType, hubBridge]);

  const handleChangePieceType = (pieceId, newType) => {
    const currentPieces = savedPiecesRef.current;
    const targetPiece = currentPieces.find((p) => p.id === pieceId);
    // FIX DATA-PREFIXO EM PEÇA (22/09/2026): preserva a data de criação da
    // pasta (gravada em pieceFolderDatePrefix no momento do handleAddPiece)
    // ao renomear pro tipo real — sem isso, o rename trocava o nome pelo
    // tipo puro e a data se perdia pra sempre. Peça legada sem
    // pieceFolderDatePrefix (criada antes deste fix) segue sem data aqui;
    // ganha data só via backfill dedicado (não neste rename pontual).
    const datePrefix = String(targetPiece?.pieceFolderDatePrefix || "").trim();
    const newFolderName = datePrefix ? `${datePrefix} - ${newType}` : newType;
    const next = currentPieces.map((p) =>
      p.id === pieceId
        ? { ...p, pieceType: newType, processPieceFolderName: newFolderName }
        : p,
    );
    setSavedPieces(next);
    persistPieces(next);

    // FIX ESTRUTURA CADERNO > PEÇAS (21/08/2026): renomeia a pasta DAQUELA
    // peça especificamente — cada peça tem sua própria pasta agora, então
    // não dá para reaproveitar hubBridge.applyPieceTypeLabel (que só sabe
    // renomear "a peça ativa"). Pode-se renomear uma peça sem ela estar
    // selecionada no momento.
    // FIX CADERNO LEGADO (02/09/2026): cadernos criados antes dessa
    // reestruturação têm cadernoFolderId, mas a peça em si nunca ganhou seu
    // próprio processPieceFolderId — esse dado só existe no campo antigo,
    // no nível do workspace (activeWorkspace.processPieceFolderId). Sem
    // fallback, o rename falhava em silêncio pra todo caderno anterior à
    // reestruturação. Confirmado: caso real [CLIENTE_A] vs.
    // UNICURITIBA. O fallback só vale pra peça ATIVA — pra outra peça sem
    // pasta própria não há como saber a qual pasta legada ela corresponde.
    const folderId = String(
      targetPiece?.processPieceFolderId ||
        (pieceId === activePieceId ? activeWorkspace?.processPieceFolderId : "") ||
        "",
    ).trim();
    if (folderId && hubBridge?.renamePieceFolderOnDrive) {
      hubBridge.renamePieceFolderOnDrive(folderId, newFolderName);
    }
    // Só atualiza o título/checklist exibido no motor de anexos se a peça
    // renomeada é a que está aberta agora — renomear outra não deve mexer
    // no que está na tela.
    if (pieceId === activePieceId && hubBridge?.applyPieceTypeLabel) {
      hubBridge.applyPieceTypeLabel(newType);
    }
  };

  // ─── Renomear peça inline ─────────────────────────────────────────────────
  const [editingPieceName, setEditingPieceName] = useState(null);
  const [editingPieceNameValue, setEditingPieceNameValue] = useState("");

  const handleStartRenamePiece = (piece) => {
    setEditingPieceName(piece.id);
    setEditingPieceNameValue(piece.pieceType || "");
  };

  const handleConfirmRenamePiece = () => {
    if (!editingPieceName) return;
    const newType = editingPieceNameValue.trim();
    if (newType) {
      handleChangePieceType(editingPieceName, newType);
    }
    setEditingPieceName(null);
    setEditingPieceNameValue("");
  };

  // P27-FIX: Forçar cancelamento de rename quando seleciona outra peça
  const ensureRenameCancelled = useCallback(() => {
    if (editingPieceName) {
      setEditingPieceName(null);
      setEditingPieceNameValue("");
    }
  }, [editingPieceName]);

  // ─── Protocolar peça (22/09/2026) ──────────────────────────────────────────
  // Mecanismo novo (Camada 2, item 2 do backfill de data-prefixo): quando a
  // peça é de fato protocolada, renomeia a pasta no Drive com a data REAL do
  // protocolo (não a de criação) e mantém processPieceFolderName/
  // pieceFolderDatePrefix em sincronia, para que uma futura troca de tipo
  // (handleChangePieceType) preserve essa data, não a de criação.
  const [protocolModalPiece, setProtocolModalPiece] = useState(null);
  const [protocolDateValue, setProtocolDateValue] = useState("");
  const [protocolNotice, setProtocolNotice] = useState("");

  const handleOpenProtocolarPiece = (piece) => {
    if (!piece) return;
    // new Date().toISOString() é UTC — em UTC-3 pode voltar um dia (regra
    // de timezone do CLAUDE.md). Monta AAAA-MM-DD a partir dos getters
    // locais para o valor default sempre bater com "hoje" no fuso do titular.
    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const defaultDate =
      piece.protocoladoEm ||
      `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    setProtocolModalPiece(piece);
    setProtocolDateValue(defaultDate);
    setProtocolNotice("");
  };

  const handleCancelProtocolarPiece = () => {
    setProtocolModalPiece(null);
    setProtocolDateValue("");
    setProtocolNotice("");
  };

  const handleConfirmProtocolarPiece = () => {
    const piece = protocolModalPiece;
    if (!piece) return;
    const dateStr = String(protocolDateValue || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      setProtocolNotice("Data inválida — use o seletor de calendário.");
      return;
    }
    const pieceTypeLabel = String(piece.pieceType || "").trim();
    if (!pieceTypeLabel) {
      setProtocolNotice("Defina o tipo da peça antes de protocolar.");
      return;
    }
    const newFolderName = `${dateStr} - ${pieceTypeLabel}`;
    const currentPieces = savedPiecesRef.current;
    const next = currentPieces.map((p) =>
      p.id === piece.id
        ? {
            ...p,
            processPieceFolderName: newFolderName,
            pieceFolderDatePrefix: dateStr,
            protocoladoEm: dateStr,
          }
        : p,
    );
    setSavedPieces(next);
    persistPieces(next);

    const folderId = String(
      piece?.processPieceFolderId ||
        (piece.id === activePieceId ? activeWorkspace?.processPieceFolderId : "") ||
        "",
    ).trim();
    if (folderId && hubBridge?.renamePieceFolderOnDrive) {
      hubBridge.renamePieceFolderOnDrive(folderId, newFolderName);
    }

    setProtocolModalPiece(null);
    setProtocolDateValue("");
    setProtocolNotice("");
  };

  const handleCancelRenamePiece = () => {
    setEditingPieceName(null);
    setEditingPieceNameValue("");
  };

  const handleSelectPiece = (piece) => {
    setActivePieceId(piece.id);
    setActiveViewDoc(null);
    // ANTI-RETROCESSO-03 (01/05/2026): usar applyPieceTypeLabel (não destrutivo) em vez de
    // commitPieceType ao selecionar peça existente. commitPieceType zera assignments e grava
    // estado vazio no Firestore — causava regressão recorrente (caso [CLIENTE_A], 3ª ocorrência).
    if (piece.pieceType && hubBridge?.applyPieceTypeLabel)
      hubBridge.applyPieceTypeLabel(piece.pieceType);
  };

  // MC_V11: Inline editing — salvar alias do caderno no Firestore
  // FIX RENAME CADERNO -> DRIVE (21/08/2026): isso só gravava o alias no
  // Firestore (o que já atualizava o rótulo na barra lateral), mas nunca
  // renomeava a pasta do caderno (cadernoFolderId) no Drive de verdade —
  // o Portal mostrava o nome novo, o Drive continuava com o nome antigo.
  const handleFinishInlineEdit = async (workspaceId) => {
    const val = editingNodeValue.trim();
    setEditingNodeId(null);
    setEditingNodeValue("");
    if (!val || !workspaceId || !appId) return;
    try {
      await updateWorkspaceAlias(workspaceId, val);
    } catch {
      /* silently */
    }
    const targetWorkspace = sortedWorkspaces.find((w) => String(w?.id || "") === workspaceId);
    const cadernoFolderId = String(targetWorkspace?.cadernoFolderId || "").trim();
    if (cadernoFolderId) {
      const baseUrl = getBackendBaseUrl();
      if (baseUrl) {
        fetch(`${baseUrl}/drive/rename-folder`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ folder_id: cadernoFolderId, desired_name: val }),
        }).catch(() => {});
      }
    }
  };

  // Sincroniza selectedClient com activeWorkspace quando este muda (troca externa).
  // MC_Z1-FIX2: NÃO sobrescrever o contexto do cliente se o cliente atual é co-parte
  // do workspace aberto. Previne que clicar no caderno da [PET_A] (a partir de [PET_B])
  // mude o header de volta para [PET_A].
  useEffect(() => {
    if (!activeWorkspace) return;
    // FIX (22/08/2026, causa raiz [PET_A]): NÃO cair para clientName aqui.
    // selectedClientId vira o path de doc do Firestore (clients/{id}) no
    // efeito de onSnapshot logo abaixo — se clientId estiver vazio (dado
    // corrompido), usar o nome no lugar do ID monta um path inexistente
    // (clients/[PET_A] em vez de clients/7gXcBl...) e o snapshot falha
    // silenciosamente. Sem clientId real, simplesmente não atualiza — fica
    // com o contexto anterior em vez de um contexto errado.
    const cid = String(activeWorkspace?.clientId || "").trim();
    const cname = String(
      activeWorkspace?.clientName || activeWorkspace?.clientNameSnapshot || "",
    ).trim();
    const currentCid = String(selectedClientIdRef.current || "").trim();
    if (currentCid && currentCid !== cid) {
      const parties = Array.isArray(activeWorkspace?.partiesRefs)
        ? activeWorkspace.partiesRefs
        : [];
      if (
        parties.some((p) => String(p?.clientId || "").trim() === currentCid)
      ) {
        return; // cliente atual é co-parte — manter contexto
      }
    }
    if (cid) setSelectedClientId(cid);
    if (cname) setSelectedClientName(cname);
    const meta = extractClientMeta(activeWorkspace);
    if (meta.cpf || meta.cnpj || meta.email) setSelectedClientMeta(meta);
  }, [activeWorkspace?.id]);

  // ═══ FIX CRM → CADERNO (20/07/2026): onSnapshot para escutar mudancas no cliente ═══
  // Quando o usuario altera o nome/qualificacao do cliente no CRM, o Firestore
  // atualiza o documento artifacts/{appId}/public/data/clients/{clientId}. Mas o
  // CadernoCockpit nunca ficava sabendo porque selectedClientName so era lido
  // do activeWorkspace na inicializacao. Este efeito escuta onSnapshot do CRM
  // e atualiza o estado em tempo real, propagando para a bridge → Word.
  useEffect(() => {
    const wsKey = String(activeWorkspace?.id || "").trim();
    const clientId = String(
      selectedClientIdRef.current || activeWorkspace?.clientId || "",
    ).trim();
    if (!wsKey || !clientId) return;

    const clientRef = doc(
      db,
      "artifacts",
      appId,
      "public",
      "data",
      "clients",
      clientId,
    );
    console.log("[DEBUG-Q] onSnapshot clientRef clientId:", clientId);
    const unsub = onSnapshot(clientRef, function (snap) {
      if (!snap.exists()) {
        console.log(
          "[DEBUG-Q] onSnapshot: documento nao existe para clientId:",
          clientId,
        );
        return;
      }
      var data = snap.data();

      // Atualiza nome do cliente
      // FIX CAUSA RAIZ (20/07/2026): O nome NUNCA esta em data.name ou data.clientName.
      // O schema real (ClientSchema.js) armazena em qualification.physical.name (PF),
      // qualification.physical.socialName (nome social), qualification.legal.companyName (PJ).
      var phys = data?.qualification?.physical || {};
      var legal = data?.qualification?.legal || {};
      var newName = String(
        phys?.socialName ||
          phys?.name ||
          legal?.companyName ||
          legal?.tradeName ||
          data?.name ||
          "",
      ).trim();
      if (newName && newName !== selectedClientName) {
        setSelectedClientName(newName);
      }

      // Atualiza metadados (CPF, CNPJ, email)
      var meta = extractClientMeta(data);
      if (meta.cpf || meta.cnpj || meta.email) {
        setSelectedClientMeta(meta);
      }

      // PROPAGA PARA O BRIDGE + FIRESTORE: avisa o useFileHub que o cliente mudou
      // para que o sync salve no workspace doc e o Word receba via postBridgeState
      if (hubBridge?.setClientData) {
        hubBridge.setClientData({
          clientId: clientId,
          clientName: newName || selectedClientName,
          clientCpf: meta.cpf || "",
          clientEmail: meta.email || "",
        });
      }
    });

    return function () {
      unsub();
    };
  }, [activeWorkspace?.id, selectedClientId]);

  // Formatadores leves (sem dependências externas) — MC_Y3
  const formatCPF = (s) => {
    const d = String(s || "")
      .replace(/\D/g, "")
      .slice(0, 11);
    if (d.length !== 11) return String(s || "").trim();
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  };
  const formatCNPJ = (s) => {
    const d = String(s || "")
      .replace(/\D/g, "")
      .slice(0, 14);
    if (d.length !== 14) return String(s || "").trim();
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
  };
  const readableClientTag = useMemo(() => {
    const { cpf, cnpj, email } = selectedClientMeta || {};
    if (cnpj) return { label: "CNPJ", value: formatCNPJ(cnpj) };
    if (cpf) return { label: "CPF", value: formatCPF(cpf) };
    if (email) return { label: "E-mail", value: email };
    return null;
  }, [selectedClientMeta]);

  // Lista efetiva de cadernos do cliente ativo (prop tem prioridade; senão filtra allWorkspaces).
  const effectiveClientWorkspaces = useMemo(() => {
    const idKey = String(selectedClientId || "")
      .trim()
      .toUpperCase();
    const nameKey = String(selectedClientName || "")
      .trim()
      .toUpperCase();
    const _match = (wId, wName) =>
      (idKey && (wId === idKey || wName === idKey)) ||
      (nameKey && (wId === nameKey || wName === nameKey));
    if (!idKey && !nameKey) return [];
    // MC_Z1-FIX: SEMPRE varrer allWorkspaces para incluir cadernos compartilhados
    // (via partiesRefs) além dos cadernos próprios do cliente. Evita regressão
    // onde clientWorkspaces early-return excluía cadernos de co-parte.
    const all = Array.isArray(allWorkspaces) ? allWorkspaces : [];
    const seen = new Set();
    const result = [];
    // MC_Y8: clientWorkspaces do pai tem prioridade (dados frescos) se pertencer ao cliente.
    if (Array.isArray(clientWorkspaces) && clientWorkspaces.length > 0) {
      const sample = clientWorkspaces[0];
      const sId = String(sample?.clientId || "")
        .trim()
        .toUpperCase();
      const sName = String(sample?.clientName || "")
        .trim()
        .toUpperCase();
      if (_match(sId, sName)) {
        for (const cw of clientWorkspaces) {
          const cwId = String(cw?.id || "");
          if (cwId && !seen.has(cwId)) {
            seen.add(cwId);
            result.push(cw);
          }
        }
      }
    }
    // MC_Z1: varrer allWorkspaces para match direto + co-parte (partiesRefs)
    for (const w of all) {
      const wsId = String(w?.id || "");
      if (!wsId || seen.has(wsId)) continue;
      const wId = String(w?.clientId || "")
        .trim()
        .toUpperCase();
      const wName = String(w?.clientName || "")
        .trim()
        .toUpperCase();
      if (_match(wId, wName)) {
        seen.add(wsId);
        result.push(w);
        continue;
      }
      if (idKey) {
        const parties = Array.isArray(w?.partiesRefs) ? w.partiesRefs : [];
        if (
          parties.some(
            (p) =>
              String(p?.clientId || "")
                .trim()
                .toUpperCase() === idKey,
          )
        ) {
          seen.add(wsId);
          result.push(w);
        }
      }
    }
    return result;
  }, [clientWorkspaces, allWorkspaces, selectedClientId, selectedClientName]);

  // MC104 (12/09/2026): novo modo de ordenação "Por vínculos" (pedido do
  // titular, item da lista de reformulação) — agrupa cada processo dentro
  // da família a que pertence (Principal + Apensos + Correlatos), como
  // uma árvore de pastas do Windows Explorer, em vez de lista achatada.
  // Reaproveita a MESMA lógica de grafo já validada na aba Vínculos
  // (findRoot sobe por processoPrincipalId, buildTree desce por apensos,
  // ambos com guarda contra ciclo — MC64 lá embaixo). Duplicada aqui de
  // propósito, não extraída pra módulo compartilhado — evita arriscar
  // mexer no código já aprovado da aba Vínculos por causa de um recurso
  // menor e novo. Diferente da aba (que resolve vínculo cross-cliente via
  // `allWorkspaces`), aqui o grafo só enxerga o cliente selecionado —
  // cruzar cliente continua sendo só papel da aba Vínculos.
  const vinculosFlat = useMemo(() => {
    if (workspaceSortMode !== "vinculos") return null;
    const list = effectiveClientWorkspaces;
    const findById = (id) => list.find((item) => item.id === id);

    const findRoot = (workspace) => {
      const visited = new Set();
      let current = workspace;
      while (current?.processoPrincipalId && !visited.has(current.id)) {
        visited.add(current.id);
        const parent = findById(current.processoPrincipalId);
        if (!parent || visited.has(parent.id)) break;
        current = parent;
      }
      return current;
    };

    const buildTree = (workspace, visited) => {
      visited.add(workspace.id);
      const apensoIds = Array.isArray(workspace?.apensos) ? workspace.apensos : [];
      const children = apensoIds
        .map(findById)
        .filter(Boolean)
        .filter((child) => !visited.has(child.id))
        .map((child) => buildTree(child, visited));
      return { workspace, children };
    };

    // MC104b (12/09/2026): achado do titular — a primeira versão só usava
    // `findRoot` puro (sobe por processoPrincipalId) sem a correção da
    // aba Vínculos (MC71 lá embaixo): se o item de onde eu partia era uma
    // "folha" sem apenso próprio, mas o CORRELATO dela tinha família de
    // verdade (apensos), o item errado virava "Principal" e a família
    // real virava "Correlato" dele — exatamente invertido. Reaplicando a
    // MESMA regra aqui: se a raiz encontrada não tem apenso próprio, mas
    // um dos correlatos dela tem, o correlato "mais rico" assume o posto
    // de Principal.
    const resolveFamilyRoot = (workspace) => {
      const myRoot = findRoot(workspace);
      if (!myRoot) return myRoot;
      if (Array.isArray(myRoot.apensos) && myRoot.apensos.length > 0) return myRoot;
      const correlatoIds = Array.isArray(myRoot.processosCorrelatos) ? myRoot.processosCorrelatos : [];
      const richerCorrelato = correlatoIds
        .map(findById)
        .filter(Boolean)
        .find((c) => Array.isArray(c.apensos) && c.apensos.length > 0);
      return richerCorrelato || myRoot;
    };

    // `guides[i]` = true → uma linha vertical "│" precisa continuar na
    // coluna i (um ancestral naquele nível ainda tem irmão depois); false
    // → coluna em branco (aquele ancestral já era o último irmão). O
    // último item de `guides` também decide o conector do PRÓPRIO nó:
    // "├─" (tem irmão depois) ou "└─" (é o último).
    // `parentId` (MC104h, 12/09/2026): achado do titular — pediu organograma
    // de verdade (linha fixa/tracejada com junção em T, tipo SmartArt do
    // Word), rotacionado pra caber na coluna estreita (tronco vertical,
    // ramo curto pra cada caixa). Isso exige saber a posição REAL de cada
    // caixa na tela pra desenhar a linha certa — não dá pra chutar altura
    // (as 3 tentativas anteriores, com texto e depois CSS esticado,
    // falharam exatamente por isso). Guardar o `parentId` aqui é o que
    // permite, no render, medir a posição de verdade das duas caixas
    // (via ref + getBoundingClientRect) e desenhar um SVG com a linha
    // ligando as coordenadas reais medidas — nunca calculadas às cegas.
    const flatten = (node, relation, depth, guides, isFamilyRoot, parentId) => {
      const rows = [{ workspace: node.workspace, relation, depth, guides, isFamilyRoot, parentId }];
      const kids = node.children || [];
      kids.forEach((child, i) => {
        const hasMoreAfter = i < kids.length - 1;
        rows.push(...flatten(child, "apenso", depth + 1, [...guides, hasMoreAfter], false, node.workspace.id));
      });
      return rows;
    };

    // MC104c (12/09/2026): achado do titular — a família inteira aparecia
    // DUAS VEZES na lista. Causa: eu só marcava a RAIZ como "já
    // processada" (`rootIds`), mas quando o loop passava por outro membro
    // da MESMA família que nunca tinha sido raiz de nada por si só (ex.:
    // o correlato-folha, sem apenso próprio), `resolveFamilyRoot` dele
    // apontava pra família certa — só que essa raiz já tinha virado
    // "correlato" da 1ª passagem (nunca entrou em `rootIds`), então o
    // código montava a família de novo do zero. Correção: marcar TODO
    // membro de uma família já construída (raiz, apenso, correlato) —
    // não só a raiz — antes de seguir pro próximo item da lista.
    const renderedIds = new Set();
    const families = [];
    list.forEach((w) => {
      if (renderedIds.has(w.id)) return;
      const root = resolveFamilyRoot(w);
      if (!root || renderedIds.has(root.id)) return;
      const visited = new Set();
      const tree = buildTree(root, visited);
      let rows = flatten(tree, "principal", 0, [], true, null);
      const correlatoIds = Array.isArray(root?.processosCorrelatos) ? root.processosCorrelatos : [];
      const correlatos = correlatoIds
        .map(findById)
        .filter(Boolean)
        .filter((c) => !visited.has(c.id));
      correlatos.forEach((correlato, i) => {
        const hasMoreAfter = i < correlatos.length - 1;
        const correlatoTree = buildTree(correlato, visited);
        rows = rows.concat(
          flatten(correlatoTree, "correlato", 1, [hasMoreAfter], false, root.id),
        );
      });
      rows.forEach((r) => renderedIds.add(r.workspace.id));
      // `familySize` vai em cada linha pra o render saber se vale a pena
      // rotular a raiz como "Principal" — família de 1 (caderno solto,
      // sem apenso/correlato nenhum) não precisa desse rótulo.
      families.push(rows.map((r) => ({ ...r, familySize: rows.length })));
    });

    // Famílias inteiras (não cadernos soltos) ordenadas pela mesma regra
    // do modo "criação" — a família aparece na posição do membro mais
    // antigo dela; não existe "nome único" de família pra ordenar por
    // outro critério óbvio.
    const toMsLocal = (ts) => {
      if (!ts) return Infinity;
      if (typeof ts?.toMillis === "function") return ts.toMillis();
      if (typeof ts?.seconds === "number") return ts.seconds * 1000;
      if (ts instanceof Date) return ts.getTime();
      return Infinity;
    };
    families.sort((a, b) => {
      const minA = Math.min(...a.map((r) => toMsLocal(r.workspace?.createdAt)));
      const minB = Math.min(...b.map((r) => toMsLocal(r.workspace?.createdAt)));
      return minA - minB;
    });

    return families.flat();
  }, [effectiveClientWorkspaces, workspaceSortMode]);

  const vinculosMetaById = useMemo(() => {
    if (!vinculosFlat) return null;
    return new Map(vinculosFlat.map((r) => [r.workspace.id, r]));
  }, [vinculosFlat]);

  // MC104h (12/09/2026): organograma de verdade no modo "Por Vínculos" —
  // pedido do titular, com referência de um SmartArt do Word (tronco +
  // ramo em T, linha fixa = apenso, tracejada = correlato). As 3
  // tentativas anteriores de conector falharam por tentar CALCULAR a
  // posição da linha (texto, CSS esticado, setinha solta) sem poder ver a
  // tela renderizada de verdade. Esta versão MEDE a posição real de cada
  // caixa (`getBoundingClientRect`, depois do navegador já ter pintado a
  // tela) e desenha 1 SVG com uma linha por aresta pai→filho, usando as
  // coordenadas medidas — nunca chutadas. `vinculosBoxRefs` é um Map (não
  // array de refs) porque as caixas têm id dinâmico e a lista pode mudar
  // de tamanho a qualquer momento (nova aba de cliente, novo caderno).
  const vinculosContainerRef = useRef(null);
  const vinculosBoxRefs = useRef(new Map());
  const [vinculosLines, setVinculosLines] = useState([]);

  const recomputeVinculosLines = useCallback(() => {
    if (
      workspaceSortMode !== "vinculos" ||
      !vinculosFlat ||
      !vinculosContainerRef.current
    ) {
      setVinculosLines((prev) => (prev.length ? [] : prev));
      return;
    }
    const containerRect = vinculosContainerRef.current.getBoundingClientRect();
    // MC104m (12/09/2026): achado do titular, confirmado numa reprodução
    // isolada (fora do app, só HTML+SVG com os mesmos números) — o bug
    // real não era "desconectado", era o CONTRÁRIO: toda linha (a1, a2,
    // a3, correlato) desenhava o percurso INTEIRO desde o fundo do PAI
    // até ela mesma. Como o correlato é sempre o último da lista, a
    // linha DELE cobria de novo, tracejada, o mesmo trecho que a1/a2/a3
    // já tinham desenhado sólido — sólido e tracejado brigando pelos
    // MESMOS pixels. Corrigido agrupando os filhos por pai e usando um
    // "cursor" de Y que anda pra baixo: cada irmão desenha só o trecho
    // NOVO (do cursor até ele mesmo), nunca repetindo o que o anterior já
    // cobriu — por isso só o ÚLTIMO trecho (o do correlato) fica
    // tracejado, igual ao desenho à mão do titular.
    const childrenByParent = new Map();
    vinculosFlat.forEach((row) => {
      if (!row.parentId) return;
      if (!childrenByParent.has(row.parentId)) childrenByParent.set(row.parentId, []);
      childrenByParent.get(row.parentId).push(row);
    });
    const lines = [];
    childrenByParent.forEach((children, parentId) => {
      const parentEl = vinculosBoxRefs.current.get(parentId);
      if (!parentEl) return;
      const parentRect = parentEl.getBoundingClientRect();
      const trunkX = Math.round(parentRect.left - containerRect.left + 8);
      let cursorY = Math.round(parentRect.bottom - containerRect.top);
      children.forEach((row) => {
        const childEl = vinculosBoxRefs.current.get(row.workspace.id);
        if (!childEl) return;
        const childRect = childEl.getBoundingClientRect();
        const y2 = Math.round(
          childRect.top + childRect.height / 2 - containerRect.top,
        );
        const x2 = Math.round(childRect.left - containerRect.left);
        lines.push({
          key: row.workspace.id,
          dashed: row.relation === "correlato",
          d: `M ${trunkX} ${cursorY} L ${trunkX} ${y2} L ${x2} ${y2}`,
        });
        cursorY = y2;
      });
    });
    setVinculosLines(lines);
  }, [workspaceSortMode, vinculosFlat]);

  useLayoutEffect(() => {
    recomputeVinculosLines();
  }, [recomputeVinculosLines]);

  // MC104o (15/09/2026, achado ao vivo — Chrome real + CDP): titular
  // relatou linha não encostando no topo da caixa principal, e níveis
  // inferiores não encostando no canto da linha do nível acima. Medindo o
  // DOM real (não só lendo o código): o `useLayoutEffect` acima calcula
  // certo NO MOMENTO em que roda, mas só reexecuta quando `vinculosFlat`
  // muda de identidade — qualquer reflow DEPOIS disso (badge de status
  // carregando async, ícone de fonte terminando de carregar, altura de
  // uma caixa mudando por qualquer motivo) desalinha as linhas em
  // definitivo, porque nada dispara um novo cálculo. Confirmado: um
  // `getBoundingClientRect()` direto no DOM, momentos depois do primeiro
  // cálculo, já mostrava caixas ~12px deslocadas das linhas que deveriam
  // apontar pra elas — sem NENHUMA mudança de `vinculosFlat`/sort mode no
  // meio. `ResizeObserver` no container corrige de vez: recalcula sempre
  // que a altura renderizada de verdade mudar, não importa a causa —
  // mesma filosofia de "medir de verdade, nunca chutar" do resto deste
  // recurso, agora aplicada de forma contínua, não só uma vez.
  useEffect(() => {
    if (workspaceSortMode !== "vinculos") return undefined;
    window.addEventListener("resize", recomputeVinculosLines);
    const el = vinculosContainerRef.current;
    let ro;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => recomputeVinculosLines());
      ro.observe(el);
    }
    return () => {
      window.removeEventListener("resize", recomputeVinculosLines);
      if (ro) ro.disconnect();
    };
  }, [workspaceSortMode, recomputeVinculosLines]);

  // MC_V11.1: Aplica ordenação escolhida pelo usuário sobre a lista filtrada.
  const sortedWorkspaces = useMemo(() => {
    if (workspaceSortMode === "vinculos" && vinculosFlat) {
      return vinculosFlat.map((r) => r.workspace);
    }
    const list = [...effectiveClientWorkspaces];
    const toMs = (ts) => {
      if (!ts) return 0;
      if (typeof ts?.toMillis === "function") return ts.toMillis();
      if (typeof ts?.seconds === "number") return ts.seconds * 1000;
      if (ts instanceof Date) return ts.getTime();
      return 0;
    };
    if (workspaceSortMode === "status") {
      // MC103 — ver STATUS_GROUP_ORDER/getStatusGroupKey no topo do
      // arquivo. Dentro de cada grupo, ordena por nome (mesmo critério do
      // modo "alpha") — previsível e fácil de escanear.
      list.sort((a, b) => {
        const ga = STATUS_GROUP_ORDER.indexOf(getStatusGroupKey(a));
        const gb = STATUS_GROUP_ORDER.indexOf(getStatusGroupKey(b));
        if (ga !== gb) return ga - gb;
        const la = String(a?.alias || a?.name || "").trim().toLowerCase();
        const lb = String(b?.alias || b?.name || "").trim().toLowerCase();
        return la.localeCompare(lb, "pt-BR");
      });
    } else if (workspaceSortMode === "alpha") {
      list.sort((a, b) => {
        const la = String(a?.alias || a?.name || "")
          .trim()
          .toLowerCase();
        const lb = String(b?.alias || b?.name || "")
          .trim()
          .toLowerCase();
        return la.localeCompare(lb, "pt-BR");
      });
    } else if (workspaceSortMode === "numero") {
      // Ordenação NUMÉRICA por nº de processo (24/08/2026, pedido do
      // titular): mesma sortKey usada como ordem-base em AutosDashboard.jsx
      // (2ª correção, rodada [PET_A] — numeroCNJ quando existe, senão
      // cliente+alias/tipo de peça, sempre determinística), reaproveitada
      // aqui em vez de reinventar a regra de comparação de CNJ. Números CNJ
      // têm o mesmo nº de dígitos no bloco sequencial (7), então
      // localeCompare em string já ordena "numericamente" corretamente
      // (ex.: 5002971-77 antes de 5619870-38 antes de 5768922-74).
      const sortKey = (w) => {
        const npu = String(w?.numeroCNJ || w?.ancestry?.primary_process_id || "").trim();
        const client = String(w?.clientName || "").trim().toUpperCase();
        const label = String(w?.alias || w?.pieceType || "").trim().toUpperCase();
        return `${npu ? "0" : "1"}|${npu}|${client}|${label}`;
      };
      list.sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    } else {
      // 'created' (também usado como fallback pra qualquer valor antigo de
      // sessionStorage que ainda diga "modified", removido do ciclo em
      // 22/08/2026) — ordem de criação, nunca muda sozinha.
      list.sort((a, b) => toMs(a?.createdAt) - toMs(b?.createdAt));
    }
    return list;
  }, [effectiveClientWorkspaces, workspaceSortMode, vinculosFlat]);

  // ─── Peças publicadas vinculadas ao caderno (Contrato, Procuração, Petição…) ──
  // FIX BRIDGE PARTES (09/07/2026): Substituído useQuery por useEffect + fetch manual
  // para eliminar erro "No QueryClient set" que impedia o carregamento de Partes
  // no RDA Workstation (Word). O useQuery dependia do QueryClientProvider ancestral;
  // quando o Error Boundary capturava o crash, FileHubArea nunca montava e o
  // POST /bridge/state nunca era enviado → Word recebia "Nenhuma parte recebida do Portal".
  const workspaceKey = String(activeWorkspace?.id || "").trim();
  const [publishedDocs, setPublishedDocs] = useState([]);
  const [publishedDocsLoading, setPublishedDocsLoading] = useState(false);
  const publishedDocsFetchedRef = useRef("");
  useEffect(() => {
    if (!workspaceKey) {
      setPublishedDocs([]);
      return;
    }
    if (publishedDocsFetchedRef.current === workspaceKey) return;
    publishedDocsFetchedRef.current = workspaceKey;
    let alive = true;
    setPublishedDocsLoading(true);
    fetchPublishedDocs(workspaceKey)
      .then((docs) => {
        if (alive) setPublishedDocs(Array.isArray(docs) ? docs : []);
      })
      .catch(() => {
        if (alive) setPublishedDocs([]);
      })
      .finally(() => {
        if (alive) setPublishedDocsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [workspaceKey]);

  // ─── Cabeçalho: extrai identidade do caderno ────────────────────────────
  const clienteNome = useMemo(() => {
    const s = String(
      activeWorkspace?.clientName || activeWorkspace?.clientNameSnapshot || "",
    ).trim();
    return s || "Cliente não identificado";
  }, [activeWorkspace]);

  // MC_Z2: lista de co-clientes/co-partes derivada de partiesRefs
  const coParties = useMemo(() => {
    const raw = Array.isArray(activeWorkspace?.partiesRefs)
      ? activeWorkspace.partiesRefs
      : [];
    return raw.filter((p) => p?.clientId || p?.name);
  }, [activeWorkspace?.partiesRefs]);

  // MC_Y9: label descritivo para o dropdown de cadernos (REGRA 02 do BLUEPINT.SH).
  const resolveCadernoLabel = (w) => {
    const alias = String(w?.alias || w?.name || "").trim();
    if (alias) return alias;
    const pid = String(w?.ancestry?.primary_process_id || "").trim();
    if (pid) return formatNPU(pid);
    // FIX RÓTULO PRÉ-PROCESSUAL (21/08/2026): o texto digitado no campo
    // "Pré-Processual" (memo interno, sem NPU) fica em
    // ancestry.pre_processual_note — antes esse valor nunca era lido aqui,
    // então o rótulo do caderno caía direto no fallback genérico
    // "Cliente (Pré-Processual)", mesmo quando o usuário tinha digitado um
    // nome real para o caderno. A pasta do Drive já usava esse texto
    // corretamente; o rótulo do Portal estava desalinhado da pasta real.
    const preProcNote = String(w?.ancestry?.pre_processual_note || "").trim();
    if (preProcNote) return preProcNote;
    const cn = String(w?.clientName || "").trim();
    const adversas = Array.isArray(w?.partes?.adversas)
      ? w.partes.adversas
      : [];
    const advName =
      adversas.length > 0 ? String(adversas[0]?.nome || "").trim() : "";
    if (cn && advName) return `${cn} vs. ${advName}`;
    if (cn) return `${cn} (Pré-Processual)`;
    return "Caderno sem nome";
  };

  // ─── Estado de acordeões (qual está aberto) ─────────────────────────────
  const [openAccordion, setOpenAccordion] = useState("infoGerais");
  const toggleAccordion = (id) =>
    setOpenAccordion((prev) => (prev === id ? null : id));

  // ─── MC41 (Fase F2): abas do processo/caderno — Resumo/Atividades/
  // Histórico/Caderno de Autos, com cabeçalho fixo acima delas. Volta pra
  // "Resumo" sempre que troca de workspace, pra não deixar o usuário preso
  // numa aba de um caderno anterior (mesmo motivo do bug do activePecaDoc).
  const CASE_TABS = [
    { id: "resumo", label: "Resumo" },
    { id: "atividades", label: "Atividades" },
    { id: "historico", label: "Histórico" },
    { id: "caderno_autos", label: "Caderno de Autos" },
    // Fase B (PRE_VOO_LIVRO_PROCESSUAL_GERADO_20260901.md) — aba própria,
    // não botão/modal (2ª versão, a pedido do titular 02/09/2026: "insere
    // uma nova aba, é muito mais simples" — mais simples de fato, sem
    // overlay/estado de abrir-fechar). Entre Caderno de Autos e Vínculos.
    { id: "caderno_trabalho", label: "Caderno de Trabalho" },
    { id: "vinculos", label: "Vínculos" },
  ];
  // MC (15/09/2026, pedido do titular — F5 dentro do Caderno de Autos
  // voltava pra tela anterior/inicial): `activeTab` nunca tocava a URL —
  // um F5 sempre remontava este componente do zero, e o valor inicial era
  // sempre "resumo" (nunca a aba real onde o titular estava). Inicializador
  // preguiçoso do useState lê `?tab=` UMA VEZ, só no primeiro mount de
  // verdade (F5/link direto) — troca de workspace SEM remount (usuário
  // clicando outro caderno na lista) continua indo pra "Resumo" sempre,
  // via o ref abaixo, sem olhar a URL de novo (regra já existente,
  // deliberada — MC41).
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const urlTab = searchParams.get("tab");
    return CASE_TABS.some((t) => t.id === urlTab) ? urlTab : "resumo";
  });
  // MC41j (Fase F2): ajuste durante a renderização, não useEffect — mesmo
  // padrão já usado acima para activePieceId (linha ~397-405), adotado ali
  // por causa da "regressão Aline" (useEffect só roda depois do commit,
  // causando piscada visível ao trocar de caderno). Achado ao auditar a
  // consistência árvore↔tela: o reset da aba tinha o mesmo risco.
  // MC (15/09/2026): ref inicializado com o id ATUAL (não `undefined`) —
  // com `undefined`, esta comparação também disparava no 1º render de
  // QUALQUER mount (F5 incluso), forçando "resumo" e descartando o `?tab=`
  // que o useState acima acabou de restaurar da URL. Só deve resetar em
  // trocas de workspace DEPOIS do mount, nunca na primeira renderização.
  const tabSyncWorkspaceIdRef = useRef(activeWorkspace?.id);
  if (tabSyncWorkspaceIdRef.current !== activeWorkspace?.id) {
    tabSyncWorkspaceIdRef.current = activeWorkspace?.id;
    setActiveTab("resumo");
  }

  // Espelha `activeTab` na URL (`?tab=...`) — REPLACE, não push: trocar de
  // aba não deve empilhar uma entrada de histórico por clique (Voltar
  // ficaria preso alternando abas em vez de sair do caderno). Só existe
  // pra o F5 e o link direto restaurarem a aba certa (useState acima).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (prev.get("tab") === activeTab) return prev;
        const next = new URLSearchParams(prev);
        next.set("tab", activeTab);
        return next;
      },
      { replace: true },
    );
  }, [activeTab, setSearchParams]);

  // Rola pro topo sempre que a aba troca — sem isso o scroll ficava onde
  // estava na aba anterior (ex.: no meio da lista de anexos do Caderno de
  // Autos), dando a impressão de que a página abriu "no meio" (pedido do
  // titular, 22/08/2026). Reseta os DOIS containers com overflow-y-auto
  // (<main> e o <div> interno do corpo da aba) — o Caderno de Autos
  // (FileHubArea) não tem scroll próprio nas seções normais (só no modo
  // Word), então dependendo do conteúdo quem realmente rola é o <main>
  // externo, não o <div> interno; resetar só um dos dois não bastava
  // (reportado pelo titular testando ao vivo).
  const tabScrollRef = useRef(null);
  const mainScrollRef = useRef(null);
  useEffect(() => {
    tabScrollRef.current?.scrollTo({ top: 0 });
    mainScrollRef.current?.scrollTo({ top: 0 });
    // No modo normal (não-Word), FileHubArea não tem h-full/flex-1 na raiz
    // (só cresce com o conteúdo) — então, dependendo da altura real da
    // árvore de layout, quem rola pode não ser nenhum dos dois <div> acima,
    // e sim a JANELA/documento inteiro. Reset defensivo também aqui —
    // barato, sem efeito colateral quando não é o caso.
    window.scrollTo({ top: 0 });
  }, [activeTab, activePieceId]);

  // ─── Vínculos entre processos (Fase F2b — MC52) ────────────────────────
  // 'principal' | 'apenso' | 'correlato' | null — qual ProcessPicker está
  // aberto. handleLinkProcessRelation chama o repository bidirecional
  // (MC50), não o handleSalvarVinculo antigo do acordeão do modal.
  const [linkPickerType, setLinkPickerType] = useState(null);
  const handleLinkProcessRelation = async (targetWorkspace) => {
    if (!activeWorkspace?.id || !targetWorkspace?.id || !linkPickerType) return;
    try {
      await linkProcessRelation(activeWorkspace.id, linkPickerType, targetWorkspace.id);
    } catch (error) {
      console.error(error);
      alert("Erro ao vincular processo: " + String(error?.message || error));
    }
  };

  // MC54 (Fase F2b): desvincular a partir do diagrama da aba Vínculos.
  const handleUnlinkProcessRelation = async (relationType, targetWorkspaceId) => {
    if (!activeWorkspace?.id || !targetWorkspaceId) return;
    try {
      await unlinkProcessRelation(activeWorkspace.id, relationType, targetWorkspaceId);
    } catch (error) {
      console.error(error);
      alert("Erro ao desvincular processo: " + String(error?.message || error));
    }
  };

  // ─── Feedback de salvamento ─────────────────────────────────────────────
  const [savingNotice, setSavingNotice] = useState(null);

  const {
    editingInfoGerais,
    setEditingInfoGerais,
    infoStatus,
    setInfoStatus,
    infoNumeroCNJ,
    setInfoNumeroCNJ,
    infoJuizo,
    setInfoJuizo,
    infoComarca,
    setInfoComarca,
    infoClasse,
    setInfoClasse,
    infoAssunto,
    setInfoAssunto,
    infoRamo,
    setInfoRamo,
    infoValorCausa,
    setInfoValorCausa,
    infoValorCondenacao,
    setInfoValorCondenacao,
    infoDataDistribuicao,
    setInfoDataDistribuicao,
    infoObjeto,
    setInfoObjeto,
    infoDataPrescricao,
    setInfoDataPrescricao,
    // Fase Processual (22/08/2026): ligado à UI agora — campo do TRIBUNAL
    // (Pré-Processual/Conhecimento/Recursal/Execução), distinto de
    // "Etapa do Caso" (infoCaseStage, controle interno do escritório,
    // ex. "Em Andamento"). Decisão anterior tinha ficado pendente; os dois
    // são conceitos diferentes, não duplicam.
    infoFaseProcessual,
    setInfoFaseProcessual,
    infoEfeitoSuspensivo,
    setInfoEfeitoSuspensivo,
    infoJulgado2Grau,
    setInfoJulgado2Grau,
    infoPenhoraNoRosto,
    setInfoPenhoraNoRosto,
    infoReuPreso,
    setInfoReuPreso,
    infoCustas,
    setInfoCustas,
    infoDataDaPrisao,
    setInfoDataDaPrisao,
    infoDataDoFato,
    setInfoDataDoFato,
    infoDelegacia,
    setInfoDelegacia,
    infoRai,
    setInfoRai,
    infoInqueritoNumeroSSP,
    setInfoInqueritoNumeroSSP,
  } = useCadernoInfoGerais(activeWorkspace);
  const [editingDadosCriminais, setEditingDadosCriminais] = useState(false);

  useEffect(() => {
    // MC61 (23/08/2026): useCadernoInfoGerais só lê activeWorkspace no
    // useState inicial (roda 1x, no primeiro mount do hook) — trocar de
    // caderno na barra lateral SEM remontar o componente deixava todo
    // campo que este efeito não resetasse "grudado" no valor do caderno
    // anterior. numeroCNJ e os 10 campos de Dados Criminais + Fase
    // Processual estavam de fora dessa lista (achado real: aba Vínculos
    // mostrando "Pré-Processual" corretamente enquanto a aba Resumo do
    // MESMO caderno mostrava o número do caderno visto antes) — a aba
    // Vínculos lê activeWorkspace.numeroCNJ direto, sem passar por este
    // estado, por isso não tinha esse bug.
    setInfoStatus(String(activeWorkspace?.status || "Ativo").trim());
    setInfoNumeroCNJ(String(activeWorkspace?.numeroCNJ || "").trim());
    setInfoJuizo(String(activeWorkspace?.juizo || "").trim());
    setInfoComarca(String(activeWorkspace?.comarca || "").trim());
    setInfoClasse(String(activeWorkspace?.metadata?.classe || "").trim());
    setInfoAssunto(String(activeWorkspace?.metadata?.assunto || "").trim());
    const raw = activeWorkspace?.metadata?.jurisdiction_branch;
    setInfoRamo(Array.isArray(raw) ? raw.join(", ") : String(raw || "").trim());
    const vc = activeWorkspace?.metadata?.valorCausa;
    setInfoValorCausa(vc === null || vc === undefined ? "" : String(vc));
    const vcond = activeWorkspace?.metadata?.valorCondenacao;
    setInfoValorCondenacao(vcond === null || vcond === undefined ? "" : String(vcond));
    setInfoDataDistribuicao(String(activeWorkspace?.metadata?.dataDistribuicao || "").trim());
    setInfoObjeto(String(activeWorkspace?.metadata?.objeto || "").trim());
    setInfoFaseProcessual(String(activeWorkspace?.metadata?.faseProcessual || "").trim());
    setInfoDataPrescricao(String(activeWorkspace?.metadata?.dataPrescricao || "").trim());
    const efeitoSuspensivo = activeWorkspace?.metadata?.efeitoSuspensivo;
    setInfoEfeitoSuspensivo(efeitoSuspensivo === null || efeitoSuspensivo === undefined ? null : Boolean(efeitoSuspensivo));
    const julgado2Grau = activeWorkspace?.metadata?.julgado2Grau;
    setInfoJulgado2Grau(julgado2Grau === null || julgado2Grau === undefined ? null : Boolean(julgado2Grau));
    const penhoraNoRosto = activeWorkspace?.metadata?.penhoraNoRosto;
    setInfoPenhoraNoRosto(penhoraNoRosto === null || penhoraNoRosto === undefined ? null : Boolean(penhoraNoRosto));
    const reuPreso = activeWorkspace?.metadata?.reuPreso;
    setInfoReuPreso(reuPreso === null || reuPreso === undefined ? null : Boolean(reuPreso));
    setInfoCustas(String(activeWorkspace?.metadata?.custas || "").trim());
    setInfoDataDaPrisao(String(activeWorkspace?.metadata?.dataDaPrisao || "").trim());
    setInfoDataDoFato(String(activeWorkspace?.metadata?.dataDoFato || "").trim());
    setInfoDelegacia(String(activeWorkspace?.metadata?.delegacia || "").trim());
    setInfoRai(String(activeWorkspace?.metadata?.rai || "").trim());
    setInfoInqueritoNumeroSSP(String(activeWorkspace?.metadata?.inqueritoNumeroSSP || "").trim());
  }, [activeWorkspace?.id]);

  // ─── Portal do Cliente (Acordeão Portal) ─────────────────────────────────
  const CASE_STAGE_OPTIONS = [
    { value: "", label: "— Não definido —" },
    { value: "pre_processual", label: "Pré-Processual" },
    { value: "peticao_inicial", label: "Petição Inicial" },
    { value: "em_andamento", label: "Em Andamento" },
    { value: "recurso", label: "Recurso" },
    { value: "encerrado", label: "Encerrado" },
  ];
  const [editingPortalStatus, setEditingPortalStatus] = useState(false);
  const [infoCaseStage, setInfoCaseStage] = useState(() =>
    String(activeWorkspace?.caseStage || "").trim(),
  );
  const [infoNextEventLabel, setInfoNextEventLabel] = useState(() =>
    String(activeWorkspace?.nextEventLabel || "").trim(),
  );
  const [infoNextEventLocation, setInfoNextEventLocation] = useState(() =>
    String(activeWorkspace?.nextEventLocation || "").trim(),
  );
  const [infoNextEventDate, setInfoNextEventDate] = useState(() => {
    const raw = activeWorkspace?.nextEventDate;
    if (!raw) return "";
    try {
      const d =
        typeof raw?.toDate === "function" ? raw.toDate() : new Date(raw);
      return d.toISOString().slice(0, 16);
    } catch {
      return "";
    }
  });

  useEffect(() => {
    setInfoCaseStage(String(activeWorkspace?.caseStage || "").trim());
    setInfoNextEventLabel(String(activeWorkspace?.nextEventLabel || "").trim());
    setInfoNextEventLocation(
      String(activeWorkspace?.nextEventLocation || "").trim(),
    );
    const raw = activeWorkspace?.nextEventDate;
    if (!raw) {
      setInfoNextEventDate("");
      return;
    }
    try {
      const d =
        typeof raw?.toDate === "function" ? raw.toDate() : new Date(raw);
      setInfoNextEventDate(d.toISOString().slice(0, 16));
    } catch {
      setInfoNextEventDate("");
    }
  }, [activeWorkspace?.id]);

  const handleSaveInfoGerais = async () => {
    if (!workspaceKey) return;
    try {
      const ramoArr = infoRamo
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await saveInfoGerais(workspaceKey, {
        numeroCNJ: infoNumeroCNJ.trim() || null,
        status: infoStatus || "Ativo",
        juizo: infoJuizo.trim() || null,
        comarca: infoComarca.trim() || null,
        metadata: {
          ...(activeWorkspace?.metadata || {}),
          classe: infoClasse.trim() || null,
          assunto: infoAssunto.trim() || null,
          jurisdiction_branch: ramoArr.length > 0 ? ramoArr : null,
          valorCausa: infoValorCausa.trim()
            ? Math.round(Number(infoValorCausa) * 100) / 100
            : null,
          valorCondenacao: infoValorCondenacao.trim()
            ? Math.round(Number(infoValorCondenacao) * 100) / 100
            : null,
          dataDistribuicao: infoDataDistribuicao.trim() || null,
          objeto: infoObjeto.trim() || null,
          dataPrescricao: infoDataPrescricao.trim() || null,
          faseProcessual: infoFaseProcessual.trim() || null,
        },
      });
      setEditingInfoGerais(false);
      setSavingNotice("Informações Gerais salvas.");
      setTimeout(() => setSavingNotice(null), 2500);
    } catch (e) {
      setSavingNotice("Erro ao salvar: " + String(e?.message || e));
    }
  };

  // Dados Criminais (22/08/2026) — mesmo repositório (saveInfoGerais faz
  // merge seguro em metadata), card de edição separado porque os campos só
  // fazem sentido pra casos penais (ramo Criminal), não pra qualquer caderno.
  const handleSaveDadosCriminais = async () => {
    if (!workspaceKey) return;
    try {
      await saveInfoGerais(workspaceKey, {
        juizo: infoJuizo.trim() || null,
        comarca: infoComarca.trim() || null,
        metadata: {
          ...(activeWorkspace?.metadata || {}),
          efeitoSuspensivo: infoEfeitoSuspensivo,
          julgado2Grau: infoJulgado2Grau,
          penhoraNoRosto: infoPenhoraNoRosto,
          reuPreso: infoReuPreso,
          custas: infoCustas.trim() || null,
          dataDaPrisao: infoDataDaPrisao.trim() || null,
          dataDoFato: infoDataDoFato.trim() || null,
          delegacia: infoDelegacia.trim() || null,
          rai: infoRai.trim() || null,
          inqueritoNumeroSSP: infoInqueritoNumeroSSP.trim() || null,
        },
      });
      setEditingDadosCriminais(false);
      setSavingNotice("Dados Criminais salvos.");
      setTimeout(() => setSavingNotice(null), 2500);
    } catch (e) {
      setSavingNotice("Erro ao salvar: " + String(e?.message || e));
    }
  };

  const handleSavePortalStatus = async () => {
    if (!workspaceKey) return;
    try {
      const nextTs = infoNextEventDate ? new Date(infoNextEventDate) : null;
      const prevStage = String(activeWorkspace?.caseStage || "").trim();
      const newStage = String(infoCaseStage || "").trim();
      const stageChanged = newStage && newStage !== prevStage;
      await savePortalStatus(workspaceKey, {
        caseStage: infoCaseStage || null,
        nextEventDate: nextTs || null,
        nextEventLabel: infoNextEventLabel.trim() || null,
        nextEventLocation: infoNextEventLocation.trim() || null,
        ...(stageChanged
          ? { npsForStage: newStage, npsCompletedForStage: null }
          : {}),
      });
      setEditingPortalStatus(false);
      setSavingNotice("Status do Portal salvo.");
      setTimeout(() => setSavingNotice(null), 2500);
    } catch (e) {
      setSavingNotice("Erro: " + String(e?.message || e));
    }
  };

  const {
    editingPartes,
    setEditingPartes,
    partesAdversas,
    setPartesAdversas,
    editingCoPartes,
    setEditingCoPartes,
    coPartesEdit,
    setCoPartesEdit,
    isCoPartePickerOpen,
    setIsCoPartePickerOpen,
  } = useCadernoPartes(activeWorkspace);

  useEffect(() => {
    const raw = activeWorkspace?.partes?.adversas;
    setPartesAdversas(Array.isArray(raw) ? raw : []);
  }, [activeWorkspace?.id]);

  const handleAddAdversa = () =>
    setPartesAdversas((p) => [
      ...p,
      { nome: "", tipo: "PF", documento: "", qualificacao: {} },
    ]);
  const handleRemoveAdversa = (idx) =>
    setPartesAdversas((p) => p.filter((_, i) => i !== idx));
  const handleChangeAdversa = (idx, field, value) => {
    setPartesAdversas((p) =>
      p.map((row, i) => {
        if (i !== idx) return row;
        if (field.startsWith("qualificacao.")) {
          const qKey = field.replace("qualificacao.", "");
          return {
            ...row,
            qualificacao: { ...(row.qualificacao || {}), [qKey]: value },
          };
        }
        return { ...row, [field]: value };
      }),
    );
  };

  // ─── Adaptador: converte qualificacao snake_case (formato legado do Caderno)
  // para o formato canônico esperado por generateQualificationText (ClientSchema.js)
  const adaptQualificacaoToCanonical = (r) => {
    const q = r?.qualificacao || {};
    const tipo = String(r?.tipo || "PF")
      .trim()
      .toUpperCase();
    return {
      type: tipo === "PJ" ? "Pessoa Jurídica" : "Pessoa Física",
      grammaticalGender: String(r?.genero || r?.gender || "M")
        .trim()
        .toUpperCase(),
      name: String(r?.nome || "").trim(),
      qualification: {
        physical: {
          name: String(r?.nome || "").trim(),
          nationality: String(q?.nacionalidade || "").trim(),
          civilStatus: String(q?.estado_civil || "").trim(),
          profession: String(q?.profissao || "").trim(),
          rg: String(q?.rg || "").trim(),
          orgaoEmissor: String(q?.orgao_expedidor || "").trim(),
          cpf: String(q?.cpf || r?.cpf || r?.documento || "")
            .replace(/\D/g, "")
            .trim(),
          hasProfessionalRegister: Boolean(
            q?.registerType && q?.registerNumber,
          ),
          registerType: String(
            q?.registerType || r?.physical?.registerType || "",
          ).trim(),
          registerUf: String(
            q?.registerUF || r?.physical?.registerUF || "",
          ).trim(),
          professionalRegister: String(
            q?.registerNumber || r?.physical?.registerNumber || "",
          ).trim(),
          hasStableUnion: Boolean(q?.hasStableUnion),
        },
      },
      location: {
        address: {
          street: String(q?.endereco || "").trim(),
          neighborhood: String(q?.bairro || "").trim(),
          city: String(q?.cidade || "").trim(),
          state: String(q?.estado || "").trim(),
          zipCode: String(q?.cep || "").trim(),
        },
      },
      contact: {
        email: String(q?.email || "").trim(),
        phone: String(q?.telefone || "").trim(),
        phonePrimary: String(q?.telefone || "").trim(),
      },
    };
  };

  // buildQualificacaoText — DELEGA ao motor canônico (Padrão Ouro definido em 21/07/2026)
  // Ordem dos 13 campos: NOME → NAC → CIVIL → PROF → CONSELHO → CPF → RG →
  // NATURALIDADE → DATA NASC → IDADE → DOMICÍLIO → E-MAIL → TEL
  const buildQualificacaoText = (r) => {
    if (!r) return "";
    const canonical = adaptQualificacaoToCanonical(r);
    return generateQualificationText(canonical);
  };

  const handleSavePartes = async () => {
    if (!workspaceKey) return;
    try {
      const clean = partesAdversas
        .map((r) => ({
          nome: String(r?.nome || "").trim(),
          tipo: String(r?.tipo || "PF").trim() || "PF",
          documento: String(r?.documento || "").trim() || null,
          qualificacao: r?.qualificacao || {},
          // Polo processual (padrão PJe/CNJ, 22/08/2026) — estar em
          // "Outros Envolvidos" (não ser nosso cliente) não implica polo
          // nenhum específico; ex.: Ministério Público como Polo Ativo/
          // Autoridade investigante num Inquérito Policial. Fallback
          // inteligente (24/08/2026): infere "Auxiliar da Justiça" se o
          // papel bater com Art. 149 CPC — ver `inferPoloFallback`.
          polo:
            String(r?.polo || "").trim() ||
            inferPoloFallback(r?.qualificacao?.posicao),
        }))
        .filter((r) => r.nome);
      await savePartes(workspaceKey, {
        ...(activeWorkspace?.partes || {}),
        adversas: clean,
      });
      setPartesAdversas(clean);
      setEditingPartes(false);
      setSavingNotice("Partes atualizadas.");
      setTimeout(() => setSavingNotice(null), 2500);
      // Envia array completo de partes ao bridge (cliente + adversas)
      // Busca qualificação do Autor com fallback de 3 níveis
      const obterQualificacaoCanonical = async (selMeta, workspace) => {
        const { cpf, cnpj } = selMeta || {};
        let documentoIdentificador = cpf || cnpj || "";

        // Nível 2: activeWorkspace
        if (!documentoIdentificador && workspace) {
          documentoIdentificador =
            workspace.clientCpf ||
            workspace.clientCnpj ||
            workspace.metadata?.cpf ||
            workspace.metadata?.cnpj ||
            "";
        }

        // Nível 3: fetchClientDoc do CRM
        if (!documentoIdentificador) {
          const wsKey = workspace?.id || workspace?.workspaceKey;
          const clientId = workspace?.clientId;
          if (wsKey && clientId) {
            try {
              const clientData = await fetchClientDoc(appId, clientId);
              // FIX QUALIFICACAO (21/07/2026): O CPF/CNPJ no Firestore esta em
              // qualification.physical.cpf (PF) / qualification.legal.cnpj (PJ),
              // e NAO em clientData.cpf (top-level). Usar caminho canonico.
              documentoIdentificador =
                clientData?.qualification?.physical?.cpf ||
                clientData?.qualification?.legal?.cnpj ||
                clientData?.cpf ||
                clientData?.cnpj ||
                "";
            } catch (e) {
              console.error("[Qualificação] Erro fetchClientDoc:", e);
            }
          }
        }

        // Normalização CPF/CNPJ por comprimento
        const apenasNumeros = String(documentoIdentificador).replace(/\D/g, "");
        if (!apenasNumeros) return { sufixo: "", doc: "" };

        const isCnpj = apenasNumeros.length === 14;
        const isCpf = apenasNumeros.length === 11;
        const rotulo = isCnpj ? "CNPJ" : isCpf ? "CPF" : "CPF";
        return {
          sufixo: `, ${rotulo} ${documentoIdentificador}`,
          doc: documentoIdentificador,
        };
      };

      const { sufixo: sufixoAutor, doc: docAutor } =
        await obterQualificacaoCanonical(selectedClientMeta, activeWorkspace);
      // FIX CAUSA RAIZ (20/07/2026): usar selectedClientName em vez de activeWorkspace.clientName
      // que pode estar desatualizado. selectedClientName e atualizado pelo onSnapshot do CRM.
      const nomeCliente = String(
        selectedClientName || activeWorkspace?.clientName || "Autor",
      ).trim();
      const nomeAutor = nomeCliente || "Autor";
      const clienteParte = nomeCliente
        ? {
            clientId: String(activeWorkspace?.clientId || "").trim(),
            id: String(activeWorkspace?.clientId || "").trim(),
            role: "Autor",
            name: nomeAutor,
            resp: "",
            isOfficeClient: true,
            text: `${nomeAutor}${sufixoAutor}`,
            ooxml: `<p><b>${nomeAutor}</b>${sufixoAutor}</p>`,
            document_cpf: null,
            document_cnpj: null,
            email: selectedClientMeta?.email || null,
          }
        : null;
      // Popula campos de documento para fallback do overlay no Word
      if (clienteParte && docAutor) {
        const nums = docAutor.replace(/\D/g, "");
        if (nums.length === 11) clienteParte.document_cpf = docAutor;
        if (nums.length === 14) clienteParte.document_cnpj = docAutor;
      }
      const adversasBridge = clean.map((r) => {
        const qualifText = buildQualificacaoText(r);
        const docStr = r?.documento
          ? r.tipo === "PJ"
            ? `CNPJ ${r.documento}`
            : `CPF ${r.documento}`
          : "";
        return {
          name: String(r?.nome || "").trim(),
          role: "Réu",
          tipo: String(r?.tipo || "PF").trim() || "PF",
          documento: String(r?.documento || "").trim() || null,
          isOfficeClient: false,
          text: qualifText
            ? `${r.nome}, ${qualifText}`
            : docStr
              ? `${r.nome}, ${docStr}`
              : `${r.nome}`,
          ooxml: qualifText
            ? `<p><b>${r.nome}</b>, ${qualifText}</p>`
            : docStr
              ? `<p><b>${r.nome}</b>, ${docStr}</p>`
              : `<p><b>${r.nome}</b></p>`,
        };
      });
      const todasPartes = clienteParte
        ? [clienteParte, ...adversasBridge]
        : adversasBridge;
      if (hubBridge?.postBridgeState) {
        hubBridge.postBridgeState({ parties: todasPartes });
      }
    } catch (e) {
      setSavingNotice("Erro ao salvar partes: " + String(e?.message || e));
    }
  };

  // ═══ FIX BRIDGE PARTES (20/07/2026): useEffect automatico para publicar partes na bridge ═══
  // As provas (anexos) sincronizam corretamente porque useFileHub.js tem useEffect watcher
  // que chama postBridgeState() automaticamente. Este efeito faz o mesmo para partes,
  // eliminando a dependencia do clique manual em "Salvar Partes".
  useEffect(() => {
    if (!workspaceKey) return;
    if (!hubBridge?.postBridgeState) return;

    // Cliente principal (Autor)
    const nomeCliente = String(
      selectedClientName || activeWorkspace?.clientName || "",
    ).trim();
    const idCliente = String(
      selectedClientId || activeWorkspace?.clientId || "",
    ).trim();
    const clienteParte =
      idCliente && nomeCliente
        ? {
            id: idCliente,
            role: "Cliente",
            name: nomeCliente,
            text: "",
            ooxml: "",
          }
        : null;

    // Partes adversas
    const adversasBridge = (Array.isArray(partesAdversas) ? partesAdversas : [])
      .filter(function (r) {
        return r && r.nome && String(r.nome).trim();
      })
      .map(function (r) {
        return {
          id: String(r.documento || r.nome || "").trim(),
          role: "Réu",
          name: String(r.nome || "")
            .trim()
            .toUpperCase(),
          text: buildQualificacaoText(r),
          ooxml: "",
        };
      });

    // Co-partes
    const coPartesBridge = (Array.isArray(coPartesEdit) ? coPartesEdit : [])
      .filter(function (r) {
        return r && (r.clientId || r.name);
      })
      .map(function (r) {
        return {
          id: String(r.clientId || r.name || "").trim(),
          role: r.role || "Co-Parte",
          name: String(r.name || "")
            .trim()
            .toUpperCase(),
          text: "",
          ooxml: "",
        };
      });

    var todasPartes = [];
    if (clienteParte) todasPartes.push(clienteParte);
    todasPartes = todasPartes.concat(adversasBridge).concat(coPartesBridge);

    if (todasPartes.length > 0) {
      hubBridge.postBridgeState({ parties: todasPartes });
    }
  }, [
    workspaceKey,
    partesAdversas,
    coPartesEdit,
    selectedClientId,
    selectedClientName,
    selectedClientMeta,
    hubBridge?.postBridgeState,
  ]);

  useEffect(() => {
    const raw = Array.isArray(activeWorkspace?.partiesRefs)
      ? activeWorkspace.partiesRefs
      : [];
    setCoPartesEdit(raw.filter((p) => p?.clientId || p?.name));
  }, [activeWorkspace?.id]);
  const handleAddCoParteRow = () =>
    setCoPartesEdit((p) => [
      ...p,
      { name: "", role: "Parte", clientId: "", resp: "" },
    ]);
  const handleAddCoParteFromCrm = (client) => {
    if (!client) return;
    const cid = String(client?.id || "").trim();
    if (coPartesEdit.some((p) => String(p?.clientId || "") === cid)) {
      setIsCoPartePickerOpen(false);
      return;
    }
    const nt = String(client?.type || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    const isAnimal = nt.includes("animal");
    const resolvedName = String(
      isAnimal
        ? client?.qualification?.animal?.name || client?.name || "Animal"
        : client?.qualification?.physical?.socialName ||
            client?.qualification?.physical?.name ||
            client?.qualification?.legal?.companyName ||
            client?.name ||
            "Cliente",
    ).trim();
    const resp = String(
      client?.qualification?.animal?.responsible?.name ||
        client?.qualification?.animal?.guardian?.name ||
        client?.qualification?.legal?.representative?.name ||
        client?.qualification?.minor?.representative?.name ||
        "",
    ).trim();
    setCoPartesEdit((prev) => [
      ...prev,
      {
        name: resolvedName,
        role: isAnimal ? "Animal" : "Autor",
        clientId: cid,
        resp: resp || null,
      },
    ]);
    setIsCoPartePickerOpen(false);
  };
  const handleRemoveCoParteRow = (idx) =>
    setCoPartesEdit((p) => p.filter((_, i) => i !== idx));
  const handleChangeCoParteRow = (idx, field, value) => {
    setCoPartesEdit((p) =>
      p.map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    );
  };
  const handleSaveCoPartes = async () => {
    if (!workspaceKey) return;
    try {
      const clean = coPartesEdit
        .map((r) => ({
          name: String(r?.name || "").trim(),
          role: String(r?.role || "").trim() || "Parte",
          clientId: String(r?.clientId || "").trim() || null,
          resp: String(r?.resp || "").trim() || null,
          // Polo processual (padrão PJe/CNJ, 22/08/2026) — "nosso
          // cliente" (estar aqui) não implica polo nenhum específico; ex.:
          // fiel depositário fica em "Auxiliar da Justiça" (Art. 149
          // CPC), não é litigante de Polo Ativo nem Passivo. Fallback
          // inteligente (24/08/2026) — ver `inferPoloFallback`.
          polo: String(r?.polo || "").trim() || inferPoloFallback(r?.role),
        }))
        .filter((r) => r.name);
      const partiesRefIds = clean.map((r) => r.clientId).filter(Boolean);
      await saveCoPartes(workspaceKey, clean, partiesRefIds);
      setCoPartesEdit(clean);
      setEditingCoPartes(false);
      setSavingNotice("Co-partes atualizadas.");
      setTimeout(() => setSavingNotice(null), 2500);
    } catch (e) {
      setSavingNotice("Erro: " + String(e?.message || e));
    }
  };

  // MC_PARTES_B (24/08/2026): marcar/desmarcar como "nosso cliente" direto
  // a partir de uma linha de partes.adversas (dado real do Projudi), sem
  // abrir o formulário de edição — grava em partiesRefs (mesmo campo/rota
  // de sempre, via saveCoPartes), como uma entrada "manual" (clientId
  // vazio), igual ao botão "+ Manual" já existente. Casamento por nome
  // normalizado (sem acento/caixa) porque a entrada manual não tem
  // clientId para comparar.
  const handleToggleAdversaCliente = async (adversaRow) => {
    if (!workspaceKey) return;
    const nome = String(adversaRow?.nome || "").trim();
    if (!nome) return;
    const nomeNorm = normalizeSearchText(nome);
    const currentRefs = Array.isArray(activeWorkspace?.partiesRefs)
      ? activeWorkspace.partiesRefs
      : [];
    const jaMarcado = currentRefs.some(
      (p) => normalizeSearchText(p?.name || p?.nome || "") === nomeNorm,
    );
    const nextRefs = jaMarcado
      ? currentRefs.filter(
          (p) => normalizeSearchText(p?.name || p?.nome || "") !== nomeNorm,
        )
      : [
          ...currentRefs,
          {
            name: nome,
            role: adversaRow?.qualificacao?.posicao || "Parte",
            clientId: "",
            resp: null,
            polo:
              adversaRow?.polo ||
              inferPoloFallback(adversaRow?.qualificacao?.posicao),
          },
        ];
    const partiesRefIds = nextRefs.map((r) => r?.clientId).filter(Boolean);
    try {
      await saveCoPartes(workspaceKey, nextRefs, partiesRefIds);
      setCoPartesEdit(nextRefs.filter((p) => p?.clientId || p?.name));
      setSavingNotice(
        jaMarcado
          ? `${nome} removido(a) da nossa classificação de cliente.`
          : `${nome} marcado(a) como nosso(a) cliente/representado(a).`,
      );
      setTimeout(() => setSavingNotice(null), 2500);
    } catch (e) {
      setSavingNotice("Erro: " + String(e?.message || e));
    }
  };

  const ROLE_LABELS = {
    LEAD: "Principal",
    ASSOCIATE: "Associado",
    COCOUNSEL: "Correspondente",
    PARALEGAL: "Paralegal",
  };
  const {
    editingCaseTeam,
    setEditingCaseTeam,
    caseTeamEdit,
    setCaseTeamEdit,
    isCaseTeamPickerOpen,
    setIsCaseTeamPickerOpen,
  } = useCadernoCaseTeam(activeWorkspace);

  useEffect(() => {
    const raw = Array.isArray(activeWorkspace?.caseTeam)
      ? activeWorkspace.caseTeam
      : [];
    setCaseTeamEdit(
      raw.length > 0
        ? raw
        : [{ ...TITULAR_SEED, addedAt: new Date().toISOString() }],
    );
  }, [activeWorkspace?.id]);

  const handleAddCaseTeamFromCrm = (client) => {
    if (!client) return;
    const cid = String(client?.id || "").trim();
    if (caseTeamEdit.some((m) => String(m?.clientId || "") === cid)) {
      setIsCaseTeamPickerOpen(false);
      return;
    }
    const q = client?.qualification?.physical || {};
    const oab = String(
      q?.professionalRegister || client?.qualification?.lawyer?.oab || "",
    ).trim();
    setCaseTeamEdit((prev) => [
      ...prev,
      {
        clientId: cid,
        name: String(q?.socialName || q?.name || client?.name || "").trim(),
        oab: oab || null,
        role: CASE_TEAM_ROLES.ASSOCIATE,
        phone: String(client?.contact?.phone || "").trim() || null,
        whatsappLink: client?.contact?.phone
          ? `https://wa.me/55${String(client.contact.phone).replace(/\D/g, "")}`
          : null,
        email: String(client?.contact?.email || "").trim() || null,
        photoURL: String(client?.contact?.photoURL || "").trim() || null,
        addedAt: new Date().toISOString(),
      },
    ]);
    setIsCaseTeamPickerOpen(false);
  };

  const handleRemoveCaseTeamRow = (idx) =>
    setCaseTeamEdit((p) => p.filter((_, i) => i !== idx));
  const handleChangeCaseTeamRole = (idx, role) => {
    setCaseTeamEdit((p) =>
      p.map((row, i) => (i === idx ? { ...row, role } : row)),
    );
  };

  const handleSaveCaseTeam = async () => {
    if (!workspaceKey) return;
    try {
      const clean = caseTeamEdit.filter((r) => r?.clientId && r?.name);
      const ids = clean.map((r) => r.clientId);
      await saveCaseTeam(workspaceKey, clean, ids);
      setCaseTeamEdit(clean);
      setEditingCaseTeam(false);
      setSavingNotice("Equipe do caso atualizada.");
      setTimeout(() => setSavingNotice(null), 2500);
    } catch (e) {
      setSavingNotice("Erro: " + String(e?.message || e));
    }
  };

  // ── Matter-Centric Fase 2: migrar Drive do caderno para pasta neutra ────
  const [migratingDrive, setMigratingDrive] = useState(false);
  const isMatterCentric = Boolean(activeWorkspace?.matterDriveFolderId);

  const handleMigrateToMatterDrive = async () => {
    if (!workspaceKey || !activeWorkspace) return;
    const pid = String(
      activeWorkspace?.ancestry?.primary_process_id || "",
    ).trim();
    const alias = String(
      activeWorkspace?.alias || activeWorkspace?.name || "",
    ).trim();
    const matterName =
      pid || alias || String(activeWorkspace?.clientName || "Processo");
    const baseUrl = getBackendBaseUrl();
    if (!baseUrl) {
      setSavingNotice("Backend não configurado.");
      return;
    }
    setMigratingDrive(true);
    try {
      const resp = await fetch(`${baseUrl}/drive/create-matter-folder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ matterName, matterAlias: matterName }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok)
        throw new Error(
          String(data?.detail || "Falha ao criar pasta de processo"),
        );
      const matterFolderId = String(data?.matterFolderId || "").trim();
      const matterFolders = data?.matterFolders || {};
      if (!matterFolderId)
        throw new Error("Backend não retornou o ID da pasta do processo.");
      await migrateDriveToMatterFolder(workspaceKey, {
        matterDriveFolderId: matterFolderId,
        matterDriveFolders: matterFolders,
        matterDriveFolderUrl:
          String(data?.matterFolderUrl || "").trim() || null,
        processRootFolderId: String(matterFolders?.pecas || matterFolderId),
      });
      setSavingNotice("Drive migrado para pasta de processo!");
      setTimeout(() => setSavingNotice(null), 3000);
    } catch (e) {
      setSavingNotice("Erro: " + String(e?.message || e).slice(0, 100));
    }
    setMigratingDrive(false);
  };

  const {
    editingSigilo,
    setEditingSigilo,
    nivelSigilo,
    setNivelSigilo,
    prioridadeAtiva,
    setPrioridadeAtiva,
    prioridadeMotivo,
    setPrioridadeMotivo,
  } = useCadernoSigilo(activeWorkspace);

  useEffect(() => {
    setNivelSigilo(
      String(activeWorkspace?.nivelSigilo || "PUBLICO").toUpperCase(),
    );
    setPrioridadeAtiva(Boolean(activeWorkspace?.prioridade?.ativa));
    setPrioridadeMotivo(
      String(activeWorkspace?.prioridade?.motivo || "").trim(),
    );
  }, [activeWorkspace?.id]);

  const sigiloAlerta = nivelSigilo !== "PUBLICO" || prioridadeAtiva;

  const handleSaveSigilo = async () => {
    if (!workspaceKey) return;
    try {
      await saveSigilo(workspaceKey, nivelSigilo || "PUBLICO", {
        ativa: Boolean(prioridadeAtiva),
        motivo: prioridadeAtiva ? prioridadeMotivo.trim() || null : null,
      });
      setEditingSigilo(false);
      setSavingNotice("Sigilo/Prioridade salvos.");
      setTimeout(() => setSavingNotice(null), 2500);
    } catch (e) {
      setSavingNotice("Erro ao salvar sigilo: " + String(e?.message || e));
    }
  };

  // ─── Prazos / Audiências / Pendências (Acordeão 4 — placeholder Fase 2) ─
  const prazosList = useMemo(() => {
    const raw = activeWorkspace?.prazos;
    return Array.isArray(raw) ? raw : [];
  }, [activeWorkspace?.id]);

  const audienciasList = useMemo(() => {
    const raw = activeWorkspace?.audiencias;
    return Array.isArray(raw) ? raw : [];
  }, [activeWorkspace?.id]);

  const prazoCriticoProximo = useMemo(() => {
    const agora = Date.now();
    const limite = agora + 5 * 24 * 60 * 60 * 1000; // 5 dias
    return prazosList.some((p) => {
      const ts = p?.data?.toDate ? p.data.toDate().getTime() : p?.dataMs || 0;
      return ts > 0 && ts <= limite;
    });
  }, [prazosList]);

  // ─── Prazos / Audiências: entrada manual (fusão — MC21) ──────────────────
  // Sai do placeholder "(Fase 2 — DataJud)". dataMs alimenta o mesmo cálculo
  // de prazoCriticoProximo que já existia (linha 1606) — nenhuma mudança lá.
  const [novoPrazoForm, setNovoPrazoForm] = useState({ modo: "PRAZO", tipo: "", descricao: "", data: "" });
  const [savingPrazo, setSavingPrazo] = useState(false);

  const handleSalvarPrazo = async () => {
    if (!activeWorkspace?.id || !novoPrazoForm.descricao.trim() || !novoPrazoForm.data) return;
    setSavingPrazo(true);
    try {
      const dataMs = new Date(`${novoPrazoForm.data}T12:00:00`).getTime();
      const novoItem = {
        tipo: novoPrazoForm.tipo.trim() || (novoPrazoForm.modo === "AUDIENCIA" ? "Audiência" : "Prazo"),
        descricao: novoPrazoForm.descricao.trim(),
        dataMs,
        origem: "MANUAL",
      };
      const nextPrazos = novoPrazoForm.modo === "PRAZO" ? [...prazosList, novoItem] : prazosList;
      const nextAudiencias = novoPrazoForm.modo === "AUDIENCIA" ? [...audienciasList, novoItem] : audienciasList;
      await savePrazosAudiencias(activeWorkspace.id, { prazos: nextPrazos, audiencias: nextAudiencias });
      setNovoPrazoForm({ modo: novoPrazoForm.modo, tipo: "", descricao: "", data: "" });
    } catch (error) {
      console.error(error);
      alert("Erro ao salvar: " + String(error?.message || error));
    } finally {
      setSavingPrazo(false);
    }
  };

  // ─── Vínculos / Apensamentos (fusão — MC22) ───────────────────────────────
  // Descontinua a leitura do array genérico activeWorkspace.vinculos
  // ({numero,descricao}) — reaponta para os 3 campos do MC02
  // (processoPrincipalId/apensos/processosCorrelatos), que distinguem
  // relação formal (apenso, estilo Projudi) de correlação factual/
  // estratégica. Array antigo não é mais lido nesta tela.
  const processoPrincipalId = activeWorkspace?.processoPrincipalId || null;
  const apensosList = useMemo(() => {
    const raw = activeWorkspace?.apensos;
    return Array.isArray(raw) ? raw : [];
  }, [activeWorkspace?.id]);
  const correlatosList = useMemo(() => {
    const raw = activeWorkspace?.processosCorrelatos;
    return Array.isArray(raw) ? raw : [];
  }, [activeWorkspace?.id]);

  // MC43 (Fase F2): findWorkspaceByCNJ/handleAbrirVinculo/novoVinculoForm/
  // handleSalvarVinculo (o formulário antigo de vínculo, por CNJ) removidos
  // daqui — ficaram incompatíveis com o schema novo do MC50 (os 3 campos
  // agora guardam ID de workspace, não CNJ) e foram substituídos de vez
  // pela Fase F2b (ProcessPicker + linkProcessRelation + aba Vínculos).
  // apensosList/correlatosList/processoPrincipalId continuam — a aba
  // Vínculos (MC53) os usa.

  // ─── Histórico de movimentações (fusão — MC24a) ───────────────────────────
  // Acordeão "Histórico" — versão compacta. Só contador + resumo do item mais
  // recente; a lista completa vive num painel expandido à parte (MC24b),
  // porque processos reais já têm 80-95 movimentações — não cabem no espaço
  // padrão de um acordeão (ressalva de design do titular).
  const [movementsList, setMovementsList] = useState([]);
  const [showMovementForm, setShowMovementForm] = useState(false);
  // MC44 (Fase F2, redesenho tabela 22/08/2026): busca livre cobre parte,
  // usuário e teor da decisão via texto — em vez de chips de taxonomia fixa
  // (a taxonomia real do tribunal, ex. "Intimação Expedida", varia por
  // tribunal e não cabe num enum fechado nosso). selectedMovement alimenta
  // o flyout ao clicar numa linha (MC47 dobrado aqui).
  const [historicoSearch, setHistoricoSearch] = useState('');
  const [selectedMovement, setSelectedMovement] = useState(null);

  // ─── Camada caderno_notas (Constituição Processos ART. 20) — RETIRADA ──────
  // ❌ APOSENTADA em 02/09/2026 (Fase 1 do
  // PRE_VOO_LIVRO_PROCESSUAL_GERADO_20260901.md), sucedendo a fase de só
  // "ocultar" de 01/09/2026. O modo "Linha do Tempo Completa" (Nova Nota,
  // Exportar/Imprimir, notasList/notaForm/historicoModo, CadernoNotaForm.jsx,
  // CadernoNotaTimelineItem.jsx, cadernoTimelineExport.js) foi removido por
  // inteiro — a Decisão "e" daquele Pré-Voo já tinha fechado que não haveria
  // tela de leitura de capítulo no Portal, só um link de saída pro
  // CADERNO_<...>.md (Fase 5). A única peça reaproveitável (mesclarLinhaDoTempo)
  // foi extraída para timelineMerge.js antes de apagar o resto — a Fase 2
  // (gerarLivroProcessual) importa de lá.

  const filteredMovementsList = useMemo(() => {
    const term = normalizeSearchText(historicoSearch);
    if (!term) return movementsList;
    return movementsList.filter((m) => {
      const haystack = normalizeSearchText(
        [m.descricaoOficial, m.usuario, m.tipoMovimento, m.numeroMovimento].filter(Boolean).join(' ')
      );
      return haystack.includes(term);
    });
  }, [movementsList, historicoSearch]);

  useEffect(() => {
    if (!activeWorkspace?.id) {
      setMovementsList([]);
      return;
    }
    const unsubMov = subscribeMovements(activeWorkspace.id, setMovementsList);
    return () => {
      unsubMov?.();
    };
  }, [activeWorkspace?.id]);

  // MC46 (Fase F2): efeito "Fechar modal de metadados com Escape" (DD-02)
  // removido — showMetadataModal não existe mais.

  // ─── Auto-expansão condicional (Pesquisa 02 §5.3) ───────────────────────
  useEffect(() => {
    // Prioridade: alertas vermelhos abrem primeiro
    if (sigiloAlerta) {
      setOpenAccordion("sigilo");
      return;
    }
    if (prazoCriticoProximo) {
      setOpenAccordion("prazos");
      return;
    }
    // Default: Info Gerais aberto
    setOpenAccordion("infoGerais");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspace?.id, sigiloAlerta, prazoCriticoProximo]);

  // ─── Componente Accordion reutilizável ──────────────────────────────────
  const Accordion = ({ id, title, summary, alert, children }) => {
    const isOpen = openAccordion === id;
    return (
      <div
        className={`bg-white border rounded-md shadow-sm overflow-hidden ${alert ? "border-red-400" : "border-gray-300"}`}
      >
        <button
          type="button"
          onClick={() => toggleAccordion(id)}
          className={`w-full px-4 py-2.5 flex items-center justify-between hover:opacity-95 transition-opacity ${alert ? "bg-red-600" : "bg-[#20243B]"}`}
          aria-expanded={isOpen}
          aria-controls={`acc-${id}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="text-[11px] font-bold text-[#C5A059] uppercase tracking-widest shrink-0">
              {title}
            </h3>
            {summary ? (
              <span className="text-[10px] text-gray-300 truncate">
                {summary}
              </span>
            ) : null}
          </div>
          <span
            className={`text-gray-300 text-[10px] transition-transform ${isOpen ? "rotate-180" : ""}`}
          >
            ▼
          </span>
        </button>
        {isOpen ? (
          <div id={`acc-${id}`} className="p-4">
            {children}
          </div>
        ) : null}
      </div>
    );
  };

  // MC42b (Fase F2): cartao sempre visivel, sem colapsar - usado nas 6
  // secoes que viraram a aba Resumo. Pesquisa de UX (Baymard) + as duas
  // referencias reais que o titular trouxe (Projudi, Astrea) confirmam:
  // pra poucas categorias com pouco dado cada (nosso caso, 6 secoes), caixas
  // sempre abertas leem melhor que acordeao - acordeao existia so por causa
  // do espaco apertado do modal antigo, que nao existe mais numa aba de
  // tela cheia. Mesmo estilo visual do card do Cadastro de Cliente
  // (ClientForm/ConfigSection): borda 2px, cantos arredondados, sombra sutil.
  const SummaryCard = ({ icon, title, alert, onEdit, children }) => (
    <div
      className={`bg-white border rounded-xl shadow-sm overflow-hidden ${alert ? "border-red-300" : "border-gray-300"}`}
    >
      {/* MC42c (Fase F2): faixa escura removida — competia visualmente com
          o cabeçalho escuro da página (título do processo). Cabeçalho do
          cartão agora é claro, só uma linha divisória embaixo, achado
          testando a tela real.
          MC42d: cabeçalho claro demais na primeira versão (gray-50 quase
          branco) — subiu um degrau de peso (slate-100/gray-300), pra não
          voltar à faixa escura mas também não sumir visualmente.
          MC42h: removido o "summary" (prévia ao lado do título, ex.:
          "Goiânia") — poluía mais do que ajudava (achado do titular). O
          cabeçalho só tem o nome da seção; "Editar" virou botão no canto
          superior direito do próprio cabeçalho (economiza espaço vertical,
          não fica mais solto embaixo do conteúdo). */}
      <div
        className={`px-4 py-2 border-b flex items-center gap-2 ${alert ? "bg-red-100 border-red-300" : "bg-slate-100 border-gray-300"}`}
      >
        {icon ? <i className={`fas ${icon} text-[#C5A059] text-xs shrink-0`}></i> : null}
        <h3 className="text-xs font-bold text-[#20243B] uppercase tracking-wide">
          {title}
        </h3>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="ml-auto text-[10px] font-bold uppercase tracking-widest text-[#C5A059] hover:text-[#20243B] transition-colors shrink-0"
          >
            ✎ Editar
          </button>
        ) : null}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );

  // ─── Classificação das peças publicadas (Gênero → Espécie) ───────────────
  const classifyPeca = (doc) => {
    const cat = String(
      doc?.classification?.category || doc?.category || doc?.type || "",
    ).toUpperCase();
    const title = String(doc?.title || doc?.name || "").toUpperCase();
    if (cat.includes("CONTRATO") || title.includes("CONTRATO"))
      return "CONTRATO";
    if (cat.includes("PROCURA") || title.includes("PROCURA"))
      return "PROCURACAO";
    if (cat.includes("DECLARA") || title.includes("DECLARA"))
      return "DECLARACAO";
    if (cat.includes("RENUNCIA") || title.includes("RENUNCIA"))
      return "TERMO_RENUNCIA";
    if (cat.includes("REVOGA") || title.includes("REVOGA"))
      return "TERMO_REVOGACAO";
    return "PETICAO";
  };

  const { contratoDocs, procuracaoDocs, declaracaoDocs } = useMemo(() => {
    const _bSlotNames = hubBridge?.slotNames || {};
    const _bAssignments = hubBridge?.assignments || {};

    // Deriva promotedTo por nome do slot ou nome do arquivo Drive — sem depender de campo Firestore
    const computePromotedTo = (keyword) => {
      // 1. Checar slotNames pelo keyword (ex: "PROCURA", "DECLARA")
      for (const [slotId, slotName] of Object.entries(_bSlotNames)) {
        if (
          String(slotName || "")
            .toUpperCase()
            .includes(keyword)
        ) {
          return { slotId, slotName: String(slotName) };
        }
      }
      // 2. Checar nome do arquivo Drive no assignment
      for (const [slotId, assignment] of Object.entries(_bAssignments)) {
        if (
          String(assignment?.name || "")
            .toUpperCase()
            .includes(keyword)
        ) {
          return { slotId, slotName: _bSlotNames[slotId] || slotId };
        }
      }
      return null;
    };

    const contratos = [];
    const procuracoes = [];
    const declaracoes = [];
    const termosRenuncia = [];
    const termosRevogacao = [];
    (publishedDocs || []).forEach((d) => {
      const fmt = String(
        d?.publication?.format || d?.format || "",
      ).toLowerCase();
      if (fmt !== "pdf") return;
      const tipo = classifyPeca(d);
      if (tipo === "CONTRATO") {
        contratos.push({
          ...d,
          promotedTo: d.promotedTo || computePromotedTo("CONTRATO"),
        });
      } else if (tipo === "PROCURACAO") {
        procuracoes.push({
          ...d,
          promotedTo: d.promotedTo || computePromotedTo("PROCURA"),
        });
      } else if (tipo === "DECLARACAO") {
        declaracoes.push({
          ...d,
          promotedTo: d.promotedTo || computePromotedTo("DECLARA"),
        });
      } else if (tipo === "TERMO_RENUNCIA") {
        termosRenuncia.push({
          ...d,
          promotedTo: d.promotedTo || computePromotedTo("RENUNCIA"),
        });
      } else if (tipo === "TERMO_REVOGACAO") {
        termosRevogacao.push({
          ...d,
          promotedTo: d.promotedTo || computePromotedTo("REVOGA"),
        });
      }
    });
    return {
      contratoDocs: contratos,
      procuracaoDocs: procuracoes,
      declaracaoDocs: declaracoes,
      // Termos de Renúncia/Revogação: classificados corretamente (não caem
      // mais no fallback "PETICAO"), mas ainda sem seção própria de UI no
      // Caderno — decisão de escopo v1 (Art. 373 / plano aprovado
      // 15/08/2026). Listas expostas aqui prontas para uma UI dedicada
      // futura, sem precisar mexer neste useMemo de novo.
      termoRenunciaDocs: termosRenuncia,
      termoRevogacaoDocs: termosRevogacao,
    };
  }, [publishedDocs, hubBridge]);

  // ─── MC06 (revisto MC_Y7): Auto-seleção da peça ativa ─────────────────
  // Ao trocar de caderno (workspaceKey muda), limpa a peça ativa stale e
  // abre automaticamente a ÚLTIMA peça publicada (mais recente — publishedDocs
  // já vem ordenado desc por createdAt do onSnapshot). Se o caderno não tem
  // nenhuma peça, permanece em empty state (usuário cria via "+ Peça").
  const lastWorkspaceKeyRef = useRef("");
  useEffect(() => {
    // Troca de caderno: limpa peça ativa antiga.
    if (
      lastWorkspaceKeyRef.current &&
      lastWorkspaceKeyRef.current !== workspaceKey
    ) {
      setActivePecaDoc(null);
      setActiveViewDoc(null);
    }
    lastWorkspaceKeyRef.current = workspaceKey;
  }, [workspaceKey]);

  useEffect(() => {
    if (activePecaDoc) return;
    if (!Array.isArray(publishedDocs) || publishedDocs.length === 0) return;
    // Prioridade: contrato > procuração > primeira petição (mais recente).
    const preferred =
      publishedDocs.find((d) => classifyPeca(d) === "CONTRATO") ||
      publishedDocs.find((d) => classifyPeca(d) === "PROCURACAO") ||
      publishedDocs[0];
    if (preferred) setActivePecaDoc(preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceKey, publishedDocs.length]);

  // 🆕 SUBSCRIBE: Cliente atualizado no CRM (rda:client-updated)
  useEffect(() => {
    const handler = async (e) => {
      const { clientId, clientName } = e.detail || {};
      if (!clientId) return;

      // Verificar se o cliente atualizado é o cliente ativo no caderno
      const activeId = String(selectedClientIdRef.current || "").trim();
      if (clientId !== activeId) return;

      console.log("[CadernoCockpit] Cliente atualizado pelo CRM:", clientName);

      try {
        // Re-fetch dos dados do cliente
        const freshClient = await fetchClientDoc(appId, clientId);
        if (!freshClient) return;

        // Atualizar dados no hubBridge
        if (hubBridge?.setClientData) {
          hubBridge.setClientData({
            clientId: freshClient.id,
            clientName: String(
              freshClient?.qualification?.physical?.name ||
                freshClient?.qualification?.legal?.companyName ||
                "",
            ).trim(),
            clientCpf: String(
              freshClient?.qualification?.physical?.cpf || "",
            ).trim(),
            clientEmail: String(freshClient?.contact?.email || "").trim(),
          });
        }

        // Disparar notificacao para bridge (Word) com as partes completas atualizadas
        // FIX V22 (21/07/2026): Incluir QUALIFICACAO FORENSE no text do cliente
        // principal (buildQualificacaoText), igual ao handleSavePartes faz quando
        // o usuario clica em "Salvar Partes". Sem isso, o Word recebe apenas o
        // nome, sem a qualificacao completa (nacionalidade, estado civil, etc).
        if (hubBridge?.postBridgeState) {
          // Cliente principal (Autor) com nome + qualificacao atualizados
          var freshName = String(
            freshClient?.qualification?.physical?.socialName ||
              freshClient?.qualification?.physical?.name ||
              freshClient?.qualification?.legal?.companyName ||
              freshClient?.name ||
              selectedClientName ||
              "",
          ).trim();
          var freshId = String(clientId || "").trim();

          // Monta qualificacao para o cliente principal usando os dados frescos
          // Replica a logica de handleSavePartes + buildQualificacaoText
          var freshQualText = "";
          try {
            var qualObj = {
              nome: freshName,
              tipo: freshClient?.qualification?.physical?.cpf ? "PF" : "PJ",
              documento:
                freshClient?.qualification?.physical?.cpf ||
                freshClient?.qualification?.legal?.cnpj ||
                "",
              qualificacao: {
                nacionalidade:
                  freshClient?.qualification?.physical?.nationality || "",
                estado_civil:
                  freshClient?.qualification?.physical?.maritalStatus || "",
                profissao:
                  freshClient?.qualification?.physical?.profession || "",
                rg: freshClient?.qualification?.physical?.rg || "",
                orgao_expedidor:
                  freshClient?.qualification?.physical?.issuingAuthority || "",
                cpf: freshClient?.qualification?.physical?.cpf || "",
                cnpj: freshClient?.qualification?.legal?.cnpj || "",
                email: freshClient?.contact?.email || "",
                endereco: freshClient?.address?.street || "",
                bairro: freshClient?.address?.neighborhood || "",
                cidade: freshClient?.address?.city || "",
                estado: freshClient?.address?.state || "",
                cep: freshClient?.address?.zipCode || "",
                telefone: freshClient?.contact?.phone || "",
                registerType:
                  freshClient?.qualification?.physical?.registerType || "",
                registerNumber:
                  freshClient?.qualification?.physical?.registerNumber || "",
                registerUF:
                  freshClient?.qualification?.physical?.registerUF || "",
              },
            };
            freshQualText = buildQualificacaoText(qualObj);
          } catch (_q) {
            freshQualText = "";
          }

          var freshClienteParte =
            freshId && freshName
              ? {
                  id: freshId,
                  role: "Cliente",
                  name: freshName,
                  text: freshQualText,
                  ooxml: freshQualText
                    ? "<p><b>" + freshName + "</b>, " + freshQualText + "</p>"
                    : "<p><b>" + freshName + "</b></p>",
                }
              : null;

          var freshAdversas = (
            Array.isArray(partesAdversas) ? partesAdversas : []
          )
            .filter(function (r) {
              return r && r.nome && String(r.nome).trim();
            })
            .map(function (r) {
              var qualifText = "";
              try {
                qualifText = buildQualificacaoText(r);
              } catch (_q) {
                qualifText = "";
              }
              return {
                id: String(r.documento || r.nome || "").trim(),
                role: "Réu",
                name: String(r.nome || "")
                  .trim()
                  .toUpperCase(),
                text: qualifText,
                ooxml: qualifText
                  ? "<p><b>" +
                    String(r.nome || "").trim() +
                    "</b>, " +
                    qualifText +
                    "</p>"
                  : "<p><b>" + String(r.nome || "").trim() + "</b></p>",
              };
            });

          var freshCoPartes = (Array.isArray(coPartesEdit) ? coPartesEdit : [])
            .filter(function (r) {
              return r && (r.clientId || r.name);
            })
            .map(function (r) {
              return {
                id: String(r.clientId || r.name || "").trim(),
                role: r.role || "Co-Parte",
                name: String(r.name || "")
                  .trim()
                  .toUpperCase(),
                text: "",
                ooxml: "",
              };
            });

          var todasPartes = [];
          if (freshClienteParte) todasPartes.push(freshClienteParte);
          todasPartes = todasPartes.concat(freshAdversas).concat(freshCoPartes);

          if (todasPartes.length > 0) {
            hubBridge.postBridgeState({ parties: todasPartes });
          }
        }
      } catch (err) {
        console.error(
          "[CadernoCockpit] Erro ao processar atualizacao do CRM:",
          err,
        );
      }
    };

    window.addEventListener("rda:client-updated", handler);
    return () => window.removeEventListener("rda:client-updated", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientIdRef.current, hubBridge]);

  // ─── Render principal: Layout IDE de 2 painéis (Explorer Tree-View | Editor FileHub)
  //     activePecaDoc controla se o painel central mostra FileHubArea (peça selecionada)
  //     ou um estado vazio de "selecione uma peça".
  return (
    <div
      data-testid="caderno-cockpit"
      className="flex flex-col h-[calc(100vh-64px)] w-full font-sans bg-gray-100 overflow-hidden relative print:h-auto print:overflow-visible print:bg-white"
    >
      {/* ═══ CABEÇALHO GLOBAL (largura total) — só Nível 0 (Cliente) ═════════
           MC41e (Fase F2): o MC41d tinha subido o cabeçalho do CADERNO
           (Nível 2: título/status/abas) pra cá também, esticando por cima da
           árvore de Cadernos (Nível 1) — dava a impressão de que as abas
           controlavam a árvore. Corrigido: só o Cliente (Nível 0, aplica a
           tudo embaixo, inclusive a árvore) fica em largura total. O
           cabeçalho do caderno aberto volta pra dentro do <main>, ao lado da
           árvore, não por cima dela — ver bloco lá embaixo. */}
      <div
        className="shrink-0 flex flex-col"
        style={{ background: "#20243B" }}
      >
        {selectedClientName ? (
          <div className="flex items-center justify-between px-4 py-2">
            {/* MC101 (11/09/2026): `uppercase` (CSS) removida — mascarava
                a inconsistência de caixa do dado sem resolvê-la (a mesma
                técnica achada em ProfileSelector/PortalMensagens/
                CadernoSelector na pesquisa "Anatomia da Maiúscula"). Nome
                do cliente agora passa por `toTitleCasePt`, igual à coluna
                "Cliente" da tabela Processos e Casos. */}
            <span className="flex items-center gap-2 text-sm font-bold text-white truncate">
              <i className="fas fa-user text-[11px] text-[#C5A059]"></i>
              {toTitleCasePt(selectedClientName)}
            </span>
            <button
              type="button"
              onClick={() => setIsClientPickerOpen(true)}
              className="text-[9px] font-bold text-[#C5A059] hover:text-white uppercase tracking-widest transition-colors"
              data-testid="btn-trocar-cliente"
            >
              Trocar
            </button>
          </div>
        ) : null}
        <div className="h-0.5 w-full" style={{ background: "#C5A059" }} />
      </div>

      {/* ═══ CORPO: EXPLORER + PAINEL CENTRAL, lado a lado ═══════════════════ */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* ═══ PAINEL ESQUERDO — EXPLORER (largura redimensionável) ════════════
           MC_Y5: padronização visual com Motor de Contratos / Motor de Procurações.
           V11: Tree-View estilo Windows Explorer (substitui tabs + acordeões).
           P25: Largura dinâmica via hook useSidebarResize + localStorage. */}
      <aside
        data-testid="explorer-panel"
        style={{ width: sidebarWidth }}
        // MC78 (10/09/2026): shadow-xl/border-gray-200 → shadow-sm/
        // border-gray-300, pra bater com a convenção já usada nos cartões
        // do lado direito (SummaryCard, linha ~2241) — achado do titular:
        // a sombra grande e difusa daqui parecia "mais clara" que a sombra
        // pequena e concentrada de lá, mesmo sendo tecnicamente maior.
        className="shrink-0 h-full bg-white border-r border-gray-300 shadow-sm z-20 flex flex-col overflow-hidden"
      >
        {/* ── CORPO: TREE-VIEW (V11 — Windows Explorer) ────────────────── */}
        {/* MC75 (10/09/2026): px-2→px-4 pra bater com o px-4 da barra do
            cliente (ícone de usuário) logo acima — achado do titular:
            sobrava um espaço à esquerda desperdiçado porque os dois
            usavam paddings diferentes (8px aqui vs. 16px lá), então nada
            alinhava verticalmente com o ícone do cliente. */}
        {/* MC104t (15/09/2026, achado ao vivo — Chrome real + CDP): titular
            relatou conteúdo "vazando" num buraco entre a barra do cliente
            e o cabeçalho "Processos" durante rolagem. O cabeçalho vivia
            `position:sticky` DENTRO deste container com `overflow-y-auto`
            — 3 tentativas de correção nessa arquitetura (corrigir a
            especificidade da margem negativa contra `space-y-0.5` do pai,
            MC104q; `will-change-transform` pra promover o sticky a
            camada própria, MC104r; `contain:paint` no scroller pra
            forçar clipping mais forte, MC104s) foram todas medidas ao
            vivo e NENHUMA eliminou o vazamento — confirmado
            reproduzindo com um `scrollTop` fixo (nem precisa de rolagem
            rápida: a mesma caixa pinta ~20px acima do limite do
            container em QUALQUER método de rolagem, sempre no mesmo
            scrollTop). Ou seja, não era timing/composição — era
            estrutural: um `position:sticky` dentro de um container com
            padding tem uma classe inteira de bugs de clipping que
            nenhuma dessas 3 mitigações cobre por completo. Fix definitivo:
            parar de tentar imitar um cabeçalho fixo com sticky+padding, e
            simplesmente TORNÁ-LO um cabeçalho fixo de verdade — mesmo
            padrão que a barra "[PET_A]" já usa (elemento estático, FORA
            da área de rolagem, nunca compete com conteúdo scrollado por
            definição). Cabeçalho "Processos (N)" migrou pra cá, como
            irmão do scroller, antes dele — ver o `<div className="sticky
            ...">` que morava logo abaixo, agora removido. */}
        {selectedClientId ? (
          <div className="shrink-0 bg-white border-b border-gray-300 shadow-sm px-4 py-1 flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-600">
              Processos{" "}
              {sortedWorkspaces.length > 0 ? `(${sortedWorkspaces.length})` : ""}
            </span>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={cycleSortMode}
                className="w-5 h-5 rounded flex items-center justify-center text-gray-400 hover:text-[#C5A059] hover:bg-[#C5A059]/10 transition-colors"
                title={`Ordenar: ${
                  workspaceSortMode === "alpha"
                    ? "A→Z"
                    : workspaceSortMode === "numero"
                      ? "Nº do processo ↑"
                      : workspaceSortMode === "status"
                        ? "Por status"
                        : workspaceSortMode === "vinculos"
                          ? "Por vínculos (família)"
                          : "Criação ↑"
                }`}
                data-testid="btn-sort-cadernos"
              >
                <i
                  className={`fas ${
                    workspaceSortMode === "alpha"
                      ? "fa-sort-alpha-down"
                      : workspaceSortMode === "numero"
                        ? "fa-sort-numeric-down"
                        : workspaceSortMode === "status"
                          ? "fa-layer-group"
                          : workspaceSortMode === "vinculos"
                            ? "fa-sitemap"
                            : "fa-clock"
                  } text-[9px]`}
                ></i>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (typeof onRequestNewCaderno === "function")
                    onRequestNewCaderno(selectedClientId);
                }}
                className="w-5 h-5 rounded flex items-center justify-center text-[#C5A059] hover:bg-[#C5A059]/10 transition-colors"
                title="Novo caderno"
                data-testid="btn-novo-caderno"
              >
                <i className="fas fa-plus text-[9px]"></i>
              </button>
            </div>
          </div>
        ) : null}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 py-2 bg-gray-50/80">
          {/* ── Nível 0 · Cliente ── */}
          {!selectedClientId ? (
            <div className="p-3 text-center">
              <button
                type="button"
                onClick={() => setIsClientPickerOpen(true)}
                className="text-[10px] font-bold text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 rounded px-2 py-1 hover:bg-[#C5A059]/20 transition-colors"
              >
                Importar CRM
              </button>
            </div>
          ) : (
            <div
              className="space-y-0.5 relative"
              data-testid="tree-view"
              ref={vinculosContainerRef}
            >
              {/* MC104h: SVG único cobrindo o container inteiro, atrás das
                  caixas (z-index padrão, pintado antes delas no DOM — a
                  cor de fundo de cada caixa cobre o pedacinho de linha que
                  "entraria" nela, o que é o efeito certo: a linha parece
                  nascer/morrer na borda da caixa, não atravessá-la).
                  `pointer-events-none` — decorativo, nunca deve capturar
                  clique no lugar da caixa. */}
              {workspaceSortMode === "vinculos" && vinculosLines.length > 0 ? (
                <svg
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  aria-hidden="true"
                >
                  {vinculosLines.map((line) => (
                    <path
                      key={line.key}
                      d={line.d}
                      fill="none"
                      stroke="#9ca3af"
                      strokeWidth="2"
                      strokeDasharray={line.dashed ? "4 3" : undefined}
                    />
                  ))}
                </svg>
              ) : null}
              {/* ── Nível 1: Cadernos (cada caderno = pasta) ──
                  MC77 (10/09/2026): cabeçalho "Processos (N)" morava aqui,
                  sticky. MC104t (15/09/2026): migrado pra fora do scroller
                  (ver comentário no container acima) — motivo completo lá.
                  */}
              {sortedWorkspaces.length === 0 ? (
                <div className="text-[10px] text-gray-400 italic border border-dashed border-gray-300 rounded px-2 py-2 text-center bg-white">
                  Nenhum caderno. Clique em <strong>+</strong> para criar.
                </div>
              ) : (
                sortedWorkspaces.map((w, idx) => {
                  const wId = String(w?.id || "");
                  const isActive =
                    activeWorkspace?.id && String(activeWorkspace.id) === wId;
                  const isEditing = editingNodeId === `caderno-${wId}`;
                  const { title, isPreProcessual, numeroFormatted } = resolveProcessTitle(w);
                  // MC81 (10/09/2026): achado do titular — todo processo TEM
                  // um número; "Pré-Processual" é o que aparece nesse lugar
                  // quando o número ainda não existe. Igual ao processual
                  // (que já mostra "CNJ - nomes" numa linha só), o
                  // pré-processual passa a mostrar "Pré-Processual - alias"
                  // na MESMA linha do título.
                  // MC82 (10/09/2026): achado do titular — pra ponto/traço
                  // do CNJ caírem no MESMO pixel entre linhas diferentes,
                  // não basta ter a mesma quantidade de caracteres (a fonte
                  // do título não é monoespaçada, então "1" e "8" têm
                  // larguras diferentes). Separado o identificador (número
                  // CNJ, sempre no mesmo formato fixo, ou "Pré-Processual"
                  // quando não existe ainda) do resto do nome, e só ele
                  // ganha fonte monoespaçada (`font-mono`) — como ele
                  // sempre começa na mesma posição (logo depois do selo de
                  // status, que já tem largura fixa), isso garante o
                  // alinhamento pixel a pixel entre CNJs de linhas
                  // diferentes, sem precisar preencher caractere nenhum.
                  const identifier = isPreProcessual ? "Pré-Processual" : (numeroFormatted || "");
                  const namesPart = isPreProcessual ? title : title.slice(identifier.length + 3);
                  const statusLabel = String(w?.status || "Sem status").trim();
                  const statusStyle = getStatusStyle(statusLabel);
                  // MC103 (12/09/2026): divisor de grupo no modo "Por
                  // status" — grupo muda em relação ao item anterior (ou é
                  // o primeiro item), mostra o rótulo do grupo antes da
                  // linha. Rótulo de texto sempre visível, nunca só cor
                  // (regra de acessibilidade padrão desta tela).
                  const groupKey =
                    workspaceSortMode === "status" ? getStatusGroupKey(w) : null;
                  const prevW = idx > 0 ? sortedWorkspaces[idx - 1] : null;
                  const prevGroupKey =
                    workspaceSortMode === "status" && prevW
                      ? getStatusGroupKey(prevW)
                      : null;
                  const showGroupHeader =
                    workspaceSortMode === "status" && groupKey !== prevGroupKey;
                  // MC104 (12/09/2026): rótulo de relação no modo "Por
                  // vínculos" — ver vinculosFlat. O traço/seta ligando as
                  // caixas é desenhado à parte, num SVG único medido de
                  // verdade (`recomputeVinculosLines`, `vinculosLines`),
                  // não aqui por linha.
                  const vMeta =
                    workspaceSortMode === "vinculos"
                      ? vinculosMetaById?.get(wId)
                      : null;
                  const vRelationLabel = !vMeta
                    ? null
                    : vMeta.relation === "apenso"
                      ? "Apenso"
                      : vMeta.relation === "correlato"
                        ? "Correlato"
                        : vMeta.familySize > 1
                          ? "Principal"
                          : null;
                  return (
                    <React.Fragment key={wId || idx}>
                      {showGroupHeader ? (
                        <div
                          className={`flex items-center gap-2 pb-1 ${idx === 0 ? "pt-0" : "pt-3"}`}
                        >
                          <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
                            {STATUS_GROUP_LABELS[groupKey]}
                          </span>
                          <span className="flex-1 h-px bg-gray-300" aria-hidden="true" />
                        </div>
                      ) : null}
                    <div
                      data-testid={`caderno-node-${wId || idx}`}
                    >
                      {/* ── Bloco do Caderno (Nível 1) — MC41h (Fase F2):
                          título + status/CNJ agora num container só, com o
                          mesmo fundo/realce (antes a 2ª linha era um <div>
                          irmão, sem o fundo da linha ativa/hover — parecia
                          "solta", fora da caixa.
                          MC74 (10/09/2026): tirada a borda dourada da
                          esquerda — achado do titular: item ativo já
                          sinalizava com 3 pistas (ícone pasta-aberta
                          dourado, seta dourada, fundo tingido), a borda era
                          uma 4ª pista redundante pra dizer a mesma coisa.
                          MC89 (10/09/2026): 3ª tentativa de resolver o
                          aperto horizontal — pedido do titular, e a que ele
                          escolheu entre as 3 opções discutidas: número/selo
                          de status SEMPRE numa linha própria (1ª), nome das
                          partes SEMPRE na 2ª linha, embaixo. Diferente da
                          MC79 (que juntava tudo numa linha só e brigava por
                          espaço) e da ideia descartada de quebra dinâmica
                          (deixaria a altura da linha variar por item — o
                          titular preferiu previsibilidade estrita: TODA
                          linha da lista tem exatamente 2 linhas, sempre,
                          não importa o tamanho do nome nem a largura da
                          barra). Fonte de volta pra 12px (`text-xs`) nas
                          duas linhas — a pressão que levou a pedir 9px
                          (MC87) era número+nome disputando espaço na mesma
                          linha; sem essa disputa, não precisa mais
                          encolher. */}
                      {/* MC93 (10/09/2026): border-gray-100 (copiado direto
                          da tabela) ficava quase invisível — achado do
                          titular: aquela tela tem fundo branco puro, esta
                          árvore tem fundo `bg-gray-50/80` (levemente
                          acinzentado), e a diferença entre gray-100 e
                          gray-50 é pequena demais pra dar contraste. Trocado
                          pra gray-300 — mesmo tom já usado nas outras
                          divisórias deste painel (cabeçalho "Processos"),
                          não um valor novo. */}
                      {/* MC94 (10/09/2026): `rounded-md` + `border-b` juntos
                          faziam a linha curvar nas pontas (seguindo o canto
                          arredondado) em vez de ficar reta — achado do
                          titular. `rounded-t-md` arredonda só os cantos de
                          CIMA (onde não tem borda), embaixo (onde a linha
                          mora) fica reto de ponta a ponta. */}
                      {/* MC95 (10/09/2026): hover:bg-slate-50 → hover:bg-
                          gray-50, pra bater exatamente com a cor de hover
                          já usada na tabela "Processos e Casos" (mesma
                          interação, mesma cor nas 2 telas).
                          MC96 (10/09/2026): linha (e fundo do hover) parava
                          onde o preenchimento do container (`px-4`) termina,
                          não na borda física do painel — achado do titular.
                          `-mx-4` cancela esse preenchimento só pra ESTE
                          bloco (mesma técnica já usada no cabeçalho
                          "Processos (11)", MC77), fazendo a linha/fundo
                          irem de ponta a ponta de verdade; `pl-4`/`pr-6` no
                          conteúdo interno repõem exatamente o mesmo recuo
                          visual que o `px-4` do container dava antes (16px
                          à esquerda, 24px à direita — a mesma assimetria
                          que já existia, só que agora explícita aqui em vez
                          de emprestada do container). */}
                      {/* MC97 (10/09/2026): achado do titular — o clique e o
                          `cursor-pointer` só existiam no div interno da 1ª
                          linha (ícone+número+status+seta); a 2ª linha
                          (nomes) e o preenchimento do container não tinham
                          handler nenhum. O FUNDO do hover já cobria o bloco
                          inteiro (ambas as linhas), mas sem `onClick`/
                          `cursor-pointer` ali, a área parecia "morta" fora
                          do número — na tabela de referência, o `<tr
                          onClick>` inteiro é uma unidade só. Handler e
                          cursor subiram pro container externo (mesmo que
                          hospeda o fundo do hover), pra linha inteira reagir
                          igual à tabela.
                          MC98 (10/09/2026): 2º achado do titular na mesma
                          rodada — "não vejo efeito nenhum, só a pastinha
                          fica dourada e só em cima do número". Causa: (1)
                          `hover:bg-gray-50` é quase a MESMA cor do fundo
                          `bg-gray-50/80` que a árvore inteira já tem (mesmo
                          problema de contraste do MC93, agora no hover em
                          vez da borda) — o fundo realmente mudava, só que
                          imperceptível a olho nu; o único efeito VISÍVEL
                          era o ícone ficando dourado. (2) esse ícone só
                          reage porque `group` estava no div interno (só a
                          1ª linha), não no container que acabou de virar a
                          unidade clicável inteira no MC97 — por isso "só em
                          cima do número". Correção: `group` sobe pro
                          container externo (ícone reage em qualquer parte
                          da linha) e o hover troca pra `gray-200`, tom
                          realmente diferente do fundo cinza-claro do
                          painel. */}
                      {/* MC104f (12/09/2026): achado do titular — não era
                          pra tirar a linha e continuar lista achatada, era
                          pra virar CONTÊINER por processo (uma caixa com
                          borda por linha, como as caixas da aba Vínculos à
                          direita), só no modo "Por Vínculos" — as outras 4
                          ordenações continuam exatamente como eram (linha
                          fininha de divisor, sem caixa). `vMeta` só existe
                          nesse modo, então ele decide qual dos dois
                          visuais aparece. Cor da caixa reaproveita
                          `statusStyle.border`/`.bg` — os MESMOS campos já
                          usados nos cards da aba Vínculos (MC79), não uma
                          paleta nova.
                          MC104h (12/09/2026): achado do titular — queria
                          organograma de verdade (linha fixa/tracejada com
                          junção em T, tipo SmartArt do Word), rotacionado
                          pra caber na coluna estreita. As 3 tentativas
                          anteriores de conector (texto, CSS esticado,
                          setinha solta) falhavam por tentar CALCULAR a
                          posição da linha sem ver a tela renderizada.
                          Desta vez a caixa só se registra (`ref`) num mapa
                          — quem desenha a linha é um SVG único, medido de
                          verdade via `getBoundingClientRect` depois do
                          render (ver `recomputeVinculosLines` acima),
                          nunca chutado. */}
                      <div
                        ref={
                          vMeta
                            ? (el) => {
                                if (el) vinculosBoxRefs.current.set(wId, el);
                                else vinculosBoxRefs.current.delete(wId);
                              }
                            : undefined
                        }
                        className={
                          vMeta
                            ? `group relative rounded-lg border transition-colors cursor-pointer mb-2 ${
                                isActive
                                  ? "border-[#C5A059] bg-[#C5A059]/5"
                                  : `${statusStyle.border} ${statusStyle.bg} hover:shadow-sm`
                              }`
                            : `group -mx-4 rounded-t-md border-b border-gray-300 transition-colors cursor-pointer ${
                                isActive ? "bg-[#C5A059]/5" : "hover:bg-gray-200"
                              }`
                        }
                        style={vMeta ? { marginLeft: vMeta.depth * 24 } : undefined}
                        onClick={() => {
                          if (
                            !isEditing &&
                            typeof onSwitchWorkspace === "function"
                          )
                            onSwitchWorkspace(w);
                        }}
                      >
                        {vMeta && vRelationLabel ? (
                          <div
                            className={`px-3 pt-1.5 text-[9px] font-bold uppercase tracking-wide ${
                              vMeta.relation === "apenso"
                                ? "text-blue-700"
                                : vMeta.relation === "correlato"
                                  ? "text-amber-700"
                                  : "text-purple-700"
                            }`}
                          >
                            {vRelationLabel}
                          </div>
                        ) : null}
                        <div
                          className={
                            vMeta
                              ? "flex items-center gap-1.5 px-3 pt-1 pb-1"
                              : "flex items-center gap-1.5 pl-4 pr-6 py-1.5"
                          }
                        >
                          <i
                            className={`fas ${isActive ? "fa-folder-open" : "fa-folder"} text-xs w-4 text-center shrink-0 ${isActive ? "text-[#C5A059]" : "text-gray-400 group-hover:text-[#C5A059]"}`}
                          ></i>
                          {isEditing ? (
                            <input
                              autoFocus
                              value={editingNodeValue}
                              onChange={(e) =>
                                setEditingNodeValue(e.target.value)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  handleFinishInlineEdit(wId);
                                if (e.key === "Escape") {
                                  setEditingNodeId(null);
                                  setEditingNodeValue("");
                                }
                              }}
                              onBlur={() => handleFinishInlineEdit(wId)}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 min-w-0 h-6 px-1.5 text-xs font-semibold text-[#20243B] border border-[#C5A059] rounded bg-white focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                          ) : (
                            // MC90 (10/09/2026): 2 achados do titular na
                            // mesma rodada. (1) o selo entre ícone e número
                            // empurrava o número pra um X diferente do que a
                            // 2ª linha (nome) usava — desalinhamento entre
                            // as 2 linhas. Selo mudou de lugar: agora vem
                            // DEPOIS do número, empurrado pro canto direito
                            // (`ml-auto`) — número fica logo após o ícone,
                            // igual à 2ª linha, e vira coluna própria à
                            // direita. Como não tem mais nada competindo por
                            // espaço à direita do número, a caixa de largura
                            // fixa (25ch) não é mais necessária — mantém só
                            // `whiteSpace:nowrap` pra não voltar a quebrar
                            // no meio.
                            // (2) "fontes diferentes" — em vez de trocar a
                            // FAMÍLIA de fonte (o que sempre vai destoar do
                            // resto da tela, é a natureza de uma fonte
                            // monoespaçada), mantém a MESMA fonte do resto
                            // da interface e só liga `tabular-nums`
                            // (recurso de fonte que iguala a largura dos
                            // dígitos, sem precisar trocar de família) —
                            // resolve "fonte estranha" sem abrir mão do
                            // alinhamento dos números.
                            <span
                              style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                              className="shrink-0 text-xs font-semibold text-[#20243B]"
                              title={identifier}
                            >
                              {identifier}
                            </span>
                          )}
                          {/* MC91 (10/09/2026): achado do titular — pílula
                              colorida grande (retângulo, fundo, negrito,
                              maiúsculo, 104px fixo) chamava atenção demais
                              numa lista de 11 itens repetindo o mesmo
                              elemento. Trocada por bolinha + texto simples
                              (mesmo espírito do selo já usado na aba
                              Vínculos, sem o fundo/retângulo) — a cor ainda
                              comunica o status (na bolinha), o texto ainda
                              está lá por extenso (nunca só cor), só não
                              grita mais. Sem largura fixa: sem fundo
                              colorido pra alinhar como coluna, não precisa
                              mais reservar espaço. */}
                          {!isEditing ? (
                            <span
                              className="shrink-0 ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 whitespace-nowrap"
                              title={statusLabel}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusStyle.dot}`}
                                aria-hidden="true"
                              />
                              {statusLabel}
                            </span>
                          ) : null}
                          <i
                            className={`fas fa-chevron-${isActive ? "down" : "right"} text-[8px] shrink-0 ${isActive ? "text-[#C5A059]" : "text-gray-300"}`}
                          ></i>
                        </div>
                        {/* 2ª linha — nome das partes envolvidas. Recuo
                            calculado pra bater EXATAMENTE onde o número
                            começa na 1ª linha: 16px (compensa o -mx-4 do
                            bloco pai, MC96) + ícone (16px, w-4) + gap (6px,
                            gap-1.5) = 38px. Direita usa o mesmo pr-6 (24px)
                            da 1ª linha, pela mesma razão. */}
                        {!isEditing ? (
                          <div
                            className={
                              vMeta
                                ? "px-3 pb-2 -mt-0.5"
                                : "pl-[38px] pr-6 pb-1.5 -mt-0.5"
                            }
                          >
                            <span
                              className="block text-xs font-semibold text-[#20243B] truncate"
                              title={namesPart}
                            >
                              {namesPart}
                            </span>
                          </div>
                        ) : null}
                      </div>

                      {/* ── Nível 2: Peças (dentro do caderno ativo) ──
                          MC41b (Fase F2): só aparece na aba "Caderno de
                          Autos" — antes aparecia em qualquer aba. */}
                      {isActive && activeTab === "caderno_autos" ? (
                        <div className="ml-7 pl-3 border-l border-gray-200 space-y-0.5 py-1">
                          {/* ── Contratos (só aparece quando publicado em PDF) */}
                          {contratoDocs.map((d, idx) => (
                            <div
                              key={d.id || idx}
                              className={`flex items-center gap-2 pl-2 pr-1 py-1 rounded cursor-pointer transition-colors group/p ${activeViewDoc?.id === d.id ? "bg-[#C5A059]/10" : "hover:bg-slate-50"}`}
                              onClick={() => {
                                setActiveViewDoc(d);
                                setActivePecaDoc(null);
                              }}
                              data-testid={
                                idx === 0
                                  ? "peca-contrato"
                                  : `peca-contrato-${idx}`
                              }
                            >
                              <i
                                className={`fas fa-file-contract w-4 text-center text-[12px] ${activeViewDoc?.id === d.id ? "text-[#C5A059]" : "text-[#20243B] group-hover/p:text-[#C5A059]"}`}
                              />
                              <span
                                className="flex-1 text-[11px] font-semibold text-[#20243B] truncate"
                                title={d.title || d.name || "Contrato"}
                              >
                                {String(d.title || d.name || "Contrato").slice(
                                  0,
                                  30,
                                )}
                              </span>
                              <span
                                className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors ${d.promotedTo ? "text-blue-500 hover:bg-blue-50" : "text-gray-300 hover:text-gray-400 hover:bg-gray-50"}`}
                                title={
                                  d.promotedTo
                                    ? `Vinculado a ${d.promotedTo.slotName || "anexo"}`
                                    : "Não vinculado a nenhuma peça"
                                }
                              >
                                <i
                                  className={`fas ${d.promotedTo ? "fa-link" : "fa-unlink"} text-[7px]`}
                                />
                              </span>
                              {d.fileUrl || d.downloadUrl ? (
                                <a
                                  href={d.fileUrl || d.downloadUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-5 h-5 rounded flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors shrink-0"
                                  title="Abrir no Drive"
                                >
                                  <i className="fas fa-external-link-alt text-[7px]" />
                                </a>
                              ) : null}
                            </div>
                          ))}

                          {/* ── Procurações (só aparece quando publicada em PDF) */}
                          {procuracaoDocs.map((d, idx) => (
                            <div
                              key={d.id || idx}
                              className={`flex items-center gap-2 pl-2 pr-1 py-1 rounded cursor-pointer transition-colors group/p ${activeViewDoc?.id === d.id ? "bg-[#C5A059]/10" : "hover:bg-slate-50"}`}
                              onClick={() => {
                                setActiveViewDoc(d);
                                setActivePecaDoc(null);
                              }}
                              data-testid={
                                idx === 0
                                  ? "peca-procuracao"
                                  : `peca-procuracao-${idx}`
                              }
                            >
                              <i
                                className={`fas fa-file-signature w-4 text-center text-[12px] ${activeViewDoc?.id === d.id ? "text-[#C5A059]" : "text-[#20243B] group-hover/p:text-[#C5A059]"}`}
                              />
                              <span
                                className="flex-1 text-[11px] font-semibold text-[#20243B] truncate"
                                title={d.title || d.name || "Procuração"}
                              >
                                {String(
                                  d.title || d.name || "Procuração",
                                ).slice(0, 30)}
                              </span>
                              <span
                                className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors ${d.promotedTo ? "text-blue-500 hover:bg-blue-50" : "text-gray-300 hover:text-gray-400 hover:bg-gray-50"}`}
                                title={
                                  d.promotedTo
                                    ? `Vinculado a ${d.promotedTo.slotName || "anexo"}`
                                    : "Não vinculado a nenhuma peça"
                                }
                              >
                                <i
                                  className={`fas ${d.promotedTo ? "fa-link" : "fa-unlink"} text-[7px]`}
                                />
                              </span>
                              {d.fileUrl || d.downloadUrl ? (
                                <a
                                  href={d.fileUrl || d.downloadUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-5 h-5 rounded flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors shrink-0"
                                  title="Abrir no Drive"
                                >
                                  <i className="fas fa-external-link-alt text-[7px]" />
                                </a>
                              ) : null}
                            </div>
                          ))}

                          {/* ── Declarações (só aparece quando publicada em PDF) */}
                          {declaracaoDocs.map((d, idx) => (
                            <div
                              key={d.id || idx}
                              className={`flex items-center gap-2 pl-2 pr-1 py-1 rounded cursor-pointer transition-colors group/p ${activeViewDoc?.id === d.id ? "bg-[#C5A059]/10" : "hover:bg-slate-50"}`}
                              onClick={() => {
                                setActiveViewDoc(d);
                                setActivePecaDoc(null);
                              }}
                              data-testid={
                                idx === 0
                                  ? "peca-declaracao"
                                  : `peca-declaracao-${idx}`
                              }
                            >
                              <i
                                className={`fas fa-file-alt w-4 text-center text-[12px] ${activeViewDoc?.id === d.id ? "text-[#C5A059]" : "text-[#20243B] group-hover/p:text-[#C5A059]"}`}
                              />
                              <span
                                className="flex-1 text-[11px] font-semibold text-[#20243B] truncate"
                                title={d.title || d.name || "Declaração"}
                              >
                                {String(
                                  d.title || d.name || "Declaração",
                                ).slice(0, 30)}
                              </span>
                              <span
                                className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors ${d.promotedTo ? "text-blue-500 hover:bg-blue-50" : "text-gray-300 hover:text-gray-400 hover:bg-gray-50"}`}
                                title={
                                  d.promotedTo
                                    ? `Vinculado a ${d.promotedTo.slotName || "anexo"}`
                                    : "Não vinculado a nenhuma peça"
                                }
                              >
                                <i
                                  className={`fas ${d.promotedTo ? "fa-link" : "fa-unlink"} text-[7px]`}
                                />
                              </span>
                              {d.fileUrl || d.downloadUrl ? (
                                <a
                                  href={d.fileUrl || d.downloadUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-5 h-5 rounded flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors shrink-0"
                                  title="Abrir PDF"
                                >
                                  <i className="fas fa-external-link-alt text-[7px]" />
                                </a>
                              ) : null}
                            </div>
                          ))}

                          {/* Peças dinâmicas (gavetas) */}
                          {savedPieces.map((piece) => (
                            <div
                              key={piece.id}
                              className={`flex items-center gap-1 pl-2 pr-1 py-1 rounded transition-colors ${
                                activePieceId === piece.id
                                  ? "bg-[#C5A059]/10"
                                  : "hover:bg-slate-50"
                              }`}
                            >
                              <span
                                className="w-[68px] shrink-0 text-left text-xs font-medium text-gray-500 tabular-nums"
                                title={
                                  piece.pieceFolderDatePrefix
                                    ? `Data da pasta no Drive: ${formatPieceDateLabel(piece.pieceFolderDatePrefix)}`
                                    : "Peça sem data — pasta criada antes do fix de data-prefixo ou ainda não organizada"
                                }
                              >
                                {formatPieceDateLabel(piece.pieceFolderDatePrefix)}
                              </span>
                              {editingPieceName === piece.id ? (
                                <div className="flex-1 flex items-center gap-1 min-w-0">
                                  <input
                                    autoFocus
                                    value={editingPieceNameValue}
                                    onChange={(e) =>
                                      setEditingPieceNameValue(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleConfirmRenamePiece();
                                      } else if (e.key === "Escape") {
                                        handleCancelRenamePiece();
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex-1 h-6 px-1.5 rounded border border-[#C5A059] bg-white text-xs font-semibold text-[#20243B] focus:ring-1 focus:ring-[#C5A059] outline-none min-w-0"
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleConfirmRenamePiece();
                                    }}
                                    className="px-1.5 py-1 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 text-[9px] font-bold hover:bg-emerald-100 shrink-0"
                                    title="Confirmar"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCancelRenamePiece();
                                    }}
                                    className="px-1.5 py-1 rounded border border-red-300 bg-red-50 text-red-600 text-[9px] font-bold hover:bg-red-100 shrink-0"
                                    title="Cancelar"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ) : (
                                (() => {
                                  // P27-FIX2: Se pieceType não existe em nenhuma option,
                                  // renderiza option extra para evitar que o navegador
                                  // selectione o primeiro option disponível automaticamente.
                                  const allOptions = [
                                    ...(hubBridge?.PINNED_PIECE_TYPES || []),
                                    ...(
                                      hubBridge?.PIECE_OPTGROUPS_V2 || []
                                    ).flatMap((g) => g.options || []),
                                  ];
                                  const pt = piece.pieceType || "";
                                  const needsCustom =
                                    pt && !allOptions.includes(pt);
                                  // MC58 (03/09/2026, pedido do titular):
                                  // nome da peça e seletor de TIPO viraram
                                  // controles separados — antes eram o
                                  // MESMO <select> (o texto do tipo atual
                                  // ERA o próprio dropdown nativo), então
                                  // clicar no nome pra só abrir a peça
                                  // sempre abria o menu de trocar tipo
                                  // também. Nome agora é botão de texto só
                                  // (seleciona a peça); o seletor de tipo
                                  // vira um controle pequeno e separado, à
                                  // direita, mesmas opções de antes.
                                  return (
                                    <div className="flex-1 flex items-center gap-1 min-w-0">
                                      <button
                                        type="button"
                                        onClick={() => handleSelectPiece(piece)}
                                        className="flex-1 h-6 px-1 text-left rounded text-xs font-semibold text-[#20243B] truncate min-w-0 hover:bg-slate-100"
                                        title={pt || "Sem tipo definido"}
                                      >
                                        {pt || "— Tipo —"}
                                      </button>
                                      <select
                                        value={pt}
                                        onChange={(e) =>
                                          handleChangePieceType(
                                            piece.id,
                                            e.target.value,
                                          )
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-5 h-6 shrink-0 rounded border-0 bg-transparent text-[10px] text-gray-400 hover:text-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none cursor-pointer"
                                        title="Mudar tipo da peça"
                                      >
                                        {!piece.pieceType ? (
                                          <option value="">— Tipo —</option>
                                        ) : null}
                                        {needsCustom ? (
                                          <option value={pt}>{pt}</option>
                                        ) : null}
                                        <optgroup label="★  Favoritos">
                                          {(
                                            hubBridge?.PINNED_PIECE_TYPES || []
                                          ).map((t) => (
                                            <option key={t} value={t}>
                                              {t}
                                            </option>
                                          ))}
                                        </optgroup>
                                        {(
                                          hubBridge?.PIECE_OPTGROUPS_V2 || []
                                        ).map((group) => (
                                          <optgroup
                                            key={group.label}
                                            label={`▸  ${group.label}`}
                                          >
                                            {group.options.map((opt) => (
                                              <option key={opt} value={opt}>
                                                {opt}
                                              </option>
                                            ))}
                                          </optgroup>
                                        ))}
                                      </select>
                                    </div>
                                  );
                                })()
                              )}
                              {/* MC41i (Fase F2): os 4 botões de ação da peça
                                  saíram daqui — foram para o cabeçalho do
                                  FileHubArea (prop `pieceActions`), ao lado
                                  do título, quando esta é a peça ativa. A
                                  árvore (Nível 1) fica só navegação. */}
                            </div>
                          ))}

                          {/* B3: Approval notice */}
                          {approval.approvalNotice && (
                            <div className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1 mt-1">
                              {approval.approvalNotice}
                            </div>
                          )}

                          {/* + Nova Peça */}
                          <button
                            type="button"
                            onClick={handleAddPiece}
                            disabled={isAddingPiece}
                            className={`w-full flex items-center gap-2 pl-2 pr-2 py-1 rounded text-left border border-dashed transition-colors mt-1 ${
                              isAddingPiece
                                ? "border-gray-200 opacity-60 cursor-not-allowed"
                                : "border-gray-300 hover:border-[#C5A059] hover:bg-[#C5A059]/5"
                            }`}
                          >
                            <i
                              className={`fas ${isAddingPiece ? "fa-spinner fa-spin" : "fa-plus"} text-[9px] text-[#C5A059] w-3.5 text-center`}
                            ></i>
                            <span
                              className="text-[10px] font-bold text-gray-500 uppercase tracking-widest"
                              data-testid="btn-nova-peca"
                            >
                              {isAddingPiece ? "Criando..." : "Nova Peça"}
                            </span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                    </React.Fragment>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* ═══ RODAPÉ FIXO DO EXPLORER (padrão Motor de Contratos) ═══════════
             Mesmo layout do ContractEngine @4490: p-3 border-t border-gray-200
             bg-white shrink-0 space-y-2 + grid 2 colunas em 2 linhas.
             Botões dependentes de peça ficam DESABILITADOS (não escondidos)
             quando não há peça ativa — padrão mantido.
             MC41b (Fase F2): o rodapé inteiro só aparece na aba "Caderno de
             Autos" agora — antes era sempre visível em qualquer aba, mesmo
             lendo Histórico/Resumo, mostrando status de Drive (hubBridge)
             desatualizado (só populado quando FileHubArea está montado). */}
        {activeTab === "caderno_autos" ? (
        <div className="relative z-[90] p-3 border-t border-gray-200 bg-white shrink-0 space-y-2">
          {/* Fase 5/B (PRE_VOO_LIVRO_PROCESSUAL_GERADO_20260901.md): o
              botão "Abrir Caderno de Trabalho" que morava aqui virou a
              própria aba "Caderno de Trabalho" (entre esta e Vínculos) —
              pedido do titular 02/09/2026, mais simples que botão+modal. */}
          <div className="grid grid-cols-1 gap-2 no-print">
            {/* AÇÕES PRIMÁRIAS — padronizado com SaveCancelBar dos motores documentais */}
            <div className="flex items-center gap-2 no-print">
              <button
                type="button"
                onClick={() => {
                  if (hubBridge?.handleSaveAll) hubBridge.handleSaveAll();
                }}
                disabled={
                  !hubBridge ||
                  hubBridge.syncBusy ||
                  hubBridge.isRenaming ||
                  !hubBridge.isHydrated ||
                  hubBridge.isMirrorMode
                }
                className="flex-1 px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-[#C5A059] bg-[#C5A059] text-white hover:bg-[#a8844a] disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
                title={
                  hubBridge
                    ? "Salvar estado no Firestore e renomear arquivos no Drive"
                    : "Abra uma peça para salvar"
                }
              >
                <i className="fas fa-save"></i>
                {hubBridge?.syncBusy
                  ? "Salvando..."
                  : hubBridge?.isRenaming
                    ? "Renomeando..."
                    : "Salvar & Organizar"}
              </button>

              <a
                href={hubBridge?.driveUrl || "#"}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => {
                  if (!hubBridge?.driveFolderId) {
                    e.preventDefault();
                  }
                }}
                aria-disabled={!hubBridge?.driveFolderId}
                className={`flex-1 px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border flex items-center justify-center gap-1.5 leading-none shadow-sm transition-colors ${
                  hubBridge?.driveFolderId
                    ? "border-[#C5A059] bg-white text-[#C5A059] hover:bg-[#C5A059]/10"
                    : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed pointer-events-none"
                }`}
                title={
                  hubBridge?.driveFolderId
                    ? "Abrir pasta no Google Drive"
                    : "Drive não conectado"
                }
              >
                <i className="fab fa-google-drive"></i> Abrir no Drive
              </a>

              {/* MC46b (Fase F2): botão "Metadados" removido — desde o
                  MC46 ele só fazia setActiveTab("resumo"), duplicando a
                  própria aba Resumo já visível na tab-bar. Sem função
                  própria, sem motivo pra existir. */}
            </div>

            {/* BADGES DE ALERTA */}
            <div
              className="flex items-center justify-end gap-1 shrink-0"
              data-testid="badges-alerta"
            >
              {nivelSigilo && nivelSigilo !== "PUBLICO" ? (
                <span className="bg-red-600 text-white text-[7px] font-bold px-1.5 py-0.5 rounded uppercase">
                  {nivelSigilo === "SEGREDO_JUSTICA" ? "Sigilo" : "Restrito"}
                </span>
              ) : null}
              {prioridadeAtiva ? (
                <span className="bg-red-600 text-white text-[7px] font-bold px-1.5 py-0.5 rounded uppercase">
                  Prior.
                </span>
              ) : null}
              {prazoCriticoProximo ? (
                <span className="bg-orange-500 text-white text-[7px] font-bold px-1.5 py-0.5 rounded uppercase">
                  Prazo
                </span>
              ) : null}
            </div>

            {/* AÇÕES DESTRUTIVAS — separadas visualmente */}
            <div className="flex items-center gap-2 no-print pt-1.5 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  if (hubBridge?.openNuke) hubBridge.openNuke();
                }}
                disabled={!hubBridge?.isWorkspaceControlled}
                className="flex-1 px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
                title={
                  hubBridge?.isWorkspaceControlled
                    ? "Resetar e deletar este caderno permanentemente"
                    : "Abra uma peça para resetar"
                }
              >
                {/* Renomeado de "Reset" (22/08/2026): colidia de nome com
                    "Forçar Reset" do painel da peça, que é uma ação segura
                    (só limpa cache local) — este aqui apaga o caderno
                    inteiro pra sempre. Mesmo botão, mesma função, só texto
                    inequívoco. */}
                <i className="fas fa-rotate-left"></i> Excluir Caderno
              </button>

              <button
                type="button"
                onClick={() => {
                  if (typeof onRequestLegacyCleanup === "function")
                    onRequestLegacyCleanup();
                }}
                disabled={typeof onRequestLegacyCleanup !== "function"}
                className="flex-1 px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
                title="Apagar pastas legadas do Google Drive por ID"
              >
                <i className="fas fa-radiation"></i> Limpar Drive Legado
              </button>
            </div>

            {/* Status line — padronizado com outros motores */}
            <div className="flex items-center justify-between gap-2 text-[9px] font-semibold uppercase tracking-[0.04em] text-gray-500 pt-1">
              <span>Google Drive</span>
              <span
                className={`px-2 py-1 rounded-full border whitespace-nowrap ${
                  hubBridge?.driveFolderId
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : hubBridge
                      ? "border-red-200 bg-red-50 text-red-700"
                      : "border-gray-200 bg-gray-50 text-gray-600"
                }`}
              >
                {hubBridge?.driveFolderId
                  ? "Conectado"
                  : hubBridge
                    ? "Desconectado"
                    : "Aguardando peça"}
              </span>
            </div>
          </div>
        </div>
        ) : null}
      </aside>

      {/* ═══ DRAG HANDLE — Divisor redimensionável ═══════════════════════════ */}
      <DragHandle onMouseDown={handleResizeMouseDown} />

      {/* ═══ PAINEL CENTRAL — EDITOR (flex-1) — ÚNICO painel rolável ═════════ */}
      <main
        ref={mainScrollRef}
        data-testid="central-panel"
        className="flex-1 min-w-0 h-full flex flex-col bg-gray-50 overflow-y-auto"
      >
        {!activeWorkspace ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-md">
              <div className="text-6xl mb-4 opacity-15">📂</div>
              <div className="text-sm font-bold text-[#20243B] uppercase tracking-widest mb-2">
                Selecione um processo
              </div>
              <div className="text-xs text-gray-600 mb-4">
                Escolha um cliente e um caderno no painel esquerdo para abrir o
                Gerenciador de Anexos.
                {!selectedClientId
                  ? " Comece escolhendo um cliente do CRM."
                  : ""}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!selectedClientId) {
                    setIsClientPickerOpen(true);
                    return;
                  }
                  if (typeof onRequestNewCaderno === "function")
                    onRequestNewCaderno(selectedClientId);
                }}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C5A059] hover:bg-[#a8844a] text-white text-[11px] font-bold uppercase tracking-widest rounded shadow-sm transition-colors"
              >
                {!selectedClientId ? "👤 Escolher Cliente" : "＋ Novo Caderno"}
              </button>
            </div>
          </div>
        ) : activeViewDoc ? (
          <DocViewPanel
            doc={activeViewDoc}
            onClose={() => setActiveViewDoc(null)}
          />
        ) : (
          <>
            {/* MC41e (Fase F2): cabeçalho do CADERNO (Nível 2) — título/
                status/CNJ/abas — de volta pro <main>, ao lado da árvore, não
                mais esticado por cima dela (isso era o MC41d, corrigido a
                pedido do titular: 2 níveis hierárquicos diferentes não
                deveriam competir na mesma faixa horizontal). */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-300 shadow-sm px-4 py-3 shrink-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-sm font-bold text-[#20243B]">
                  {resolveProcessTitle(activeWorkspace).title}
                </h2>
                {/* MC53 (02/09/2026, pedido do titular): badge de status
                    removido daqui — já aparece na árvore lateral, no nó
                    deste mesmo caderno, visível na tela ao mesmo tempo
                    (linha ~2692). Duplicação confirmada antes de cortar,
                    mesmo princípio SSOT já aplicado no resto da Fase C. */}
                {/* MC53b (02/09/2026, pedido do titular): abas do caso
                    subiram pra dentro desta MESMA linha flex-wrap (título +
                    ícones), em vez de linha própria abaixo. Título deste
                    caderno é de comprimento variável (nomes de partes +
                    instituições) — quando não couber tudo numa linha só,
                    o flex-wrap existente empurra as abas pra uma 2ª linha
                    (mesmo mecanismo que já quebrava o título sozinho antes
                    desta mudança, não é regressão nova). */}
                <div className="flex gap-1">
                  {CASE_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-md border transition-colors ${
                        activeTab === tab.id
                          ? "border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]"
                          : "border-gray-200 text-gray-400 hover:text-gray-600"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {/* MC41f (Fase F2): Renomear/Vincular NPU/Excluir saíram da
                    árvore (Nível 1, virou só navegação) — são ações sobre
                    O CADERNO ABERTO (Nível 2), então moram aqui. */}
                <div className="flex items-center gap-1 ml-auto">
                  <button
                    type="button"
                    onClick={() => {
                      const wId = String(activeWorkspace?.id || "");
                      if (!wId) return;
                      setEditingNodeId(`caderno-${wId}`);
                      setEditingNodeValue(resolveCadernoLabel(activeWorkspace));
                    }}
                    className="px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    title="Renomear processo/caso"
                    data-testid="btn-renomear-caderno"
                  >
                    <i className="fas fa-pencil-alt text-[10px]"></i>
                  </button>
                  {typeof onRequestLinkProcess === "function" ? (
                    <button
                      type="button"
                      onClick={() => onRequestLinkProcess(String(activeWorkspace?.id || ""))}
                      className="px-2 py-1 rounded-md border border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100"
                      title="Vincular NPU (Processo)"
                      data-testid="btn-vincular-npu"
                    >
                      <i className="fas fa-link text-[10px]"></i>
                    </button>
                  ) : null}
                  {typeof onRequestDeleteWorkspace === "function" ? (
                    <button
                      type="button"
                      onClick={() => onRequestDeleteWorkspace(String(activeWorkspace?.id || ""))}
                      className="px-2 py-1 rounded-md border border-red-300 bg-red-50 text-red-600 hover:bg-red-100"
                      title="Excluir processo/caso"
                      data-testid="btn-excluir-caderno"
                    >
                      <i className="fas fa-trash-alt text-[10px]"></i>
                    </button>
                  ) : null}
                  {/* MC104n (12/09/2026): achado do titular — divisor sutil
                      antes do grupo de vínculo, pra separar visualmente
                      "ações sobre o caderno" (renomear/vincular NPU/
                      excluir) de "ações de vínculo entre cadernos"
                      (Principal/Apenso/Correlato), que antes eram 3
                      pílulas roxas idênticas soltas na mesma linha dos
                      outros 3 botões, sem nenhuma separação lógica. */}
                  <div
                    className="w-px h-5 bg-gray-200 mx-0.5"
                    aria-hidden="true"
                  />
                  {/* MC52 (Fase F2b): 3 ações de vínculo — agrupadas num só
                      bloco (MC104n) em vez de 3 botões separados com borda
                      própria cada um; mesmas 3 ações, mesmos ícones/
                      tooltips, só a moldura que virou 1 pílula com
                      divisores internos em vez de 3 pílulas repetidas. */}
                  <div className="flex items-center rounded-md border border-purple-300 bg-purple-50 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setLinkPickerType("principal")}
                      className="px-2 py-1 text-purple-600 hover:bg-purple-100 transition-colors"
                      title="Vincular a um Processo Principal (este é apenso dele)"
                      data-testid="btn-vincular-principal"
                    >
                      <i className="fas fa-sitemap text-[10px]"></i>
                    </button>
                    <div className="w-px h-4 bg-purple-200" aria-hidden="true" />
                    <button
                      type="button"
                      onClick={() => setLinkPickerType("apenso")}
                      className="px-2 py-1 text-purple-600 hover:bg-purple-100 transition-colors"
                      title="Adicionar Apenso (depende deste)"
                      data-testid="btn-adicionar-apenso"
                    >
                      <i className="fas fa-paperclip text-[10px]"></i>
                    </button>
                    <div className="w-px h-4 bg-purple-200" aria-hidden="true" />
                    <button
                      type="button"
                      onClick={() => setLinkPickerType("correlato")}
                      className="px-2 py-1 text-purple-600 hover:bg-purple-100 transition-colors"
                      title="Adicionar Correlato (relacionado, sem apenso formal)"
                      data-testid="btn-adicionar-correlato"
                    >
                      <i className="fas fa-code-branch text-[10px]"></i>
                    </button>
                  </div>
                </div>
              </div>
              {/* MC104n (12/09/2026): achado do titular — linha "Pré-
                  Processual · [PET_A]" (MC53) removida. Era redundante:
                  o nome do cliente já fica fixo no topo da barra lateral
                  o tempo todo (cabeçalho "[PET_A]" acima da árvore), e
                  "Pré-Processual" já aparece na própria linha do caderno
                  na árvore — mesmo raciocínio de SSOT que já tinha
                  derrubado o selo de status redundante deste cabeçalho. */}
            </div>

            <div ref={tabScrollRef} className="flex-1 min-h-0 p-4 overflow-y-auto">
              {activeTab === "caderno_autos" ? (
                <FileHubArea
                  activeWorkspace={activeWorkspace}
                  activePieceId={activePieceId}
                  initialFolderId={initialFolderId}
                  autoConnect={autoConnect}
                  onBack={() => setActivePecaDoc(null)}
                  onHostBridge={setHubBridge}
                  pieceActions={(() => {
                    const activePiece = savedPieces.find(
                      (p) => p.id === activePieceId,
                    );
                    if (!activePiece) return null;
                    return {
                      onRename: () => handleStartRenamePiece(activePiece),
                      onRemove: () => handleRemovePiece(activePiece.id),
                      onSendForApproval: () => {
                        approval.setApprovalModalPiece(activePiece);
                        approval.setApprovalNote("");
                        approval.approval.setApprovalNotice("");
                      },
                      onGenerateConsent: () =>
                        approval.setConsentBuilderPiece(activePiece),
                      onProtocolar: () => handleOpenProtocolarPiece(activePiece),
                    };
                  })()}
                />
              ) : activeTab === "resumo" ? (
                /* MC42 (Fase F2): 6 dos 10 acordeoes do antigo modal
                   "Metadados" vem pra ca (Informacoes Gerais, Portal do
                   Cliente, Modo Drive, Partes, Equipe do Caso, Sigilo) -
                   mesmos estados/funcoes de leitura e gravacao, so mudou o
                   contentor (modal -> aba). Prazos/Vinculos/Historico/
                   Tarefas ficam no modal por enquanto, vao para outras
                   abas ou para a Fase F2b em micro-commits futuros. */
                <div className="space-y-3 max-w-2xl">
                  {savingNotice ? (
                    <div className="bg-[#C5A059]/10 border border-[#C5A059] text-[#20243B] text-[11px] font-semibold px-3 py-1.5 rounded-md">
                      {savingNotice}
                    </div>
                  ) : null}
              {/* Acordeão 1 — Informações Gerais */}
              <SummaryCard
                icon="fa-circle-info"
                id="infoGerais"
                title="Informações Gerais"
                onEdit={!editingInfoGerais ? () => setEditingInfoGerais(true) : null}
              >
                {!editingInfoGerais ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Número do Processo
                        </span>
                        <div className="text-slate-800 font-mono">
                          {infoNumeroCNJ || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Status do Processo
                        </span>
                        <div className="text-slate-800">
                          {infoStatus || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Juízo / Vara
                        </span>
                        <div className="text-slate-800">
                          {infoJuizo || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Comarca / Foro
                        </span>
                        <div className="text-slate-800">
                          {infoComarca || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Classe Processual
                        </span>
                        <div className="text-slate-800">
                          {infoClasse || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Assunto Principal
                        </span>
                        <div className="text-slate-800">
                          {infoAssunto || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Ramo do Direito
                        </span>
                        <div className="text-slate-800">
                          {infoRamo || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Valor da Causa
                        </span>
                        <div className="text-slate-800">
                          {infoValorCausa
                            ? Number(infoValorCausa).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                            : "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Valor da Condenação
                        </span>
                        <div className="text-slate-800">
                          {infoValorCondenacao
                            ? Number(infoValorCondenacao).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
                            : "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Distribuído em
                        </span>
                        <div className="text-slate-800">
                          {infoDataDistribuicao
                            ? new Date(infoDataDistribuicao + "T12:00:00").toLocaleDateString("pt-BR")
                            : "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Data de Prescrição
                        </span>
                        <div className={`font-semibold ${infoDataPrescricao ? "text-red-600" : "text-slate-800"}`}>
                          {infoDataPrescricao
                            ? new Date(infoDataPrescricao + "T12:00:00").toLocaleDateString("pt-BR")
                            : "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Fase Processual
                        </span>
                        <div className="text-slate-800">
                          {infoFaseProcessual || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Objeto
                        </span>
                        <div className="text-slate-800 whitespace-pre-wrap">
                          {infoObjeto || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Número do Processo (CNJ)
                        </label>
                        <input
                          value={infoNumeroCNJ}
                          onChange={(e) => setInfoNumeroCNJ(e.target.value)}
                          placeholder="Ex: 0000000-00.0000.0.00.0000"
                          maxLength={60}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs font-mono focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Status do Processo
                        </label>
                        <select
                          value={infoStatus}
                          onChange={(e) => setInfoStatus(e.target.value)}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        >
                          {STATUS_COLUMNS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Juízo / Vara
                        </label>
                        <input
                          value={infoJuizo}
                          onChange={(e) => setInfoJuizo(e.target.value)}
                          placeholder="Ex: 3ª Vara Cível"
                          maxLength={120}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Comarca / Foro
                        </label>
                        <input
                          value={infoComarca}
                          onChange={(e) => setInfoComarca(e.target.value)}
                          placeholder="Ex: Curitiba/PR"
                          maxLength={120}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Classe Processual
                        </label>
                        <input
                          value={infoClasse}
                          onChange={(e) => setInfoClasse(e.target.value)}
                          placeholder="Ex: Procedimento Comum Cível"
                          maxLength={120}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Assunto Principal
                        </label>
                        <input
                          value={infoAssunto}
                          onChange={(e) => setInfoAssunto(e.target.value)}
                          placeholder="Ex: Aposentadoria por Invalidez"
                          maxLength={120}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Ramo do Direito (vírgulas)
                        </label>
                        <input
                          value={infoRamo}
                          onChange={(e) => setInfoRamo(e.target.value)}
                          placeholder="Ex: CIVIL, PREVIDENCIARIO"
                          maxLength={200}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Valor da Causa (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={infoValorCausa}
                          onChange={(e) => setInfoValorCausa(e.target.value)}
                          placeholder="0,00"
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Valor da Condenação (R$)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={infoValorCondenacao}
                          onChange={(e) => setInfoValorCondenacao(e.target.value)}
                          placeholder="0,00"
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Distribuído em
                        </label>
                        <input
                          type="date"
                          value={infoDataDistribuicao}
                          onChange={(e) => setInfoDataDistribuicao(e.target.value)}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Data de Prescrição
                        </label>
                        <input
                          type="date"
                          value={infoDataPrescricao}
                          onChange={(e) => setInfoDataPrescricao(e.target.value)}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Fase Processual
                        </label>
                        <select
                          value={infoFaseProcessual}
                          onChange={(e) => setInfoFaseProcessual(e.target.value)}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        >
                          <option value="">— Não informado —</option>
                          <option value="Pré-Processual">Pré-Processual</option>
                          <option value="Conhecimento">Conhecimento</option>
                          <option value="Recursal">Recursal</option>
                          <option value="Execução">Execução</option>
                          <option value="Cumprimento de Sentença">Cumprimento de Sentença</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Objeto
                        </label>
                        <textarea
                          value={infoObjeto}
                          onChange={(e) => setInfoObjeto(e.target.value)}
                          placeholder="Descrição livre do processo"
                          maxLength={2000}
                          rows={3}
                          className="w-full px-2 py-1.5 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none resize-y"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingInfoGerais(false);
                          setInfoNumeroCNJ(String(activeWorkspace?.numeroCNJ || ""));
                          setInfoStatus(String(activeWorkspace?.status || "Ativo"));
                          setInfoJuizo(String(activeWorkspace?.juizo || ""));
                          setInfoComarca(
                            String(activeWorkspace?.comarca || ""),
                          );
                          setInfoClasse(
                            String(activeWorkspace?.metadata?.classe || ""),
                          );
                          setInfoAssunto(
                            String(activeWorkspace?.metadata?.assunto || ""),
                          );
                          const vc = activeWorkspace?.metadata?.valorCausa;
                          setInfoValorCausa(vc === null || vc === undefined ? "" : String(vc));
                          const vcond = activeWorkspace?.metadata?.valorCondenacao;
                          setInfoValorCondenacao(vcond === null || vcond === undefined ? "" : String(vcond));
                          setInfoDataDistribuicao(
                            String(activeWorkspace?.metadata?.dataDistribuicao || ""),
                          );
                          setInfoObjeto(
                            String(activeWorkspace?.metadata?.objeto || ""),
                          );
                          setInfoDataPrescricao(
                            String(activeWorkspace?.metadata?.dataPrescricao || ""),
                          );
                        }}
                        className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700 px-3 py-1"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveInfoGerais}
                        className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#C5A059] hover:bg-[#a8844a] px-3 py-1 rounded"
                      >
                        Salvar
                      </button>
                    </div>
                  </div>
                )}
              </SummaryCard>

              {/* Dados Criminais (22/08/2026) — 10 campos achados na tela
                  real do Projudi sem lugar nenhum no schema até agora
                  (Efeito Suspensivo, Custas, Julgado 2º Grau, Penhora no
                  Rosto, Réu Preso, Data da Prisão, Data do Fato, Delegacia,
                  RAI, Inquérito/Nº SSP — ver
                  AGENT_INGESTAO_DADOS_PROCESSO.md §4.2). Card à parte, só
                  aparece quando o ramo é Criminal — não fazem sentido em
                  Cível/Trabalhista/etc. */}
              {/Criminal/i.test(infoRamo) ? (
                <SummaryCard
                  icon="fa-gavel"
                  id="dados-criminais"
                  title="Dados Criminais"
                  onEdit={
                    !editingDadosCriminais
                      ? () => setEditingDadosCriminais(true)
                      : undefined
                  }
                >
                  {!editingDadosCriminais ? (
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Réu Preso
                        </span>
                        <div className={`font-semibold ${infoReuPreso ? "text-red-600" : "text-slate-800"}`}>
                          {infoReuPreso === null ? "—" : infoReuPreso ? "Sim" : "Não"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Data da Prisão
                        </span>
                        <div className="text-slate-800">
                          {infoDataDaPrisao
                            ? new Date(infoDataDaPrisao + "T12:00:00").toLocaleDateString("pt-BR")
                            : "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Data do Fato
                        </span>
                        <div className="text-slate-800">
                          {infoDataDoFato
                            ? new Date(infoDataDoFato + "T12:00:00").toLocaleDateString("pt-BR")
                            : "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Efeito Suspensivo
                        </span>
                        <div className="text-slate-800">
                          {infoEfeitoSuspensivo === null ? "—" : infoEfeitoSuspensivo ? "Sim" : "Não"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Julgado 2º Grau
                        </span>
                        <div className="text-slate-800">
                          {infoJulgado2Grau === null ? "—" : infoJulgado2Grau ? "Sim" : "Não"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Custas
                        </span>
                        <div className="text-slate-800">{infoCustas || "—"}</div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Penhora no Rosto
                        </span>
                        <div className="text-slate-800">
                          {infoPenhoraNoRosto === null ? "—" : infoPenhoraNoRosto ? "Sim" : "Não"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Delegacia
                        </span>
                        <div className="text-slate-800">{infoDelegacia || "—"}</div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          RAI
                        </span>
                        <div className="text-slate-800">{infoRai || "—"}</div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Inquérito / Nº SSP
                        </span>
                        <div className="text-slate-800">
                          {infoInqueritoNumeroSSP || "—"}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Réu Preso
                          </label>
                          <select
                            value={
                              infoReuPreso === null ? "" : infoReuPreso ? "sim" : "nao"
                            }
                            onChange={(e) =>
                              setInfoReuPreso(
                                e.target.value === ""
                                  ? null
                                  : e.target.value === "sim",
                              )
                            }
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          >
                            <option value="">— Não informado —</option>
                            <option value="sim">Sim</option>
                            <option value="nao">Não</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Data da Prisão
                          </label>
                          <input
                            type="date"
                            value={infoDataDaPrisao}
                            onChange={(e) => setInfoDataDaPrisao(e.target.value)}
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Data do Fato
                          </label>
                          <input
                            type="date"
                            value={infoDataDoFato}
                            onChange={(e) => setInfoDataDoFato(e.target.value)}
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Efeito Suspensivo
                          </label>
                          <select
                            value={
                              infoEfeitoSuspensivo === null ? "" : infoEfeitoSuspensivo ? "sim" : "nao"
                            }
                            onChange={(e) =>
                              setInfoEfeitoSuspensivo(
                                e.target.value === ""
                                  ? null
                                  : e.target.value === "sim",
                              )
                            }
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          >
                            <option value="">— Não informado —</option>
                            <option value="sim">Sim</option>
                            <option value="nao">Não</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Julgado 2º Grau
                          </label>
                          <select
                            value={
                              infoJulgado2Grau === null ? "" : infoJulgado2Grau ? "sim" : "nao"
                            }
                            onChange={(e) =>
                              setInfoJulgado2Grau(
                                e.target.value === ""
                                  ? null
                                  : e.target.value === "sim",
                              )
                            }
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          >
                            <option value="">— Não informado —</option>
                            <option value="sim">Sim</option>
                            <option value="nao">Não</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Custas
                          </label>
                          <input
                            value={infoCustas}
                            onChange={(e) => setInfoCustas(e.target.value)}
                            placeholder="Ex.: Isento"
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Penhora no Rosto
                          </label>
                          <select
                            value={
                              infoPenhoraNoRosto === null ? "" : infoPenhoraNoRosto ? "sim" : "nao"
                            }
                            onChange={(e) =>
                              setInfoPenhoraNoRosto(
                                e.target.value === ""
                                  ? null
                                  : e.target.value === "sim",
                              )
                            }
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          >
                            <option value="">— Não informado —</option>
                            <option value="sim">Sim</option>
                            <option value="nao">Não</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Delegacia
                          </label>
                          <input
                            value={infoDelegacia}
                            onChange={(e) => setInfoDelegacia(e.target.value)}
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            RAI
                          </label>
                          <input
                            value={infoRai}
                            onChange={(e) => setInfoRai(e.target.value)}
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Inquérito / Nº SSP
                          </label>
                          <input
                            value={infoInqueritoNumeroSSP}
                            onChange={(e) => setInfoInqueritoNumeroSSP(e.target.value)}
                            className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => setEditingDadosCriminais(false)}
                          className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700 px-3 py-1"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveDadosCriminais}
                          className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#C5A059] hover:bg-[#a8844a] px-3 py-1 rounded"
                        >
                          Salvar
                        </button>
                      </div>
                    </div>
                  )}
                </SummaryCard>
              ) : null}

              {/* Acordeão 2 — Partes */}
              <SummaryCard
                icon="fa-users"
                id="partes"
                title="Partes"
              >
                {/* REESTRUTURADO (24/08/2026, 4ª revisão) — taxonomia de 6
                    categorias aprovada pelo titular (CPC art. 17/18, art.
                    149/159, tela oficial do PJe/TJMG): 5 seções processuais
                    (Polo Ativo, Polo Passivo, Terceiro Interessado,
                    Auxiliar da Justiça, Vítima/Ofendido — `POLO_PROCESSUAL`
                    em PosicoesProcessuais.js) + 1 seção não-processual à
                    parte ("Quem Defendemos" — objeto do processo/bem
                    jurídico tutelado sem personalidade jurídica, ex.:
                    animal; sentinela `POLO_NAO_PROCESSUAL`, fora do array
                    processual de propósito). "É nosso cliente ou não"
                    continua existindo como dado (badge "Cliente"/
                    "Representamos" abaixo) e como base de edição (2
                    formulários, CRM vs. manual) — só não é mais o
                    agrupamento visual principal. */}
                {!editingCoPartes && !editingPartes ? (
                  <div className="space-y-3 mb-3">
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => setEditingCoPartes(true)}
                        className="text-[10px] font-bold uppercase tracking-widest text-[#C5A059] hover:text-[#20243B]"
                      >
                        ✎ Editar Representados
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingPartes(true)}
                        className="text-[10px] font-bold uppercase tracking-widest text-[#C5A059] hover:text-[#20243B]"
                      >
                        ✎ Editar Outros
                      </button>
                    </div>
                    {(() => {
                      // Hoisted (24/08/2026) para fora do loop de polo — antes
                      // era recalculado a cada uma das 3 (agora 6) iterações
                      // sem necessidade, já que não depende de `polo`.
                      const clientItems = coParties.map((p) => {
                        const isMainClient =
                          String(p?.clientId || "").trim() ===
                          String(selectedClientId || "").trim();
                        const resolvedName =
                          isMainClient && selectedClientName
                            ? selectedClientName
                            : p?.name || p?.clientId;
                        return {
                          isClient: true,
                          nome: resolvedName,
                          sub: p?.role || "",
                          polo: p?.polo || inferPoloFallback(p?.role),
                          tipo: null,
                        };
                      });
                      const adversaItems = partesAdversas.map((row) => ({
                        isClient: false,
                        nome: row?.nome || "—",
                        sub: row?.qualificacao?.posicao || "",
                        polo:
                          row?.polo ||
                          inferPoloFallback(row?.qualificacao?.posicao),
                        tipo: row?.tipo || "PF",
                        _raw: row,
                      }));
                      const allItems = [...clientItems, ...adversaItems];

                      // Renderiza uma seção (linha de itens filtrados por
                      // polo) — reaproveitada pelas 5 seções processuais E
                      // pela 6ª seção não-processual, só trocando os rótulos
                      // do badge/checkbox ("Cliente"/"Nosso cliente" vs.
                      // "Representamos"/"Representamos").
                      const renderSecaoPolo = (polo, label, opts = {}) => {
                        const { clientBadge = "Cliente", checkboxLabel = "Nosso cliente" } =
                          opts;
                        const items = allItems.filter(
                          (it) => it.polo === polo,
                        );
                        return (
                          <div key={polo}>
                            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                              {label}
                            </div>
                            {items.length === 0 ? (
                              <div className="text-[11px] text-gray-400 italic bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                                Ninguém registrado.
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {items.map((it, idx) => (
                                  <div
                                    key={idx}
                                    className={`flex items-start gap-2 rounded px-2 py-1.5 text-xs border ${
                                      it.isClient
                                        ? "bg-[#C5A059]/5 border-[#C5A059]/30"
                                        : "bg-gray-50 border-gray-200"
                                    }`}
                                  >
                                    {it.tipo ? (
                                      <span className="text-[9px] font-bold bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded shrink-0">
                                        {it.tipo}
                                      </span>
                                    ) : null}
                                    <div className="min-w-0 flex-1">
                                      <div className="font-semibold text-slate-800 truncate">
                                        {it.nome}
                                      </div>
                                      {it.sub ? (
                                        <div
                                          className={`text-[10px] mt-0.5 ${it.isClient ? "text-[#C5A059] font-semibold" : "text-gray-500"}`}
                                        >
                                          {it.sub}
                                        </div>
                                      ) : null}
                                    </div>
                                    {it.isClient ? (
                                      <span className="text-[8px] font-bold uppercase tracking-widest text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 rounded px-1.5 py-0.5 shrink-0">
                                        {clientBadge}
                                      </span>
                                    ) : !isAdversaEntePublico(it._raw) ? (
                                      // MC_PARTES_B (24/08/2026): seleção manual
                                      // "nosso cliente"/"representamos" só nas
                                      // partes REAIS de PF/PJ (nunca em ente
                                      // oficial — MP, Estado, etc., excluídos
                                      // acima). Rótulo de texto sempre junto do
                                      // checkbox (nunca só cor) e sempre
                                      // visível (nunca só-hover) — Art.
                                      // acessibilidade.
                                      <label className="flex items-center gap-1 shrink-0 cursor-pointer select-none">
                                        <input
                                          type="checkbox"
                                          checked={coParties.some(
                                            (p) =>
                                              normalizeSearchText(
                                                p?.name || p?.nome || "",
                                              ) ===
                                              normalizeSearchText(it.nome),
                                          )}
                                          onChange={() =>
                                            handleToggleAdversaCliente(it._raw)
                                          }
                                          className="h-3.5 w-3.5 accent-[#C5A059] cursor-pointer"
                                        />
                                        <span className="text-[8px] font-bold uppercase tracking-widest text-gray-500">
                                          {checkboxLabel}
                                        </span>
                                      </label>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      };

                      return (
                        <>
                          {POLO_PROCESSUAL.map((polo) =>
                            renderSecaoPolo(polo, polo),
                          )}
                          {/* 6ª seção — NÃO-PROCESSUAL, separada de propósito
                              das 5 acima (não entra no loop de
                              POLO_PROCESSUAL nem na doutrina de CPC). Exclusiva
                              para entidades sem personalidade jurídica que o
                              escritório efetivamente representa (ex.: animal —
                              STJ não reconhece personalidade jurídica a
                              animal, então nunca pode estar em
                              Polo/Terceiro/Auxiliar). Todo item aqui já veio
                              de `partiesRefs` (é, por definição, "nosso
                              cliente"/representado) — o rótulo muda para
                              "Representamos" em vez de "Cliente"/"Nosso
                              cliente", mas o mecanismo é o mesmo. */}
                          <div className="pt-1 border-t border-dashed border-gray-200">
                            {renderSecaoPolo(
                              POLO_NAO_PROCESSUAL,
                              "Quem Defendemos (não-processual)",
                              {
                                clientBadge: "Representamos",
                                checkboxLabel: "Representamos",
                              },
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                ) : null}

                {/* MC_Z2/Z3: edição inline (CRM) — só a EDIÇÃO fica gated
                    por editingCoPartes; a exibição de leitura vive só no
                    bloco unificado acima. */}
                <div className="mb-3">
                  {editingCoPartes ? (
                    <div className="space-y-2">
                      {coPartesEdit.map((row, idx) => (
                        <div
                          key={idx}
                          className="space-y-1 bg-gray-50 border border-gray-200 rounded p-1.5"
                        >
                          <div className="grid grid-cols-12 gap-1 items-center">
                            <select
                              value={row?.role || "Parte"}
                              onChange={(e) =>
                                handleChangeCoParteRow(
                                  idx,
                                  "role",
                                  e.target.value,
                                )
                              }
                              className="col-span-3 h-7 px-1 rounded border border-gray-300 bg-white text-[10px]"
                            >
                              {/* FONTE ÚNICA (22/08/2026): antes eram 8 opções
                                  soltas, sem nenhuma posição de Direito Animal
                                  (ex.: Fiel Depositário) — agora lê da mesma
                                  lista usada em Procurações e Contratos
                                  (src/config/PosicoesProcessuais.js).
                                  Agrupado por ramo via <optgroup>. */}
                              {Object.keys(POSICOES_POR_RAMO).map((ramoKey) => (
                                <optgroup
                                  key={ramoKey}
                                  label={RAMO_TAG_MAP[ramoKey] || ramoKey}
                                >
                                  {POSICOES_POR_RAMO[ramoKey].map((posicao) => (
                                    <option key={posicao} value={posicao}>
                                      {posicao}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                              {/* Art. 149 CPC — auxiliar da justiça nunca é
                                  "parte" (Autor/Réu); grupo à parte, não
                                  amarrado a ramo do direito. */}
                              <optgroup label="Auxiliares da Justiça (Art. 149 CPC)">
                                {POSICOES_AUXILIARES_JUSTICA.map((posicao) => (
                                  <option key={posicao} value={posicao}>
                                    {posicao}
                                  </option>
                                ))}
                              </optgroup>
                            </select>
                            <input
                              value={row?.name || ""}
                              onChange={(e) =>
                                handleChangeCoParteRow(
                                  idx,
                                  "name",
                                  e.target.value,
                                )
                              }
                              placeholder="Nome"
                              className="col-span-8 h-7 px-2 rounded border border-gray-300 bg-white text-[10px]"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveCoParteRow(idx)}
                              className="col-span-1 h-7 flex items-center justify-center text-red-400 hover:text-red-600 text-[14px]"
                            >
                              ×
                            </button>
                          </div>
                          {/* Polo processual (22/08/2026, padrão PJe/CNJ) —
                              eixo independente da posição/papel (o QUE ela
                              é: Autor, Réu, Depositário...), já que "nosso
                              cliente" (estar aqui, em vez de Outros
                              Envolvidos) não implica polo nenhum em
                              específico. */}
                          <select
                            value={
                              row?.polo || inferPoloFallback(row?.role)
                            }
                            onChange={(e) =>
                              handleChangeCoParteRow(
                                idx,
                                "polo",
                                e.target.value,
                              )
                            }
                            className="w-full h-6 px-1 rounded border border-gray-200 bg-white text-[9px] text-gray-500"
                            title="Polo processual (padrão PJe/CNJ) ou categoria não-processual"
                          >
                            {POLO_PROCESSUAL.map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                            {/* 6ª categoria — NÃO-PROCESSUAL (24/08/2026),
                                fora da doutrina de CPC (Art. 149/159, 17/18):
                                "Quem Defendemos" — objeto do processo/bem
                                jurídico tutelado sem personalidade jurídica
                                (ex.: animal). Separada visualmente por
                                <optgroup> das 5 categorias processuais
                                acima. */}
                            <optgroup label="Não-processual (controle interno)">
                              <option value={POLO_NAO_PROCESSUAL}>
                                Quem Defendemos (não-processual)
                              </option>
                            </optgroup>
                          </select>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setIsCoPartePickerOpen(true)}
                          className="flex-1 text-[10px] font-bold uppercase tracking-widest text-[#C5A059] hover:text-[#20243B] border border-dashed border-[#C5A059]/40 hover:border-[#C5A059] rounded py-1.5 transition-colors"
                        >
                          + Co-parte (CRM)
                        </button>
                        <button
                          type="button"
                          onClick={handleAddCoParteRow}
                          className="text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-600 border border-dashed border-gray-300 hover:border-gray-400 rounded py-1.5 px-2 transition-colors"
                          title="Adicionar manualmente"
                        >
                          + Manual
                        </button>
                      </div>
                      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingCoPartes(false);
                            const raw = Array.isArray(
                              activeWorkspace?.partiesRefs,
                            )
                              ? activeWorkspace.partiesRefs
                              : [];
                            setCoPartesEdit(
                              raw.filter((p) => p?.clientId || p?.name),
                            );
                          }}
                          className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700 px-3 py-1"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveCoPartes}
                          className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#C5A059] hover:bg-[#a8844a] px-3 py-1 rounded"
                        >
                          Salvar
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div>
                  {/* Leitura antiga removida (22/08/2026, 3ª revisão) — a
                      exibição agora vive só no bloco unificado por polo,
                      acima. Só a EDIÇÃO fica aqui, gated por
                      editingPartes. */}
                  {editingPartes ? (
                    <div className="space-y-2">
                      {partesAdversas.map((row, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="grid grid-cols-12 gap-1 items-center bg-gray-50 border border-gray-200 rounded p-1.5">
                            <select
                              value={row?.tipo || "PF"}
                              onChange={(e) =>
                                handleChangeAdversa(idx, "tipo", e.target.value)
                              }
                              className="col-span-3 h-7 px-1 rounded border border-gray-300 bg-white text-[10px]"
                            >
                              <option value="PF">PF</option>
                              <option value="PJ">PJ</option>
                              <option value="ORGAO">Órgão</option>
                            </select>
                            <input
                              value={row?.nome || ""}
                              onChange={(e) =>
                                handleChangeAdversa(idx, "nome", e.target.value)
                              }
                              placeholder="Nome / Razão social"
                              className="col-span-8 h-7 px-2 rounded border border-gray-300 bg-white text-[11px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveAdversa(idx)}
                              className="col-span-1 h-7 text-[10px] font-bold text-red-500 hover:text-red-700"
                            >
                              ✕
                            </button>
                            <input
                              value={row?.documento || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "documento",
                                  e.target.value,
                                )
                              }
                              placeholder="CPF/CNPJ (opcional)"
                              className="col-span-12 h-7 px-2 rounded border border-gray-300 bg-white text-[11px] font-mono focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            {/* Polo processual (22/08/2026, padrão PJe/CNJ):
                                estar nesta lista (não é nosso cliente) não
                                significa ser litigante adverso — ex.: o
                                Ministério Público num Inquérito Policial é
                                Polo Ativo/Autoridade, não "parte contra" o
                                nosso cliente. */}
                            <select
                              value={
                                row?.polo ||
                                inferPoloFallback(row?.qualificacao?.posicao)
                              }
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "polo",
                                  e.target.value,
                                )
                              }
                              className="col-span-12 h-6 px-1 rounded border border-gray-200 bg-white text-[9px] text-gray-500"
                              title="Polo processual (padrão PJe/CNJ) ou categoria não-processual"
                            >
                              {POLO_PROCESSUAL.map((n) => (
                                <option key={n} value={n}>
                                  {n}
                                </option>
                              ))}
                              {/* 6ª categoria — NÃO-PROCESSUAL (24/08/2026) —
                                  ver comentário equivalente no <select> de
                                  co-partes acima. */}
                              <optgroup label="Não-processual (controle interno)">
                                <option value={POLO_NAO_PROCESSUAL}>
                                  Quem Defendemos (não-processual)
                                </option>
                              </optgroup>
                            </select>
                          </div>
                          {/* Qualificação completa — sempre visível no modo edição (P17) */}
                          <div className="grid grid-cols-2 gap-1.5 bg-amber-50/60 border border-amber-200 rounded p-2">
                            <input
                              value={row?.qualificacao?.nacionalidade || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.nacionalidade",
                                  e.target.value,
                                )
                              }
                              placeholder="Nacionalidade"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.estado_civil || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.estado_civil",
                                  e.target.value,
                                )
                              }
                              placeholder="Estado civil"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.profissao || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.profissao",
                                  e.target.value,
                                )
                              }
                              placeholder="Profissão"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.rg || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.rg",
                                  e.target.value,
                                )
                              }
                              placeholder="RG"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] font-mono focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.orgao_expedidor || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.orgao_expedidor",
                                  e.target.value,
                                )
                              }
                              placeholder="Órgão expedidor"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.endereco || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.endereco",
                                  e.target.value,
                                )
                              }
                              placeholder="Endereço"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.bairro || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.bairro",
                                  e.target.value,
                                )
                              }
                              placeholder="Bairro"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.cidade || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.cidade",
                                  e.target.value,
                                )
                              }
                              placeholder="Cidade"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.estado || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.estado",
                                  e.target.value,
                                )
                              }
                              placeholder="Estado"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.cep || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.cep",
                                  e.target.value,
                                )
                              }
                              placeholder="CEP"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] font-mono focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.email || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.email",
                                  e.target.value,
                                )
                              }
                              placeholder="E-mail"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                            <input
                              value={row?.qualificacao?.telefone || ""}
                              onChange={(e) =>
                                handleChangeAdversa(
                                  idx,
                                  "qualificacao.telefone",
                                  e.target.value,
                                )
                              }
                              placeholder="Telefone"
                              className="h-7 px-2 rounded border border-gray-300 bg-white text-[10px] focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                            />
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddAdversa}
                        className="w-full text-[10px] font-bold uppercase tracking-widest text-[#C5A059] hover:text-[#20243B] border border-dashed border-[#C5A059]/40 hover:border-[#C5A059] rounded py-1.5 transition-colors"
                      >
                        + Parte Adversa
                      </button>
                      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingPartes(false);
                            const raw = activeWorkspace?.partes?.adversas;
                            setPartesAdversas(Array.isArray(raw) ? raw : []);
                          }}
                          className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700 px-3 py-1"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleSavePartes}
                          className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#C5A059] hover:bg-[#a8844a] px-3 py-1 rounded"
                        >
                          Salvar
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </SummaryCard>

              {/* Acordeão 3 — Sigilo / Prioridade */}
              <SummaryCard
                icon="fa-shield-halved"
                id="sigilo"
                title="Sigilo / Prioridade"
                alert={sigiloAlerta}
                onEdit={!editingSigilo ? () => setEditingSigilo(true) : null}
              >
                {!editingSigilo ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Nível de Sigilo
                        </span>
                        <div
                          className={`font-semibold ${nivelSigilo !== "PUBLICO" ? "text-red-600" : "text-slate-800"}`}
                        >
                          {nivelSigilo === "SEGREDO_JUSTICA"
                            ? "Segredo de Justiça"
                            : nivelSigilo === "RESTRITO"
                              ? "Restrito"
                              : "Público"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Prioridade
                        </span>
                        <div
                          className={`font-semibold ${prioridadeAtiva ? "text-red-600" : "text-slate-800"}`}
                        >
                          {prioridadeAtiva
                            ? prioridadeMotivo || "Ativa"
                            : "Sem prioridade"}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                        Nível de Sigilo
                      </label>
                      <select
                        value={nivelSigilo}
                        onChange={(e) => setNivelSigilo(e.target.value)}
                        className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                      >
                        <option value="PUBLICO">Público</option>
                        <option value="SEGREDO_JUSTICA">
                          Segredo de Justiça
                        </option>
                        <option value="RESTRITO">Restrito</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="checkbox"
                        id="prioridadeChkModal"
                        checked={prioridadeAtiva}
                        onChange={(e) => setPrioridadeAtiva(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#C5A059] focus:ring-[#C5A059]"
                      />
                      <label
                        htmlFor="prioridadeChkModal"
                        className="text-[11px] font-semibold text-slate-700"
                      >
                        Prioridade legal
                      </label>
                    </div>
                    {prioridadeAtiva ? (
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Motivo
                        </label>
                        <input
                          value={prioridadeMotivo}
                          onChange={(e) => setPrioridadeMotivo(e.target.value)}
                          placeholder="Ex: Idoso 60+ · PCD · Reu preso"
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                          maxLength={200}
                        />
                      </div>
                    ) : null}
                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingSigilo(false);
                          setNivelSigilo(
                            String(
                              activeWorkspace?.nivelSigilo || "PUBLICO",
                            ).toUpperCase(),
                          );
                          setPrioridadeAtiva(
                            Boolean(activeWorkspace?.prioridade?.ativa),
                          );
                          setPrioridadeMotivo(
                            String(activeWorkspace?.prioridade?.motivo || ""),
                          );
                        }}
                        className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700 px-3 py-1"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveSigilo}
                        className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#C5A059] hover:bg-[#a8844a] px-3 py-1 rounded"
                      >
                        Salvar
                      </button>
                    </div>
                  </div>
                )}
              </SummaryCard>
              {/* Acordeão 2.5 — Equipe do Caso (Art.327) */}
              <SummaryCard
                icon="fa-user-tie"
                id="caseTeam"
                title="Equipe do Caso"
                onEdit={!editingCaseTeam ? () => setEditingCaseTeam(true) : null}
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                      Advogados Vinculados
                    </div>
                  </div>
                  {!editingCaseTeam ? (
                    caseTeamEdit.length === 0 ? (
                      <div className="text-[11px] text-gray-500 italic bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                        Nenhum advogado vinculado.
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {caseTeamEdit.map((member, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-[11px]"
                          >
                            <span
                              className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${member?.role === "LEAD" ? "bg-[#C5A059] text-white" : "bg-gray-200 text-gray-700"}`}
                            >
                              {ROLE_LABELS[member?.role] ||
                                member?.role ||
                                "Membro"}
                            </span>
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-slate-800 truncate block">
                                {member?.name || "—"}
                              </span>
                              {member?.oab ? (
                                <span className="text-[10px] text-gray-500">
                                  {member.oab}
                                </span>
                              ) : null}
                            </div>
                            {member?.whatsappLink ? (
                              <a
                                href={member.whatsappLink}
                                target="_blank"
                                rel="noreferrer"
                                className="text-green-600 hover:text-green-800 shrink-0"
                                title="WhatsApp"
                              >
                                <i className="fab fa-whatsapp text-sm" />
                              </a>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )
                  ) : (
                    <div className="space-y-2">
                      {caseTeamEdit.map((member, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded p-1.5"
                        >
                          <select
                            value={member?.role || "ASSOCIATE"}
                            onChange={(e) =>
                              handleChangeCaseTeamRole(idx, e.target.value)
                            }
                            className="h-7 px-1 rounded border border-gray-300 bg-white text-[10px] shrink-0"
                          >
                            {Object.entries(ROLE_LABELS).map(([k, v]) => (
                              <option key={k} value={k}>
                                {v}
                              </option>
                            ))}
                          </select>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-semibold text-slate-800 truncate">
                              {member?.name || "—"}
                            </div>
                            {member?.oab ? (
                              <div className="text-[9px] text-gray-500">
                                {member.oab}
                              </div>
                            ) : null}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveCaseTeamRow(idx)}
                            className="h-7 w-7 text-[10px] font-bold text-red-500 hover:text-red-700 shrink-0 flex items-center justify-center"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setIsCaseTeamPickerOpen(true)}
                        className="w-full text-[10px] font-bold uppercase tracking-widest text-[#C5A059] hover:text-[#20243B] border border-dashed border-[#C5A059]/40 hover:border-[#C5A059] rounded py-1.5 transition-colors"
                      >
                        + Advogado (CRM)
                      </button>
                      <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingCaseTeam(false);
                            const raw = Array.isArray(activeWorkspace?.caseTeam)
                              ? activeWorkspace.caseTeam
                              : [];
                            setCaseTeamEdit(
                              raw.length > 0
                                ? raw
                                : [
                                    {
                                      ...TITULAR_SEED,
                                      addedAt: new Date().toISOString(),
                                    },
                                  ],
                            );
                          }}
                          className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700 px-3 py-1"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveCaseTeam}
                          className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#C5A059] hover:bg-[#a8844a] px-3 py-1 rounded"
                        >
                          Salvar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </SummaryCard>

              {/* Acordeão 1.5 — Status no Portal do Cliente */}
              <SummaryCard
                icon="fa-user-check"
                id="portalStatus"
                title="Portal do Cliente"
                onEdit={!editingPortalStatus ? () => setEditingPortalStatus(true) : null}
              >
                {!editingPortalStatus ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 gap-2 text-xs">
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Etapa do Caso
                        </span>
                        <div className="text-slate-800">
                          {CASE_STAGE_OPTIONS.find(
                            (o) => o.value === infoCaseStage,
                          )?.label || "—"}
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Próximo Evento
                        </span>
                        <div className="text-slate-800">
                          {infoNextEventLabel || "—"}
                        </div>
                      </div>
                      {infoNextEventDate ? (
                        <div className="flex items-baseline gap-2">
                          <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                            Data / Hora
                          </span>
                          <div className="text-slate-800">
                            {new Date(infoNextEventDate).toLocaleString(
                              "pt-BR",
                              {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </div>
                        </div>
                      ) : null}
                      {infoNextEventLocation ? (
                        <div className="flex items-baseline gap-2">
                          <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                            Local
                          </span>
                          <div className="text-slate-800">
                            {infoNextEventLocation}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Etapa do Caso
                        </label>
                        <select
                          value={infoCaseStage}
                          onChange={(e) => setInfoCaseStage(e.target.value)}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        >
                          {CASE_STAGE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Nome do Próximo Evento
                        </label>
                        <input
                          value={infoNextEventLabel}
                          onChange={(e) =>
                            setInfoNextEventLabel(e.target.value)
                          }
                          placeholder="Ex: Audiência de Instrução"
                          maxLength={120}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Data e Hora
                        </label>
                        <input
                          type="datetime-local"
                          value={infoNextEventDate}
                          onChange={(e) => setInfoNextEventDate(e.target.value)}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Local (opcional)
                        </label>
                        <input
                          value={infoNextEventLocation}
                          onChange={(e) =>
                            setInfoNextEventLocation(e.target.value)
                          }
                          placeholder="Ex: Fórum Central — 1ª Vara"
                          maxLength={120}
                          className="h-8 w-full px-2 rounded border border-gray-300 bg-gray-50 text-xs focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => setEditingPortalStatus(false)}
                        className="text-[10px] font-bold uppercase tracking-widest text-gray-500 hover:text-gray-700 px-3 py-1"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleSavePortalStatus}
                        className="text-[10px] font-bold uppercase tracking-widest text-white bg-[#C5A059] hover:bg-[#a8844a] px-3 py-1 rounded"
                      >
                        Salvar
                      </button>
                    </div>
                  </div>
                )}
              </SummaryCard>

              {/* Acordeão 1.7 — Google Drive (Matter-Centric Fase 2) */}
              <SummaryCard
                icon="fa-brands fa-google-drive"
                id="driveMode"
                title="Google Drive"
              >
                <div className="space-y-2">
                  <div className="grid grid-cols-1 gap-2 text-xs">
                    <div className="flex items-baseline gap-2">
                      <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                        Modo de Armazenamento
                      </span>
                      <div
                        className={`font-semibold ${isMatterCentric ? "text-emerald-700" : "text-amber-700"}`}
                      >
                        {isMatterCentric
                          ? "📁 Pasta de Processo (Matter-Centric)"
                          : "📁 Pasta do Cliente (Client-Centric)"}
                      </div>
                    </div>
                    {isMatterCentric &&
                    activeWorkspace?.matterDriveFolderUrl ? (
                      <div className="flex items-baseline gap-2">
                        <span className="font-bold text-gray-600 uppercase text-[10px] tracking-wider w-40 shrink-0">
                          Pasta no Drive
                        </span>
                        <div>
                          <a
                            href={activeWorkspace.matterDriveFolderUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-[11px] font-semibold inline-flex items-center gap-1"
                          >
                            <i className="fab fa-google-drive text-[10px]" />{" "}
                            Abrir pasta do processo
                          </a>
                        </div>
                      </div>
                    ) : null}
                    {!isMatterCentric ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-1">
                        <div className="text-[11px] font-semibold text-amber-800 mb-1">
                          <i className="fas fa-exclamation-triangle text-[10px] mr-1" />
                          {coParties.length > 1
                            ? "Este caderno tem múltiplas partes mas usa pasta individual"
                            : "Usando pasta do cliente"}
                        </div>
                        <div className="text-[10px] text-amber-700 mb-2">
                          {coParties.length > 1
                            ? "Migre para uma pasta de processo compartilhada para que todos os clientes vinculados possam acessar os mesmos documentos."
                            : "Para processos com múltiplos clientes, recomenda-se migrar para uma pasta de processo compartilhada."}
                        </div>
                        <button
                          type="button"
                          onClick={handleMigrateToMatterDrive}
                          disabled={migratingDrive}
                          className="w-full px-3 py-2 rounded-lg font-bold text-[11px] uppercase tracking-widest border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          {migratingDrive ? (
                            <>
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-emerald-300 border-t-emerald-800" />{" "}
                              Criando pasta...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-folder-plus text-[10px]" />{" "}
                              Migrar para Pasta de Processo
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-1">
                        <div className="text-[11px] font-semibold text-emerald-800">
                          <i className="fas fa-check-circle text-[10px] mr-1" />
                          Documentos deste caderno são salvos em uma pasta
                          neutra (PROCESSOS/).
                        </div>
                        <div className="text-[10px] text-emerald-700 mt-1">
                          Todos os clientes vinculados compartilham a mesma
                          pasta de documentos.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </SummaryCard>

                </div>
              ) : activeTab === "caderno_trabalho" ? (
                /* Fase B (PRE_VOO_LIVRO_PROCESSUAL_GERADO_20260901.md):
                   aba própria pro leitor do CADERNO_<...>.md — reverte a
                   Decisão "e" do Pré-Voo, decisão revista ao vivo com o
                   titular 02/09/2026 (preferiu aba a botão+modal). */
                <div className="h-full max-w-3xl mx-auto">
                  {/* MC (15/09/2026, pedido do titular): replicar "Nova
                      Janela" do Caderno de Autos ("A Peça em Si") pro
                      Caderno de Trabalho — mesmo padrão de pop-out real
                      (rota própria + window.open), ver
                      CadernoTrabalhoStandaloneViewer.jsx. "Abrir no Drive"
                      já vinha de graça (header interno do
                      CadernoMarkdownViewer); os dois ficam na MESMA linha
                      porque o botão é passado como prop pro próprio
                      componente, não desenhado numa linha separada aqui. */}
                  <CadernoMarkdownViewer
                    driveWebViewLink={activeWorkspace?.cadernoMdWebViewLink}
                    onOpenNovaJanela={() => {
                      const wId = String(activeWorkspace?.id || "");
                      if (!wId) return;
                      window.open(
                        `/caderno-trabalho/${wId}`,
                        `caderno_trabalho_${wId}`,
                        "width=900,height=800,left=100,top=100",
                      );
                    }}
                  />
                </div>
              ) : activeTab === "vinculos" ? (
                /* MC58 (redesenho v3, 23/08/2026 — usuário pediu simetria
                   estrita depois do MC56/MC57): a tentativa anterior usava
                   CSS Grid com align-items:stretch (altura de cada card
                   dependendo do conteúdo do vizinho mais alto) — funciona
                   bem quando há 1 item por coluna, mas quebra a garantia de
                   "tudo do mesmo tamanho" assim que uma coluna empilha mais
                   de 1 apenso (a coluna vizinha estica pra acompanhar,
                   ficando artificialmente alta) e deixava a seta um pouco
                   fora do centro vertical do card. Confirmado por pesquisa
                   de acessibilidade (mesma data): regra nº 1 de fluxograma
                   pra dislexia/autismo é "toda forma do mesmo tamanho, com o
                   mesmo espaçamento, alinhadas uniformemente" — não
                   "aproximadamente do mesmo tamanho".
                   Troca: NODE_W/NODE_H/LABEL_H abaixo são constantes fixas
                   em pixel, iguais pra TODO card (Principal/Este Caderno/
                   Apenso/Correlato) — nenhum depende do conteúdo do vizinho.
                   Os conectores horizontais reservam um espaçador do mesmo
                   tamanho do rótulo+gap (LABEL_H+GAP) antes de desenhar a
                   linha, pra o centro da linha cair exatamente no centro
                   vertical do card ao lado — matemática, não "chute" via
                   items-center num grid de alturas desiguais. Texto que
                   ultrapasse o card fixo usa line-clamp (nunca corta o dado
                   de forma invisível — title= no botão sempre carrega o
                   texto completo). IDs (não CNJ mais, ver MC50) resolvidos
                   contra allWorkspaces. */
                (() => {
                  const findById = (id) =>
                    (Array.isArray(allWorkspaces) ? allWorkspaces : []).find(
                      (w) => w.id === id,
                    );

                  // MC64 (23/08/2026): pedido do titular — não importa qual
                  // processo da família você está vendo (raiz, apenso,
                  // apenso de apenso...), a aba sempre resolve até a raiz de
                  // verdade (sobe por processoPrincipalId até não achar mais
                  // principal, com guarda contra ciclo — dado poderia estar
                  // mal vinculado) e desenha a árvore inteira a partir dali
                  // (desce recursivamente por apensos). Unifica "quem é meu
                  // principal" e "quem são meus apensos" numa coisa só: a
                  // árvore inteira construída pela mesma aresta (apensos) —
                  // linkProcessRelation (MC50) grava dos dois lados, então
                  // se X.processoPrincipalId == Y, Y.apensos já contém X, e
                  // subir até a raiz + descer por apensos reconstrói a
                  // família completa sem precisar de 2 lógicas separadas.
                  const findRoot = (workspace) => {
                    const visited = new Set();
                    let current = workspace;
                    while (current?.processoPrincipalId && !visited.has(current.id)) {
                      visited.add(current.id);
                      const parent = findById(current.processoPrincipalId);
                      if (!parent || visited.has(parent.id)) break;
                      current = parent;
                    }
                    return current;
                  };

                  const buildTree = (workspace, visited) => {
                    visited.add(workspace.id);
                    const apensoIds = Array.isArray(workspace?.apensos) ? workspace.apensos : [];
                    const children = apensoIds
                      .map(findById)
                      .filter(Boolean)
                      .filter((child) => !visited.has(child.id))
                      .map((child) => buildTree(child, visited));
                    return { workspace, children };
                  };

                  // MC71 (24/08/2026): achado real do titular — abrir o
                  // 5619870 (uma folha isolada, sem apenso nenhum, só um
                  // correlato) invertia os rótulos: ele virava "Processo
                  // Principal" e o 5813599 (a família de verdade, com 4
                  // apensos) virava "Correlato". Correlato não tem
                  // hierarquia (MC67) — quem é "Principal" não deveria
                  // depender de qual dos dois processos você abriu agora,
                  // senão o mesmo par de processos troca de papel dependendo
                  // do clique. Regra: se a MINHA linhagem não tem apenso
                  // nenhum (raiz sem família própria) mas o correlato dela
                  // TEM apenso, o lado com família de verdade vira o
                  // Principal/direita — o outro (a folha isolada) vira
                  // Correlato/esquerda, sempre, não importa qual dos dois
                  // está aberto.
                  const myRoot = activeWorkspace ? findRoot(activeWorkspace) : null;
                  let rootWorkspace = myRoot;
                  if (myRoot && (!Array.isArray(myRoot.apensos) || myRoot.apensos.length === 0)) {
                    const myRootCorrelatoIds = Array.isArray(myRoot.processosCorrelatos)
                      ? myRoot.processosCorrelatos
                      : [];
                    const richerCorrelato = myRootCorrelatoIds
                      .map((id) => findById(id))
                      .filter(Boolean)
                      .find((c) => Array.isArray(c.apensos) && c.apensos.length > 0);
                    if (richerCorrelato) {
                      rootWorkspace = richerCorrelato;
                    }
                  }
                  const tree = rootWorkspace ? buildTree(rootWorkspace, new Set()) : null;

                  // MC69 (24/08/2026): pedido do titular — pensar como
                  // árvore genealógica. Processo Principal e Processo
                  // Correlato são como duas famílias diferentes que se
                  // conectam (ex.: casamento) — cada um mantém a PRÓPRIA
                  // linhagem de apensos. Tudo derivado do Principal vai pra
                  // DIREITA (já existia); tudo derivado do Correlato vai
                  // pra ESQUERDA, como árvore espelhada, usando a MESMA
                  // função de layout (buildTree/assignLayout), só com
                  // direção invertida — não é um conceito novo, é o mesmo
                  // motor rodando pro outro lado. Corrige o MC68, que só
                  // mostrava o correlato como uma caixa solta (sem a própria
                  // linhagem dele).
                  const rootCorrelatos = (
                    Array.isArray(rootWorkspace?.processosCorrelatos)
                      ? rootWorkspace.processosCorrelatos
                      : []
                  )
                    .map((id) => findById(id))
                    .filter(Boolean);

                  // Constantes fixas em pixel — a MESMA pra todo card, sem
                  // exceção. NODE_W/NODE_H definem o retângulo do card,
                  // LABEL_H+GAP_LABEL é o espaço ocupado pelo rótulo acima
                  // (usado pelos conectores pra centralizar a linha no meio
                  // do card, não no meio do bloco card+rótulo).
                  const NODE_W = 260;
                  const NODE_H = 234;
                  const LABEL_H = 20;
                  const GAP_LABEL = 8;
                  // MC72 (24/08/2026): 36px dava conta das setas pequenas
                  // originais, mas depois que aumentei o tamanho da seta
                  // (MC70, base de 16px) quase não sobrava espaço pra linha
                  // visível entre a espinha e a ponta — resultado: várias
                  // pontas de seta espremidas quase no mesmo lugar, lendo
                  // como "empilhado" em vez de linhas distintas (achado real
                  // do titular). 64px dá espaço de sobra pro tronco, pra
                  // linha do ramo E pra ponta da seta, sem espremer nada.
                  const CONNECTOR_W = 64;

                  // Fatias internas fixas do card — MC59: não bastava o
                  // card de fora ter altura fixa, o divisor interno
                  // "flutuava" porque vinha logo depois do nome, e nome de
                  // 1 linha vs. 2 linhas empurrava tudo abaixo pra uma
                  // altura diferente em cada card. Cada campo abaixo ocupa
                  // uma fatia de altura fixa (reservada mesmo que o texto
                  // seja curto ou o campo esteja vazio) — é isso que garante
                  // o divisor (e tudo mais) na MESMA posição vertical em
                  // TODO card, não só o contorno externo.
                  const ROW_LABEL_H = 16;
                  const ROW_NAME_H = 40;
                  const ROW_GAP_H = 8;
                  const ROW_DIVIDER_H = 18;
                  const ROW_STATUS_H = 20;
                  const ROW_CLASSE_H = 20;
                  const ROW_GAP_SMALL_H = 4;
                  const ROW_CASE_STATUS_H = 20;

                  // Estilo de TRAÇO da borda por tipo de vínculo (categoria,
                  // sem hierarquia de valor entre si — por isso não leva
                  // cor própria, só o traço: sólido/tracejado/pontilhado,
                  // igual ao conector). MC62 (23/08/2026): pesquisado contra
                  // Astrea (padrão que este Portal já usa como referência,
                  // ver formatters.js), PJe e Projudi — nenhum dos três
                  // colore o card por "isso é o principal / isso é apenso".
                  // A Astrea mostra o apenso como link de texto simples; o
                  // PJe reserva cor pra sigilo, não pra tipo de vínculo. Sem
                  // convenção externa pra copiar, a cor da borda passou a
                  // vir do STATUS do processo (ver STATUS_STYLES abaixo) —
                  // decisão do usuário, ciente de que a maioria "Ativo"
                  // deixa quase tudo verde; quem carrega a categoria agora é
                  // só o rótulo + o traço da borda/conector.
                  // MC65 (23/08/2026): correção de semântica — o titular
                  // apontou que "apenso" é vínculo DIRETO (processo anexado
                  // a outro pra julgamento conjunto, ligação forte), não
                  // "mais fraco" que o Principal. A raiz e qualquer apenso
                  // dela (em qualquer profundidade) fazem parte da MESMA
                  // relação direta — só o Correlato (mesmo autor/fato, mas
                  // processo diferente, sem anexação formal) é indireto.
                  // Sólido = direto (Principal/Apenso/Este Caderno, mesma
                  // coisa nesse eixo); pontilhado = só Correlato.
                  const VARIANT_STYLES = {
                    principal: "border-solid",
                    apenso: "border-solid",
                    correlato: "border-dotted",
                  };

                  // MC79 (10/09/2026): STATUS_STYLES/getStatusStyle saíram
                  // daqui — viraram helper compartilhado em
                  // src/utils/formatters.js, reaproveitado também na árvore
                  // lateral de Processos (mesma cor por status nos 2
                  // lugares, sem duplicar). Import já no topo do arquivo.

                  // Padrão fixo de linhas — MESMO bloco pra qualquer card,
                  // inclusive "Este Caderno" e cadernos pré-processuais
                  // (MC57: usuário pediu explicitamente "mesmo modelo do
                  // processo principal" pros outros dois — antes um card
                  // pré-processual pulava direto pro título livre/alias só
                  // por não ter numeroCNJ ainda, mesmo quando clientName/
                  // partes.adversas já existiam preenchidos). Nunca inventa
                  // Autor/Réu quando não há polo estruturado: cai pra rótulo
                  // genérico e real (Cliente / Parte Adversa) em vez de
                  // chutar quem é quem; e nunca fatia o texto livre do alias
                  // pra fingir 2 partes — se não há adversas/clientName
                  // estruturados, cai numa única linha "Processo" com o
                  // título como está (alias/nome), sem inventar dado.
                  const resolvePartesLinhas = (workspace) => {
                    const w = workspace || {};
                    const {
                      title,
                      subtitle,
                      isPreProcessual,
                      numeroFormatted,
                      poloAtivoNome,
                      poloPassivoNome,
                    } = resolveProcessTitle(w);
                    const adversas = Array.isArray(w?.partes?.adversas) ? w.partes.adversas : [];
                    const clientName = String(w.clientName || "").trim();
                    const advName = adversas.length > 0 ? String(adversas[0]?.nome || "").trim() : "";

                    let linha1Label = "Processo";
                    let linha1Nome = title;
                    let linha2Label = "";
                    let linha2Nome = "";
                    if (poloAtivoNome && poloPassivoNome) {
                      // Reaproveita o mesmo cálculo de resolveProcessTitle
                      // (já cobre o fallback via partiesRefs quando o Polo
                      // Ativo é a própria parte representada, não uma
                      // "adversa") — evita 2 lógicas divergentes.
                      linha1Label = "Polo Ativo";
                      linha1Nome = poloAtivoNome;
                      linha2Label = "Polo Passivo";
                      linha2Nome = poloPassivoNome;
                    } else if (clientName && advName) {
                      linha1Label = "Cliente";
                      linha1Nome = clientName;
                      linha2Label = "Parte Adversa";
                      linha2Nome = advName;
                    }

                    const classeOuAssunto =
                      String(w?.metadata?.classe || "").trim() ||
                      String(w?.metadata?.assunto || "").trim();

                    // Status do Processo (Ativo/Suspenso/Recurso/Liminar/
                    // Arquivado) — mesmo campo/mesmo default "Ativo" que a
                    // aba Resumo (infoStatus, ~L1542/L4424) já usava.
                    // CORRIGIDO 13/09/2026: este comentário afirmava (errado)
                    // que ProcessosCadernoListView.jsx também já usava "Ativo"
                    // — na verdade usava 'Sem status', achado do titular
                    // comparando as duas telas do mesmo workspace lado a
                    // lado. Alinhado lá também (STATUS_COLUMNS[0] = "Ativo"),
                    // agora os 3 lugares que leem w.status concordam.
                    const statusProcesso = String(w?.status || "Ativo").trim();

                    return {
                      linha1Label,
                      linha1Nome,
                      linha2Label,
                      linha2Nome,
                      numero: isPreProcessual ? "" : (numeroFormatted || ""),
                      statusBadge: isPreProcessual ? subtitle : "",
                      classeOuAssunto,
                      statusProcesso,
                    };
                  };

                  // Cada campo é uma fatia de altura FIXA (ROW_*_H), reservada
                  // mesmo vazia ou com texto curto — é isso que faz o divisor
                  // (e todo o resto) cair na mesma posição em qualquer card,
                  // em vez de "flutuar" conforme o nome tem 1 ou 2 linhas.
                  // line-clamp é só a trava de segurança pro texto nunca
                  // estourar a fatia — o texto completo continua no title=
                  // do botão (MC54/MC55: nunca esconder o dado sem
                  // alternativa).
                  // CSS Grid com linhas em pixel fixo, não divs empilhadas
                  // com height individual — MC60: mesmo a versão anterior
                  // (cada campo com style={{height}}) devendo somar certo
                  // no papel, o usuário reportou o divisor ainda saindo em
                  // alturas diferentes. Grid com gridTemplateRows explícito
                  // é o jeito mais direto de garantir isso — cada uma das 8
                  // linhas abaixo tem EXATAMENTE a altura da lista, e como é
                  // a mesma lista/grid pra todo card, não tem conta pra
                  // errar: linha 4 (Polo Passivo, label) cai sempre na
                  // mesma posição em pixel absoluto, ponto.
                  const PARTY_ROW_HEIGHTS = [
                    ROW_LABEL_H,
                    ROW_NAME_H,
                    ROW_GAP_H,
                    ROW_LABEL_H,
                    ROW_NAME_H,
                    ROW_DIVIDER_H,
                    ROW_STATUS_H,
                    ROW_CLASSE_H,
                    ROW_GAP_SMALL_H,
                    ROW_CASE_STATUS_H,
                  ];

                  const PartyLines = ({ info }) => (
                    <div
                      className="grid w-full h-full"
                      style={{ gridTemplateRows: PARTY_ROW_HEIGHTS.map((h) => `${h}px`).join(" ") }}
                    >
                      <div className="text-xs font-bold uppercase tracking-wide text-gray-600 leading-4 overflow-hidden">
                        {info.linha1Label}
                      </div>
                      <div className="overflow-hidden">
                        <div className="text-sm font-bold text-[#20243B] leading-snug break-words line-clamp-2">
                          {info.linha1Nome}
                        </div>
                      </div>
                      <div />
                      <div className="text-xs font-bold uppercase tracking-wide text-gray-600 leading-4 overflow-hidden">
                        {info.linha2Label}
                      </div>
                      <div className="overflow-hidden">
                        <div className="text-sm font-bold text-[#20243B] leading-snug break-words line-clamp-2">
                          {info.linha2Nome}
                        </div>
                      </div>
                      <div className="flex items-center">
                        <div className="w-full border-t-2 border-gray-300" />
                      </div>
                      <div className="flex items-center overflow-hidden">
                        {info.numero ? (
                          <div className="text-xs text-gray-800 font-mono font-semibold leading-snug break-words truncate">
                            {info.numero}
                          </div>
                        ) : info.statusBadge ? (
                          <div className="inline-block text-xs font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                            {info.statusBadge}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center overflow-hidden">
                        {info.classeOuAssunto ? (
                          <div className="text-xs text-gray-700 leading-snug break-words line-clamp-1">
                            {info.classeOuAssunto}
                          </div>
                        ) : null}
                      </div>
                      <div />
                      <div className="flex items-center overflow-hidden">
                        {(() => {
                          const statusStyle = getStatusStyle(info.statusProcesso);
                          return (
                            <div
                              className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase px-2 py-0.5 rounded-full ${statusStyle.badge}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusStyle.dot}`} aria-hidden="true" />
                              {info.statusProcesso}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );

                  // MC69: NodeBox/GroupLabel (cards planos fora da árvore,
                  // usados antes só pro correlato solto) saíram — todo card
                  // agora é um TreeCard, incluindo os de correlato, já que
                  // o correlato virou raiz da própria arvorezinha em vez de
                  // uma lista solta. Rótulo do correlato também virou um
                  // rótulo por card (ver TreeCard), não mais um GroupLabel
                  // único acima de uma fileira.

                  // MC64/MC69: layout recursivo da árvore — cada nó ganha
                  // uma coluna (nível de profundidade a partir da raiz) e
                  // uma posição vertical (centerY), calculada de baixo pra
                  // cima (nó-folha ocupa 1 "fatia" = NODE_H; nó com filhos
                  // ocupa a soma das fatias dos filhos e fica centralizado
                  // entre o primeiro e o último filho — convenção padrão de
                  // desenho de árvore). `direction` (+1/-1) é o que permite
                  // reusar a MESMA função pros apensos do Principal (direita)
                  // e pros apensos do(s) Correlato(s) (esquerda, árvore
                  // espelhada — MC69, pedido do titular: "árvore genealógica",
                  // cada família com a própria linhagem).
                  const SIBLING_GAP = LABEL_H + GAP_LABEL;
                  const countLeaves = (node) => {
                    if (node._leaves != null) return node._leaves;
                    node._leaves =
                      node.children.length === 0
                        ? 1
                        : node.children.reduce((sum, c) => sum + countLeaves(c), 0);
                    return node._leaves;
                  };
                  const assignLayout = (node, col, yTop, direction) => {
                    node.col = col;
                    countLeaves(node);
                    node.subtreeH = node._leaves * NODE_H + (node._leaves - 1) * SIBLING_GAP;
                    if (node.children.length === 0) {
                      node.centerY = yTop + NODE_H / 2;
                    } else {
                      let cursor = yTop;
                      node.children.forEach((child) => {
                        assignLayout(child, col + direction, cursor, direction);
                        cursor += child.subtreeH + SIBLING_GAP;
                      });
                      const firstY = node.children[0].centerY;
                      const lastY = node.children[node.children.length - 1].centerY;
                      node.centerY = (firstY + lastY) / 2;
                    }
                  };
                  const flattenTree = (node, out) => {
                    out.push(node);
                    node.children.forEach((c) => flattenTree(c, out));
                    return out;
                  };

                  let treeNodes = [];
                  let correlatoTrees = [];
                  let correlatoNodes = [];
                  let canvasWidth = 0;
                  let canvasHeight = 0;
                  let xOffset = 0;
                  const correlatoRootIds = new Set();

                  if (tree) {
                    assignLayout(tree, 0, 0, 1);
                    treeNodes = flattenTree(tree, []);

                    // MC69: cada Correlato vira raiz da PRÓPRIA árvore,
                    // construída com o mesmo buildTree usado pra raiz
                    // principal (reaproveita apensos reais do correlato, se
                    // existirem), empilhados como "irmãos" e depois
                    // recentralizados na altura da raiz principal (o ponto
                    // de conexão, tipo um "casamento" entre 2 linhagens).
                    correlatoTrees = rootCorrelatos.map((c) =>
                      buildTree(c, new Set([rootWorkspace.id])),
                    );
                    correlatoTrees.forEach((ct) => correlatoRootIds.add(ct.workspace.id));
                    if (correlatoTrees.length > 0) {
                      let cursor = 0;
                      correlatoTrees.forEach((ct) => {
                        assignLayout(ct, -1, cursor, -1);
                        cursor += ct.subtreeH + SIBLING_GAP;
                      });
                      const totalH = cursor - SIBLING_GAP;
                      const shiftY = tree.centerY - totalH / 2;
                      const shiftNode = (node) => {
                        node.centerY += shiftY;
                        node.children.forEach(shiftNode);
                      };
                      correlatoTrees.forEach(shiftNode);
                      correlatoNodes = correlatoTrees.flatMap((ct) => flattenTree(ct, []));
                    }

                    const allNodes = [...treeNodes, ...correlatoNodes];
                    const minCol = Math.min(0, ...allNodes.map((n) => n.col));
                    const maxCol = Math.max(0, ...allNodes.map((n) => n.col));
                    // 28px de folga no topo pro rótulo do card mais alto —
                    // mesma ideia do TOP_PAD antigo, só que agora calculada
                    // depois de saber a extensão real (principal + correlato
                    // podem crescer em direções/alturas diferentes).
                    const minY = Math.min(...allNodes.map((n) => n.centerY - NODE_H / 2)) - 28;
                    const maxY = Math.max(...allNodes.map((n) => n.centerY + NODE_H / 2));
                    allNodes.forEach((n) => {
                      n.centerY -= minY;
                    });

                    xOffset = -minCol * (NODE_W + CONNECTOR_W);
                    canvasWidth = (maxCol - minCol + 1) * NODE_W + (maxCol - minCol) * CONNECTOR_W;
                    canvasHeight = maxY - minY;
                  }

                  // Zoom simples: encolhe a árvore inteira proporcionalmente
                  // se ela for mais larga que um teto generoso — pedido do
                  // titular ("pode diminuir proporcionalmente pra caber").
                  // Não é medição real do container (exigiria
                  // ResizeObserver, fora de escopo aqui) — é um teto fixo
                  // suficiente pra maioria dos casos reais; famílias muito
                  // largas ainda contam com o overflow-x-auto do wrapper.
                  const MAX_CANVAS_W = 1400;
                  const scale = canvasWidth > MAX_CANVAS_W ? MAX_CANVAS_W / canvasWidth : 1;

                  // Botão de desvincular só existe pra vizinho DIRETO do
                  // caderno aberto agora — handleUnlinkProcessRelation opera
                  // relativo a activeWorkspace, então mostrar "×" num neto/
                  // sobrinho (2+ níveis de distância) chamaria a função com
                  // um relationType errado (ele não é apenso DIRETO deste
                  // caderno, é apenso de outro apenso). Mesma regra vale pro
                  // correlato: só retorna "correlato" quando o vínculo está
                  // de fato no array do PRÓPRIO activeWorkspace.
                  const directRelationToActive = (node) => {
                    if (!activeWorkspace) return null;
                    if (node.workspace.id === activeWorkspace.processoPrincipalId) return "principal";
                    const apensoIds = Array.isArray(activeWorkspace.apensos) ? activeWorkspace.apensos : [];
                    if (apensoIds.includes(node.workspace.id)) return "apenso";
                    const correlatoIds = Array.isArray(activeWorkspace.processosCorrelatos)
                      ? activeWorkspace.processosCorrelatos
                      : [];
                    if (correlatoIds.includes(node.workspace.id)) return "correlato";
                    return null;
                  };

                  const TreeCard = ({ node }) => {
                    const isActive = node.workspace.id === activeWorkspace?.id;
                    const isRoot = node === tree;
                    const isCorrelatoRoot = correlatoRootIds.has(node.workspace.id);
                    const variant = isRoot ? "principal" : isCorrelatoRoot ? "correlato" : "apenso";
                    const info = resolvePartesLinhas(node.workspace);
                    const statusStyle = getStatusStyle(info.statusProcesso);
                    const relationType = directRelationToActive(node);
                    // MC70 (24/08/2026): achado do titular — quando o
                    // caderno aberto agora É a raiz ou um correlato, o
                    // rótulo virava "Este Caderno" e APAGAVA "Processo
                    // Principal"/"Correlato". São 2 informações diferentes
                    // (qual é o PAPEL na família + qual é o caderno aberto
                    // agora) e uma não deveria sumir por causa da outra.
                    // Papel sempre aparece, por texto; "estar aberto agora"
                    // vira destaque visual (anel dourado na borda), nunca
                    // mais apagando o nome do papel.
                    const roleLabel = isRoot ? "Processo Principal" : isCorrelatoRoot ? "Correlato" : "Apenso";
                    const roleColor = isRoot ? "text-purple-700" : isCorrelatoRoot ? "text-amber-700" : "text-blue-700";
                    return (
                      <div
                        style={{
                          position: "absolute",
                          left: xOffset + node.col * (NODE_W + CONNECTOR_W),
                          top: node.centerY - NODE_H / 2,
                          width: NODE_W,
                          height: NODE_H,
                        }}
                        className={`group flex flex-col text-left px-4 py-3 rounded-xl border-[3px] shadow-md hover:shadow-lg transition-shadow ${VARIANT_STYLES[variant]} ${statusStyle.border} ${statusStyle.bg} ${isActive ? "ring-4 ring-[#C5A059] ring-offset-2" : ""}`}
                      >
                        {/* MC66/MC69/MC70: todo card precisa de rótulo de
                            papel (Processo Principal/Correlato/Apenso),
                            sempre visível, independente de ser ou não o
                            caderno aberto agora — "Este Caderno" deixou de
                            ser texto que substitui o papel e virou só um
                            sufixo dourado, mais o anel na borda. */}
                        <div className={`absolute -top-6 left-0 text-xs font-bold uppercase tracking-wide whitespace-nowrap ${isActive ? "text-[#C5A059]" : roleColor}`}>
                          {roleLabel}
                          {isActive ? " · Este Caderno" : ""}
                        </div>
                        {relationType ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlinkProcessRelation(relationType, node.workspace.id);
                            }}
                            title="Desvincular este processo"
                            aria-label="Desvincular este processo"
                            className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-white border-2 border-gray-400 text-gray-600 hover:bg-red-50 hover:border-red-500 hover:text-red-600 text-sm leading-none flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity z-10"
                          >
                            ×
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => onSwitchWorkspace?.(node.workspace)}
                          title={`${info.linha1Nome}${info.linha2Nome ? ` vs. ${info.linha2Nome}` : ""} — ${info.numero || info.statusBadge || ""}`}
                          className="flex-1 w-full h-full text-left overflow-hidden"
                        >
                          <PartyLines info={info} />
                        </button>
                      </div>
                    );
                  };

                  // Conectores: 1 tronco saindo de cada nó-pai até uma
                  // espinha vertical, e 1 ramo por filho — SÓLIDO (MC65:
                  // apenso é vínculo direto, não "mais fraco" que o
                  // Principal — em qualquer profundidade da árvore é a
                  // mesma relação de anexação, por isso a mesma linha
                  // sólida usada pro Principal). MC69: generalizada com
                  // `dir` (+1 direita, pro lado do Principal; -1 esquerda,
                  // pro lado de cada Correlato) — é a MESMA matemática,
                  // só espelhada; nenhuma lógica nova, só reaproveitada.
                  // Geometria vem 100% de node.col/centerY — mesma fonte de
                  // verdade do card, não uma conta paralela.
                  const renderForksForNodes = (nodesList, dir) =>
                    nodesList
                      .filter((node) => node.children.length > 0)
                      .map((node) => {
                        const edgeX = xOffset + node.col * (NODE_W + CONNECTOR_W) + (dir === 1 ? NODE_W : 0);
                        const sourceY = node.centerY;
                        const spineX = edgeX + dir * (CONNECTOR_W / 2);
                        const targetEdgeX = edgeX + dir * (CONNECTOR_W - 1);
                        const arrowBaseX = targetEdgeX - dir * 16;
                        const childYs = node.children.map((c) => c.centerY);
                        const minY = Math.min(sourceY, ...childYs);
                        const maxY = Math.max(sourceY, ...childYs);
                        return (
                          <g key={node.workspace.id}>
                            {/* MC73 (24/08/2026): linha do fluxograma
                                grossa demais (6px) perto da borda fina dos
                                cards (border-[3px]) — achado real do
                                titular, quer as duas na mesma espessura. */}
                            {node.children.length > 1 ? (
                              <line x1={spineX} y1={minY} x2={spineX} y2={maxY} stroke="#6b7280" strokeWidth="3" />
                            ) : null}
                            <line x1={edgeX} y1={sourceY} x2={spineX} y2={sourceY} stroke="#6b7280" strokeWidth="3" />
                            {node.children.map((child) => (
                              <g key={child.workspace.id}>
                                <line x1={spineX} y1={child.centerY} x2={arrowBaseX} y2={child.centerY} stroke="#6b7280" strokeWidth="3" />
                                <polygon
                                  points={`${arrowBaseX},${child.centerY - 9} ${arrowBaseX},${child.centerY + 9} ${targetEdgeX},${child.centerY}`}
                                  fill="#6b7280"
                                />
                              </g>
                            ))}
                          </g>
                        );
                      });

                  // Vínculo Principal↔Correlato: o "casamento" entre as 2
                  // famílias — pontilhado e bidirecional (MC67: correlato
                  // não tem hierarquia, nenhum lado manda no outro, seta só
                  // numa ponta sugeriria direção que não existe). Mesmo
                  // desenho de tronco+espinha+ramos do renderForksForNodes,
                  // adaptado pra ter 2 pontas com seta em vez de 1.
                  const renderCorrelatoLink = () => {
                    if (correlatoTrees.length === 0) return null;
                    const rootEdgeX = xOffset + tree.col * (NODE_W + CONNECTOR_W);
                    const rootY = tree.centerY;
                    const spineX = rootEdgeX - CONNECTOR_W / 2;
                    const targetEdgeX = rootEdgeX - (CONNECTOR_W - 1);
                    const childYs = correlatoTrees.map((ct) => ct.centerY);
                    const minY = Math.min(rootY, ...childYs);
                    const maxY = Math.max(rootY, ...childYs);
                    return (
                      <g>
                        {correlatoTrees.length > 1 ? (
                          <line x1={spineX} y1={minY} x2={spineX} y2={maxY} stroke="#b45309" strokeWidth="3" strokeDasharray="3 8" />
                        ) : null}
                        <line x1={rootEdgeX - 16} y1={rootY} x2={spineX} y2={rootY} stroke="#b45309" strokeWidth="3" strokeDasharray="3 8" />
                        <polygon
                          points={`${rootEdgeX - 16},${rootY - 9} ${rootEdgeX - 16},${rootY + 9} ${rootEdgeX},${rootY}`}
                          fill="#b45309"
                        />
                        {correlatoTrees.map((ct) => (
                          <g key={ct.workspace.id}>
                            <line x1={spineX} y1={ct.centerY} x2={targetEdgeX + 16} y2={ct.centerY} stroke="#b45309" strokeWidth="3" strokeDasharray="3 8" />
                            <polygon
                              points={`${targetEdgeX + 16},${ct.centerY - 9} ${targetEdgeX + 16},${ct.centerY + 9} ${targetEdgeX},${ct.centerY}`}
                              fill="#b45309"
                            />
                          </g>
                        ))}
                      </g>
                    );
                  };

                  const hasAnyVinculo = treeNodes.length > 1 || rootCorrelatos.length > 0;

                  return (
                    <div className="flex-1 py-8 px-2 overflow-x-auto">
                      {!hasAnyVinculo ? (
                        <div className="text-sm font-semibold text-gray-700">
                          Nenhum vínculo ainda — use os botões no cabeçalho
                          acima para vincular.
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          {tree ? (
                            <div style={{ width: canvasWidth * scale, height: canvasHeight * scale }}>
                              <div
                                style={{
                                  width: canvasWidth,
                                  height: canvasHeight,
                                  transform: `scale(${scale})`,
                                  transformOrigin: "top left",
                                  position: "relative",
                                }}
                              >
                                <svg
                                  width={canvasWidth}
                                  height={canvasHeight}
                                  className="absolute top-0 left-0 pointer-events-none"
                                  aria-hidden="true"
                                >
                                  {renderForksForNodes(treeNodes, 1)}
                                  {renderForksForNodes(correlatoNodes, -1)}
                                  {renderCorrelatoLink()}
                                </svg>

                                {[...treeNodes, ...correlatoNodes].map((node) => (
                                  <TreeCard key={node.workspace.id} node={node} />
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : activeTab === "atividades" ? (
                /* MC43 (Fase F2): Prazos/Audiencias + Tarefas saem do
                   modal Metadados e vem pra aba Atividades - mesmos
                   estados/funcoes de sempre, so mudou o contentor
                   (modal -> aba), mesmo padrao SummaryCard do Resumo. */
                <div className="space-y-3 max-w-2xl">
              <SummaryCard
                icon="fa-calendar-days"
                id="prazos"
                title="Prazos / Audiências"
                alert={prazoCriticoProximo}
                summary={
                  prazosList.length === 0 && audienciasList.length === 0
                    ? "Nenhum prazo ou audiência"
                    : `${prazosList.length} prazo${prazosList.length !== 1 ? "s" : ""} · ${audienciasList.length} aud.`
                }
              >
                <div className="space-y-2">
                  {prazosList.length === 0 && audienciasList.length === 0 ? (
                    <div className="text-[11px] text-gray-500 italic bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                      Nenhum prazo ou audiência ainda.
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {[...prazosList, ...audienciasList].map((p, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-[11px]"
                        >
                          <span className="font-bold text-slate-800">
                            {String(p?.tipo || "Prazo")}
                          </span>
                          <span className="text-gray-600 truncate flex-1">
                            {String(p?.descricao || "")}
                          </span>
                          {p?.dataMs ? (
                            <span className="text-[10px] text-gray-400 shrink-0">
                              {new Date(p.dataMs).toLocaleDateString("pt-BR")}
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Fusão Processos/Caderno (MC21): entrada manual real,
                      substitui o placeholder "(Fase 2 — DataJud)". */}
                  <div className="border-t border-gray-200 pt-2 space-y-1.5">
                    <div className="flex gap-1">
                      {["PRAZO", "AUDIENCIA"].map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setNovoPrazoForm((prev) => ({ ...prev, modo: m }))}
                          className={`flex-1 px-2 py-1 rounded text-[9px] font-bold uppercase border ${
                            novoPrazoForm.modo === m
                              ? "border-[#C5A059] bg-[#C5A059]/10 text-[#20243B]"
                              : "border-gray-200 bg-white text-gray-500"
                          }`}
                        >
                          {m === "PRAZO" ? "Prazo" : "Audiência"}
                        </button>
                      ))}
                    </div>
                    <input
                      value={novoPrazoForm.tipo}
                      onChange={(e) => setNovoPrazoForm((prev) => ({ ...prev, tipo: e.target.value }))}
                      placeholder="Tipo (ex.: Contestação, Audiência de Instrução)"
                      className="w-full px-2 py-1.5 rounded border border-gray-300 text-[11px]"
                    />
                    <input
                      value={novoPrazoForm.descricao}
                      onChange={(e) => setNovoPrazoForm((prev) => ({ ...prev, descricao: e.target.value }))}
                      placeholder="Descrição"
                      className="w-full px-2 py-1.5 rounded border border-gray-300 text-[11px]"
                    />
                    <div className="flex gap-1.5">
                      <input
                        type="date"
                        value={novoPrazoForm.data}
                        onChange={(e) => setNovoPrazoForm((prev) => ({ ...prev, data: e.target.value }))}
                        className="flex-1 px-2 py-1.5 rounded border border-gray-300 text-[11px]"
                      />
                      <button
                        type="button"
                        onClick={handleSalvarPrazo}
                        disabled={savingPrazo || !novoPrazoForm.descricao.trim() || !novoPrazoForm.data}
                        className="px-3 py-1.5 rounded bg-[#20243B] text-white text-[10px] font-bold uppercase disabled:opacity-40"
                      >
                        {savingPrazo ? "..." : "Adicionar"}
                      </button>
                    </div>
                  </div>
                </div>
              </SummaryCard>

              <SummaryCard
                icon="fa-list-check"
                id="tarefas"
                title="Tarefas do Cliente"
                summary={
                  selectedClientId
                    ? "Atribuir e acompanhar tarefas com prazo"
                    : "Nenhum cliente vinculado"
                }
              >
                <CadernoTarefas
                  clientId={selectedClientId}
                  workspaceId={workspaceKey}
                />
              </SummaryCard>
                </div>
              ) : activeTab === "historico" ? (
                /* MC44 (Fase F2, redesenho tabela 22/08/2026): sai do modal
                   "Linha do Tempo" e do formato cartão vertical, vira aba
                   Histórico em tabela — layout inspirado no real "Eventos do
                   Processo" do Projudi (Nº/Movimentação/Data/Usuário/
                   Arquivo), não no card genérico anterior. Busca livre cobre
                   parte/usuário/teor em vez de chips de taxonomia fixa
                   (justificativa: PLANO_FUSAO_PROCESSOS_CADERNO.md MC44).
                   Clique na linha abre o flyout com o texto oficial +
                   resumo (MC47 dobrado aqui). */
                <div className="flex flex-col h-full">
                  {/* Modo "Linha do Tempo Completa" (Nova Nota/Exportar/caderno_notas)
                      foi retirado por inteiro em 02/09/2026 — Fase 1 do
                      PRE_VOO_LIVRO_PROCESSUAL_GERADO_20260901.md. A aba "Histórico"
                      só mostra os Andamentos do Tribunal (abaixo); a síntese de
                      trabalho do caso vai morar no CADERNO_<...>.md (Fase 2). */}
                  <div className="flex flex-col flex-1 min-h-0">
                  <div className="flex items-center gap-3 mb-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowMovementForm(true)}
                      className="bg-[#20243B] text-white text-xs font-bold uppercase px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2 shrink-0"
                    >
                      <i className="fas fa-plus"></i> Nova Movimentação
                    </button>
                    <div className="relative flex-1 max-w-sm">
                      <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                      <input
                        value={historicoSearch}
                        onChange={(e) => setHistoricoSearch(e.target.value)}
                        placeholder="Buscar por parte, usuário ou teor..."
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm focus:border-brand-blue outline-none"
                      />
                    </div>
                    <span className="text-xs text-gray-400 shrink-0">
                      {filteredMovementsList.length} de {movementsList.length}
                    </span>
                  </div>

                  <div className="flex-1 overflow-y-auto custom-scrollbar border border-gray-200 rounded-lg">
                    <table className="w-full text-sm table-fixed">
                      <colgroup>
                        <col className="w-[6%]" />
                        <col className="w-[11%]" />
                        <col className="w-[47%]" />
                        <col className="w-[24%]" />
                        <col className="w-[12%]" />
                      </colgroup>
                      <thead className="sticky top-0 bg-slate-100 border-b border-gray-300 text-[11px] uppercase text-gray-600 font-bold tracking-wide">
                        <tr>
                          <th className="text-left px-3 py-2.5">Nº</th>
                          <th className="text-left px-3 py-2.5">Data</th>
                          <th className="text-left px-3 py-2.5">Movimentação</th>
                          <th className="text-left px-3 py-2.5">Usuário</th>
                          <th className="text-left px-3 py-2.5">Anexo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredMovementsList.map((m) => {
                          // Citação literal do Projudi (Constituição Art. 1.1) — nunca
                          // usar tipoMovimento (rótulo genérico do DataJud) como se fosse
                          // a movimentação real; descricaoOficial/detalheMovimento são o
                          // texto exato copiado da aba "Eventos do Processo".
                          const { data: dataStr, hora: horaStr } = formatDataHoraMovimento(m.dataMovimento);
                          const descricao = String(m.descricaoOficial || m.tipoMovimento || "");
                          const detalhe = String(m.detalheMovimento || "");
                          return (
                            <tr
                              key={m.id}
                              onClick={() => setSelectedMovement(m)}
                              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer align-top"
                            >
                              <td className="px-3 py-3 text-gray-400 font-mono text-xs">{m.numeroMovimento || "—"}</td>
                              <td className="px-3 py-3 text-gray-600 text-xs">
                                {dataStr ? (
                                  <>
                                    <div>{dataStr}</div>
                                    <div className="text-gray-400">{horaStr}</div>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td className="px-3 py-3">
                                <div className="font-bold text-gray-800 text-xs">{descricao}</div>
                                {detalhe ? (
                                  <div className="text-gray-600 text-xs line-clamp-2 mt-0.5">{detalhe}</div>
                                ) : null}
                              </td>
                              <td className="px-3 py-3 text-gray-600 text-xs truncate">{m.usuario || "—"}</td>
                              <td className="px-3 py-3">
                                {/* arquivosIndividuais SUPERSEDE anexo (Constituição Art. 13.7.1)
                                    — nunca os dois ao mesmo tempo, mas o indicador de anexo
                                    precisa reconhecer QUALQUER um dos dois, senão os 51
                                    movimentos com granularidade ficam com falso "sem anexo"
                                    (achado do titular, 23/08/2026: o traço tracejado parecia
                                    "não existe anexo", quando na verdade existiam vários). */}
                                {m.anexo?.downloadURL ? (
                                  <a
                                    href={m.anexo.downloadURL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    title={m.anexo.nomeArquivo}
                                    className="text-gray-400 hover:text-[#C5A059]"
                                  >
                                    <i className="fas fa-paperclip"></i>
                                  </a>
                                ) : Array.isArray(m.arquivosIndividuais) && m.arquivosIndividuais.length > 0 ? (
                                  <span
                                    className="inline-flex items-center gap-1 text-gray-400"
                                    title={`${m.arquivosIndividuais.length} arquivo(s) — clique na linha para abrir`}
                                  >
                                    <i className="fas fa-paperclip"></i>
                                    {m.arquivosIndividuais.length > 1 ? (
                                      <span className="text-[10px] font-bold">{m.arquivosIndividuais.length}</span>
                                    ) : null}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {filteredMovementsList.length === 0 && (
                          <tr>
                            <td colSpan={5} className="p-8 text-center text-xs text-gray-400">
                              {movementsList.length === 0
                                ? "Nenhuma movimentação registrada ainda."
                                : "Nenhuma movimentação encontrada para essa busca."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-xs text-gray-400 uppercase tracking-widest">
                  Aba "{CASE_TABS.find((t) => t.id === activeTab)?.label}" — conteúdo chega no próximo micro-commit (Fase F2)
                </div>
              )}
            </div>
          </>
        )}
      </main>
      </div>

      {/* MC44 (Fase F2): modal "Linha do Tempo" (showHistoricoModal) removido
          — conteúdo já vive na aba Histórico (ver acima), tela cheia, sem
          popup. */}

      {showMovementForm && activeWorkspace ? (
        <MovementCaptureForm
          workspaceId={activeWorkspace.id}
          numeroCNJ={activeWorkspace.numeroCNJ}
          pieces={Array.isArray(activeWorkspace.pieces) ? activeWorkspace.pieces : []}
          onClose={() => setShowMovementForm(false)}
          onSaved={() => {}}
        />
      ) : null}

      {/* Modal "Nota do Caderno" (camada caderno_notas, ART. 20) retirado em
          02/09/2026 junto com o resto do modo "Linha do Tempo Completa" —
          ver comentário na aba Histórico acima. */}

      {/* ═══ MODAL — Metadados / Capivara (V11 — substitui painel direito) ═══ */}
      {/* MC46 (Fase F2): modal "Contexto · Metadados" (showMetadataModal)
          removido por inteiro — esvaziado pelos MC42/MC43/MC44 (os 6
          acordeões de leitura/edição foram todos pra aba Resumo/Atividades/
          Histórico). savingNotice e os badges de Sigilo/Prioridade/Prazo já
          são exibidos em duplicata no cabeçalho da sidebar (linhas
          2780-2795), então nada foi perdido. Botão "Metadados" repontado
          pra aba Resumo em vez de removido (ver onClick abaixo). */}

      {/* ═══ FLYOUT — Vincular Processo (Fase F2b — MC52) ══════════════════ */}
      <ProcessPicker
        open={Boolean(linkPickerType)}
        onClose={() => setLinkPickerType(null)}
        title={
          linkPickerType === "principal"
            ? "Vincular a um Processo Principal"
            : linkPickerType === "apenso"
              ? "Adicionar Apenso"
              : "Adicionar Correlato"
        }
        workspaces={allWorkspaces}
        excludeWorkspaceId={activeWorkspace?.id}
        onSelect={handleLinkProcessRelation}
      />

      {/* ═══ FLYOUT — Movimentação (aba Histórico, MC44/MC47) ═══════════════ */}
      <FlyoutPanel
        open={Boolean(selectedMovement)}
        onClose={() => setSelectedMovement(null)}
        eyebrow={String(selectedMovement?.descricaoOficial || selectedMovement?.tipoMovimento || "Movimentação")}
        title={(() => {
          const { data, hora } = formatDataHoraMovimento(selectedMovement?.dataMovimento);
          return data ? `${data} ${hora}` : "Movimentação";
        })()}
        subtitle={selectedMovement?.usuario ? `Usuário: ${selectedMovement.usuario}` : null}
      >
        {selectedMovement ? (() => {
          // Quando existe a lista granular (Constituição Art. 13.7.1), ela já
          // cobre 100% do conteúdo do PDF unificado (Art. 13.6) — mostrar os
          // dois ao mesmo tempo duplica a mesma informação na tela (achado do
          // titular, 23/08/2026). O link do PDF unificado só aparece quando
          // NÃO há granularidade para aquela movimentação.
          const temArquivosIndividuais =
            Array.isArray(selectedMovement.arquivosIndividuais) && selectedMovement.arquivosIndividuais.length > 0;
          return (
          <>
            {selectedMovement.detalheMovimento ? (
              <p className="text-xs text-gray-600 leading-relaxed">
                {selectedMovement.detalheMovimento}
              </p>
            ) : null}
            {!temArquivosIndividuais && selectedMovement.anexo?.downloadURL ? (
              <a
                href={selectedMovement.anexo.downloadURL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-bold text-[#20243B] hover:text-[#C5A059]"
              >
                <i className="fas fa-paperclip"></i> {selectedMovement.anexo.nomeArquivo || "Anexo"}
              </a>
            ) : null}
            {temArquivosIndividuais ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[10px] font-bold text-gray-500 uppercase tracking-wide">
                  Arquivos desta movimentação ({selectedMovement.arquivosIndividuais.length})
                </div>
                {/* Sem max-h/overflow próprio (achado do titular, 23/08/2026): o
                    flyout inteiro já rola pelo container do FlyoutPanel — uma
                    caixa de rolagem aninhada aqui limitava a lista a 256px fixos
                    e deixava o resto do painel vazio em vez de usar a altura
                    real disponível na tela. */}
                <ul className="divide-y divide-gray-100">
                  {[...selectedMovement.arquivosIndividuais]
                    .sort((a, b) => (a.numeroArquivo || 0) - (b.numeroArquivo || 0))
                    .map((arq) => {
                      // Fallback (achado do titular, 24/08/2026): scripts de raspagem
                      // gravam driveFileId sem webViewLink — mesmo padrão de fallback
                      // já usado em ClientAcervoTab.jsx/PortalVault.jsx/ClientManager.jsx.
                      const arqLink =
                        arq.webViewLink ||
                        (arq.driveFileId ? `https://drive.google.com/file/d/${arq.driveFileId}/view` : "");
                      return (
                      <li key={arq.numeroArquivo} className="px-3 py-2 flex items-center gap-2 hover:bg-gray-50">
                        <span className="text-[10px] font-mono text-gray-400 shrink-0">{arq.numeroArquivo}</span>
                        {arqLink ? (
                          <a
                            href={arqLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#20243B] hover:text-[#C5A059] hover:underline truncate flex-1"
                            title={arq.nomeArquivo}
                          >
                            {arq.nomeArquivo}
                          </a>
                        ) : (
                          <span className="text-xs text-gray-500 truncate flex-1">{arq.nomeArquivo}</span>
                        )}
                        {arq.paginas ? (
                          <span className="text-[10px] text-gray-400 shrink-0">{arq.paginas}p</span>
                        ) : null}
                      </li>
                      );
                    })}
                </ul>
              </div>
            ) : null}
            {activeWorkspace ? (
              <MovementResumoPanel
                workspaceId={activeWorkspace.id}
                movementId={selectedMovement.id}
                descricaoOficial={selectedMovement.descricaoOficial}
                resumo={selectedMovement.resumo}
                currentUserId={auth?.currentUser?.uid}
              />
            ) : null}
          </>
          );
        })() : null}
      </FlyoutPanel>

      {/* ═══ MODAL — Seletor de Cliente (CRM) — MC_X ═══════════════════════ */}
      {isClientPickerOpen ? (
        <CrmClientPickerModal
          isOpen={true}
          onClose={() => setIsClientPickerOpen(false)}
          onSelect={(client) => {
            const cid = String(client?.id || client?.clientId || "").trim();
            // MC_Y8: resolve nome via qualificação (espelha CrmClientPickerModal.resolveName)
            const _t = String(client?.type || "").toUpperCase();
            const cname = String(
              _t === "PESSOA FÍSICA" || _t === "PHYSICAL"
                ? client?.qualification?.physical?.socialName ||
                    client?.qualification?.physical?.name ||
                    client?.name ||
                    ""
                : _t === "PESSOA JURÍDICA" ||
                    _t === "PESSOA JURIDICA" ||
                    _t === "LEGAL"
                  ? client?.qualification?.legal?.companyName ||
                    client?.name ||
                    ""
                  : _t === "ANIMAL" ||
                      _t === "ANIMAL NÃO-HUMANO" ||
                      _t === "ANIMAL NAO-HUMANO"
                    ? client?.qualification?.animal?.name || client?.name || ""
                    : client?.name || client?.clientName || "",
            ).trim();
            if (cid) setSelectedClientId(cid);
            if (cname) setSelectedClientName(cname);
            // MC_Y3: captura CPF/CNPJ/Email do CRM
            setSelectedClientMeta(extractClientMeta(client));
            setIsClientPickerOpen(false);
            // MC_Y7: limpa peça ativa stale (do cliente anterior).
            setActivePecaDoc(null);
            // MC_Y8: Auto-seleção robusta — match por clientId OU clientName (cobre
            // workspaces antigos onde clientId = nome e novos onde clientId = Firestore doc ID).
            const cidUp = cid.toUpperCase();
            const cnameUp = cname.toUpperCase();
            const siblings = (
              Array.isArray(allWorkspaces) ? allWorkspaces : []
            ).filter((w) => {
              const wId = String(w?.clientId || "")
                .trim()
                .toUpperCase();
              const wName = String(w?.clientName || "")
                .trim()
                .toUpperCase();
              return (
                (cidUp && (wId === cidUp || wName === cidUp)) ||
                (cnameUp && (wId === cnameUp || wName === cnameUp))
              );
            });
            const toMs = (v) => {
              if (!v) return 0;
              if (typeof v === "number") return v;
              if (typeof v?.toMillis === "function") return v.toMillis();
              if (v?.seconds) return v.seconds * 1000;
              const d = new Date(v);
              return isNaN(d.getTime()) ? 0 : d.getTime();
            };
            const sorted = [...siblings].sort(
              (a, b) =>
                toMs(b?.updatedAt || b?.createdAt) -
                toMs(a?.updatedAt || a?.createdAt),
            );
            if (typeof onSwitchWorkspace === "function") {
              // Passa o caderno mais recente, OU null para limpar se cliente não tem cadernos.
              onSwitchWorkspace(sorted[0] || null);
            }
          }}
          db={db}
          appId={appId}
          title="Base de Clientes (CRM)"
        />
      ) : null}

      {/* ═══ MODAL — Seletor de Co-Parte (CRM) — Matter-Centric Fase 1 ════ */}
      {isCoPartePickerOpen ? (
        <CrmClientPickerModal
          isOpen={true}
          onClose={() => setIsCoPartePickerOpen(false)}
          onSelect={handleAddCoParteFromCrm}
          db={db}
          appId={appId}
          title="Adicionar Co-Parte ao Processo"
        />
      ) : null}

      {/* ═══ MODAL — Seletor de Advogado (CRM) — Art.327 caseTeam ══════════ */}
      {isCaseTeamPickerOpen ? (
        <CrmClientPickerModal
          isOpen={true}
          onClose={() => setIsCaseTeamPickerOpen(false)}
          onSelect={handleAddCaseTeamFromCrm}
          db={db}
          appId={appId}
          title="Adicionar Advogado à Equipe do Caso"
          requiredRole="PARTNER"
        />
      ) : null}

      {/* ═══ MODAL B3 — Enviar Peça para Aprovação do Cliente ═══ */}
      {approval.approvalModalPiece ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={() => approval.setApprovalModalPiece(null)}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#C5A059]/40 bg-[#20243B] flex items-center justify-between rounded-t-xl">
              <h3 className="text-[11px] font-bold text-[#C5A059] uppercase tracking-widest">
                Enviar para Aprovação
              </h3>
              <button
                type="button"
                onClick={() => approval.setApprovalModalPiece(null)}
                className="w-6 h-6 rounded-full flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/20"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-xs text-gray-700">
                <span className="font-bold text-[#20243B]">Peça:</span>{" "}
                {approval.approvalModalPiece.pieceType || "Documento sem tipo"}
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">
                  Mensagem para o cliente (opcional)
                </label>
                <textarea
                  value={approval.approvalNote}
                  onChange={(e) => approval.setApprovalNote(e.target.value)}
                  placeholder="Ex: Petição inicial revisada — aguardo sua aprovação antes do protocolo."
                  rows={3}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 resize-none outline-none focus:border-[#20243B] placeholder-gray-400"
                  disabled={approval.sendingApproval}
                />
              </div>
              {approval.approvalNotice && (
                <div className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                  {approval.approvalNotice}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => approval.setApprovalModalPiece(null)}
                  className="flex-1 px-3 py-2 text-xs font-bold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() =>
                    approval.handleSendForApproval(approval.approvalModalPiece)
                  }
                  disabled={approval.sendingApproval}
                  className="flex-1 px-3 py-2 text-xs font-bold text-white bg-[#C5A059] hover:bg-[#a8844a] rounded-lg disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {approval.sendingApproval ? (
                    <>
                      <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white" />{" "}
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane text-[10px]" /> Enviar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ═══ MODAL — Protocolar Peça (data-prefixo, 22/09/2026) ═══ */}
      {protocolModalPiece ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={handleCancelProtocolarPiece}
        >
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#C5A059]/40 bg-[#20243B] flex items-center justify-between rounded-t-xl">
              <h3 className="text-[11px] font-bold text-[#C5A059] uppercase tracking-widest">
                Protocolar Peça
              </h3>
              <button
                type="button"
                onClick={handleCancelProtocolarPiece}
                className="w-6 h-6 rounded-full flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/20"
              >
                <i className="fas fa-times text-xs"></i>
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-xs text-gray-700">
                <span className="font-bold text-[#20243B]">Peça:</span>{" "}
                {protocolModalPiece.pieceType || "Documento sem tipo"}
              </div>
              <div className="text-[11px] text-gray-500">
                Renomeia a pasta desta peça no Drive com a data real do
                protocolo (substitui a data de criação, se houver).
              </div>
              <div>
                <label
                  htmlFor="protocolar-peca-data"
                  className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1"
                >
                  Data do protocolo
                </label>
                <input
                  id="protocolar-peca-data"
                  type="date"
                  value={protocolDateValue}
                  onChange={(e) => setProtocolDateValue(e.target.value)}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#20243B]"
                />
              </div>
              {protocolNotice ? (
                <div className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">
                  {protocolNotice}
                </div>
              ) : null}
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleCancelProtocolarPiece}
                  className="flex-1 px-3 py-2 text-xs font-bold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmProtocolarPiece}
                  className="flex-1 px-3 py-2 text-xs font-bold text-white bg-[#C5A059] hover:bg-[#a8844a] rounded-lg flex items-center justify-center gap-1.5"
                >
                  <i className="fas fa-stamp text-[10px]" /> Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ═══ CONSENT BUILDER MODAL ═══ */}
      {approval.consentBuilderPiece && (
        <ConsentBuilder
          petitionDocId={null}
          petitionTitle={String(
            approval.consentBuilderPiece?.pieceType || "Petição",
          )}
          clientId={String(activeWorkspace?.clientId || selectedClientId || "")}
          clientName={String(
            activeWorkspace?.clientName || selectedClientName || "",
          )}
          cadernoId={String(activeWorkspace?.id || "")}
          cadernoTitle={String(
            activeWorkspace?.alias || activeWorkspace?.name || "",
          )}
          pieceId={String(approval.consentBuilderPiece?.id || "")}
          onClose={() => approval.setConsentBuilderPiece(null)}
          onSuccess={() => {
            approval.setConsentBuilderPiece(null);
            approval.setApprovalNotice("✅ Consentimento informado publicado!");
            setTimeout(() => approval.setApprovalNotice(""), 4000);
          }}
        />
      )}
    </div>
  );
}
